"use client";

import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  type CSSProperties,
} from "react";
import { createPortal } from "react-dom";
import { ApiClient, PrimeStyleError, jsonHeaders, type SdkBehaviorEventType, type StyleMatchRequestProduct, type StyleMatchSelection, type TryOnCategory } from "../api-client";
import { SseClient } from "../sse-client";
import { getOrCreateSessionId } from "../session";
import { compressImage, isValidImageFile } from "../image-utils";
import { savePhoto as savePhotoIDB, getPhoto as getPhotoIDB, saveResult as saveResultIDB, getResult as getResultIDB, pruneToIds as prunePhotosIDB } from "./utils/photo-store";
import type { VtoUpdate, FitAreaInfo } from "../types";
import { detectMeasurementLines, detectBodyLandmarks, type MeasurementLines, type BodyLandmarks } from "../pose-detect";
import { cropHeadForSizing, detectFaceMeasurements, type FaceLandmarks } from "../face-detect";
import { buildFitInfo, buildSilhouetteContext } from "../sizing/fit-compute";
import { createT, type TranslateFn } from "../i18n";
import type {
  PrimeStyleTryonProps,
  ViewState,
  DrawerType,
  Profile,
  ProfileMeasurements,
  ProfileCompletionDraft,
  HistoryEntry,
  PrimeStyleAddToBagPayload,
  SizeGuide,
  SizeGuideField,
  SizingResult,
  SavedTryOnResult,
  ChestProfile,
  MidsectionProfile,
  HipProfile,
} from "./types";
import { FALLBACK_FIELDS_FEMALE, FALLBACK_FIELDS_MALE, TOTAL_STEPS, LOGO_IMG } from "./constants";
import {
  lsGet, lsSet, lsRemove,
  clearProfileLocalStorage,
} from "./utils/storage";
import { estimateFullMeasurements, recommendForProduct, setCachedMediaPipe } from "./recommendForProduct";
import { isImperial } from "./utils/units";
import { pickBestGarmentImage } from "./utils/pickBestGarmentImage";
import { cx } from "./utils/classnames";
import { detectLocale, getApiKey, getApiUrl } from "./utils/locale";
import {
  getStoredProfileSession,
  setStoredProfileSession,
  clearStoredProfileSession,
  startSocialProfileLogin,
  type ProfileAuthSession,
  type SocialProvider,
} from "./utils/profile-auth";
import { deleteRemoteProfile, fetchRemoteProfiles, isProfileAuthError, saveRemoteProfiles } from "./utils/remote-profiles";
import { normalizeProfilePhotoSource, profilePhotoToBlob, profilePhotoToDataUrl } from "./utils/profile-image";
import { sanitizeProfileForPersistence, sanitizeProfilesForPersistence } from "./utils/profile-sanitize";
import {
  detectProductFitType,
  detectProductGender,
  normalizeProductGender,
  productFitTypeToMeasurementType,
  type ProductFitType,
  type ProductGender,
  type ProductMeasurementType,
} from "./utils/product-fit";
import { STYLES } from "./styles";
import { setLastCompletedProduct, setLastSizeSelection } from "../storefront/cart-hook";
import { logProductView, logProfileEvent, logTryOnFeedback } from "../storefront/events";
import { startSdkClarity, tagSdkClaritySession } from "../clarity";

// ── Inject styles at module load — before any component renders ──
if (typeof document !== "undefined") {
  const id = "ps-tryon-styles";
  if (!document.getElementById(id)) {
    const el = document.createElement("style");
    el.id = id;
    el.textContent = STYLES;
    document.head.appendChild(el);
  }
}

function optionalStyleText(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

import { CameraIcon, XIcon, UserIcon, ClockIcon } from "./icons";
import { Stepper } from "./components/Stepper";
import { LangSwitcher } from "./components/LangSwitcher";
import { DrawerPanel } from "./components/DrawerPanel";
import { ProfileDetailModal } from "./components/ProfileDetailModal";
import { ConfirmMeasurementsModal } from "./components/ConfirmMeasurementsModal";
import { WelcomeView } from "./views/WelcomeView";

import { EstimationReviewView } from "./views/EstimationReviewView";
import { SizeResultView } from "./views/SizeResultView";
import { UploadView } from "./views/UploadView";
import { PhotoGuideView } from "./views/PhotoGuideView";
import { ProcessingView } from "./views/ProcessingView";
import { ErrorView } from "./views/ErrorView";
import { NoChartView } from "./views/NoChartView";
import { MySizingProfilesView, DeleteConfirmModal } from "./views/MySizingProfilesView";
import { SocialProfileAuthView } from "./views/SocialProfileAuthView";

import { ResultView } from "./views/ResultView";
import { BodyProfileView } from "./views/BodyProfileView";
import { WristSizeView } from "./views/WristSizeView";

const PROFILE_COMPLETION_DRAFT_KEY = "profile_completion_draft";
const TRYON_FEEDBACK_SUBMITTED_KEY = "tryon_feedback_submitted";
const FULL_SCAN_MIN_VISIBLE_MS = 6000;
const FACE_SCAN_MIN_VISIBLE_MS = 4500;

function getFaceHeadScanMinVisibleMs(measurementType: ProductMeasurementType): number {
  return measurementType === "head" ? FULL_SCAN_MIN_VISIBLE_MS : FACE_SCAN_MIN_VISIBLE_MS;
}

type SnapBraSizeInput = {
  band: string | number;
  cup: string;
  region?: string;
};

type SnapSubmitData = {
  photoFile: File | null;
  photoBase64: string;
  height: number;
  weight: number;
  heightUnit: string;
  weightUnit: string;
  sizingUnit?: string;
  gender: string;
  age?: number;
  bandSize?: string;
  cupSize?: string;
  braSizeRegion?: string;
  braRegion?: string;
  braSize?: SnapBraSizeInput;
  chestProfile?: ChestProfile;
  midsectionProfile?: MidsectionProfile;
  hipProfile?: HipProfile;
  extraMeasurements?: Record<string, string | number>;
  knownMeasurements?: Record<string, number>;
  styleMatchSelections?: StyleMatchSelection[];
};

function buildTryOnFeedbackKey(input: {
  jobId?: string | null;
  historyEntryId?: string | null;
  resultImageUrl?: string | null;
  productId?: string;
  productTitle?: string;
  recommendedSize?: string;
}): string {
  if (input.jobId) return `job:${input.jobId}`;
  if (input.historyEntryId) return `history:${input.historyEntryId}`;
  if (input.resultImageUrl) return `result:${input.resultImageUrl.slice(0, 180)}`;
  return `fallback:${input.productId || input.productTitle || "product"}:${input.recommendedSize || "size"}`;
}

function positiveNumber(value: unknown): number | undefined {
  const n = typeof value === "number" ? value : parseFloat(String(value ?? ""));
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

function snapBraFields(data: {
  gender?: string;
  bandSize?: string | number;
  cupSize?: string;
  braSizeRegion?: string;
  braRegion?: string;
  braSize?: SnapBraSizeInput;
}): Pick<ProfileCompletionDraft, "bandSize" | "cupSize" | "braSizeRegion"> {
  if (data.gender !== "female") return {};
  const band = data.bandSize ?? data.braSize?.band;
  const cup = data.cupSize ?? data.braSize?.cup;
  const region = data.braSizeRegion ?? data.braRegion ?? data.braSize?.region;
  return {
    ...(band != null && String(band).trim() ? { bandSize: String(band) } : {}),
    ...(cup && String(cup).trim() ? { cupSize: String(cup) } : {}),
    ...(region && String(region).trim() ? { braSizeRegion: String(region) } : {}),
  };
}

function validChestProfile(value: unknown): ChestProfile | undefined {
  return value === "narrow" || value === "average" || value === "broad" ? value : undefined;
}

function validMidsectionProfile(value: unknown): MidsectionProfile | undefined {
  return value === "flat" || value === "average" || value === "round" ? value : undefined;
}

function validHipProfile(value: unknown): HipProfile | undefined {
  return value === "narrow" || value === "average" || value === "full" ? value : undefined;
}

function cleanProfileMeasurements(value: unknown): ProfileMeasurements | undefined {
  if (!value || typeof value !== "object") return undefined;
  const measurements: ProfileMeasurements = {};
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    const n = positiveNumber(raw);
    if (n != null) measurements[key] = n;
  }
  return Object.keys(measurements).length > 0 ? measurements : undefined;
}

async function estimateHeadCircumferenceMmFromPhoto(photoBase64: string): Promise<number | null> {
  const firstResult = await detectFaceMeasurements(photoBase64).catch(() => null);
  if (firstResult?.measurementsMm.headCircumference && firstResult.irisConfidence >= 0.45) {
    return firstResult.measurementsMm.headCircumference;
  }

  const croppedHead = await cropHeadForSizing(photoBase64).catch(() => null);
  if (croppedHead) {
    const croppedResult = await detectFaceMeasurements(croppedHead).catch(() => null);
    if (croppedResult?.measurementsMm.headCircumference && croppedResult.irisConfidence >= 0.45) {
      return croppedResult.measurementsMm.headCircumference;
    }
  }

  return firstResult?.measurementsMm.headCircumference && firstResult.irisConfidence >= 0.35
    ? firstResult.measurementsMm.headCircumference
    : null;
}

function headCircumferenceMmToUnit(mm: number, unit: "cm" | "in"): number {
  const value = unit === "in" ? mm / 25.4 : mm / 10;
  return Math.round(value * 10) / 10;
}

function isVisibleBodyPoint(point: unknown, minVisibility = 0.12): point is { x: number; y: number; visibility?: number } {
  if (!point || typeof point !== "object") return false;
  const p = point as { x?: unknown; y?: unknown; visibility?: unknown };
  if (typeof p.x !== "number" || typeof p.y !== "number") return false;
  return typeof p.visibility !== "number" || p.visibility >= minVisibility;
}

function isLikelyFullBodyLandmarks(landmarks: BodyLandmarks | null | undefined): boolean {
  if (!landmarks) return false;
  const lowerBodyKeys = ["leftHip", "rightHip", "leftKnee", "rightKnee", "leftAnkle", "rightAnkle"] as const;
  const visibleLowerBodyCount = lowerBodyKeys.filter((key) => isVisibleBodyPoint((landmarks as any)[key])).length;
  const shoulderYs = [landmarks.leftShoulder, landmarks.rightShoulder]
    .filter((point) => isVisibleBodyPoint(point))
    .map((point) => point.y);
  const ankleYs = [landmarks.leftAnkle, landmarks.rightAnkle]
    .filter((point) => isVisibleBodyPoint(point))
    .map((point) => point.y);
  const shoulderY = shoulderYs.length ? Math.min(...shoulderYs) : null;
  const ankleY = ankleYs.length ? Math.max(...ankleYs) : null;
  const verticalSpan = shoulderY != null && ankleY != null ? ankleY - shoulderY : 0;
  return visibleLowerBodyCount >= 4 || verticalSpan > 0.38;
}

function isTransientResultImageUrl(url?: string | null): boolean {
  if (!url) return false;
  const clean = url.trim();
  return clean.startsWith("data:") || clean.startsWith("blob:");
}

function durableHistoryImageUrl(url?: string | null): string | undefined {
  if (!url) return undefined;
  const clean = url.trim();
  if (!clean || isTransientResultImageUrl(clean)) return undefined;
  return clean;
}

function isTransientHistoryImageUrl(url?: string | null): boolean {
  return isTransientResultImageUrl(url);
}

function normalizeHistoryEntry(entry: HistoryEntry): HistoryEntry {
  const storedResultUrl = entry.resultImageUrl?.trim().startsWith("blob:") ? undefined : entry.resultImageUrl;
  return {
    ...entry,
    resultImageUrl: storedResultUrl,
    hasResult: entry.hasResult || !!entry.resultImageUrl || undefined,
  };
}

function sanitizeHistoryEntry(entry: HistoryEntry): HistoryEntry {
  const durableResultUrl = durableHistoryImageUrl(entry.resultImageUrl);
  return {
    ...entry,
    resultImageUrl: durableResultUrl,
    hasResult: entry.hasResult || !!entry.resultImageUrl || undefined,
  };
}

function comparableProductId(value?: string | null): string {
  return String(value || "")
    .trim()
    .replace(/^gid:\/\/shopify\/Product\//i, "")
    .replace(/^Product\//i, "");
}

function historyEntryMatchesProduct(
  entry: HistoryEntry,
  productId?: string,
  productTitle?: string,
): boolean {
  const currentId = comparableProductId(productId);
  const entryId = comparableProductId(entry.productId);
  if (currentId && entryId && currentId === entryId) return true;

  const currentTitle = String(productTitle || "").trim().toLowerCase();
  const entryTitle = String(entry.productTitle || "").trim().toLowerCase();
  return !!currentTitle && !!entryTitle && currentTitle === entryTitle;
}

function hasCompletedTryOnResult(entry: HistoryEntry): boolean {
  return !!(entry.resultImageUrl || entry.hasResult);
}

function savedTryOnResultToHistoryEntry(
  result: SavedTryOnResult,
  fallback: {
    productImage?: string;
    productImages?: string[];
    productCarouselItems?: Array<{ image: string; title?: string; href?: string }>;
    productTitle?: string;
    productUrl?: string;
  },
): HistoryEntry {
  const sizingResult = result.sizingResult ?? {
    recommendedSize: result.recommendedSize,
    confidence: "high",
    reasoning: "",
  };
  return {
    id: `backend_${result.historyId}`,
    productId: result.productId,
    productTitle: result.productTitle || fallback.productTitle,
    productUrl: result.productUrl || fallback.productUrl,
    jobId: result.jobId,
    productImage: fallback.productImage,
    productImages: fallback.productImages?.slice(0, 12),
    productCarouselItems: fallback.productCarouselItems?.slice(0, 12),
    resultImageUrl: result.resultImageUrl || undefined,
    recommendedSize: result.recommendedSize,
    confidence: result.sizingResult?.confidence,
    reasoning: result.sizingResult?.reasoning,
    internationalSizes: result.sizingResult?.internationalSizes,
    matchDetails: result.sizingResult?.matchDetails,
    sizingResult,
    date: Date.parse(result.createdAt) || Date.now(),
    hasResult: result.hasResultImage || !!result.resultImageUrl || undefined,
  };
}

function savedTryOnResultFromLimitError(err: unknown): SavedTryOnResult | null {
  if (!(err instanceof PrimeStyleError) || err.code !== "TRYON_IP_LIMIT_REACHED") return null;
  const details = err.details;
  if (!details || typeof details !== "object") return null;
  if ((details as { limitScope?: unknown }).limitScope !== "product") return null;
  const savedResult = (details as { savedResult?: unknown }).savedResult;
  if (!savedResult || typeof savedResult !== "object") return null;
  return (savedResult as { found?: unknown }).found === true
    ? (savedResult as SavedTryOnResult)
    : null;
}

function isStoreMonthlyTryOnLimitError(err: unknown): boolean {
  if (!(err instanceof PrimeStyleError) || err.code !== "TRYON_IP_LIMIT_REACHED") return false;
  const details = err.details;
  return !!details && typeof details === "object" && (details as { limitScope?: unknown }).limitScope === "store_monthly";
}

function dispatchTryOnCompleteEvent(detail: {
  jobId?: string;
  historyEntryId?: string | null;
  productId?: string;
  productTitle?: string;
  recommendedSize?: string;
  resultImageUrl?: string;
}): void {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent("primestyle:tryon-complete", { detail }));
  } catch {
    /* non-fatal */
  }
}

/**
 * Map measurement type to the VTO category routed to the backend prompt
 * branch. Footwear and accessories must reach dedicated prompts so Gemini
 * does not receive apparel-only wording like "put the dress on the model".
 */
// Compute the overall fit-match percentage from a sizing recommend response.
// Aggregates matchDetails across the top-level result + every section.
// Returns null when there's nothing to score (accessories, no chart, etc.).
function computeMatchScore(recData: { matchDetails?: Array<{ fit: string }>; sections?: Record<string, { matchDetails?: Array<{ fit: string }> }> } | null | undefined): number | null {
  if (!recData) return null;
  const all: Array<{ fit: string }> = [];
  for (const m of recData.matchDetails ?? []) all.push(m);
  if (recData.sections) {
    for (const sec of Object.values(recData.sections)) {
      for (const m of sec?.matchDetails ?? []) all.push(m);
    }
  }
  if (all.length === 0) return null;
  const good = all.filter(r => r.fit === "good" || r.fit === "a-bit-tight" || r.fit === "a-bit-loose").length;
  return Math.round((good / all.length) * 100);
}

function measurementTypeToVtoCategory(
  type: ProductMeasurementType,
  fitType: ProductFitType,
): TryOnCategory {
  if (type === "foot") return "shoe";
  if (type === "face") return "sunglasses";
  if (type === "head") return "hat";
  if (type === "wrist") return fitType === "watch" ? "watch" : "bracelet";
  if (type === "body-basic") {
    if (
      fitType === "bag" ||
      fitType === "belt" ||
      fitType === "earring" ||
      fitType === "necklace" ||
      fitType === "bracelet" ||
      fitType === "ring" ||
      fitType === "watch" ||
      fitType === "accessory"
    ) {
      return fitType;
    }
    return "accessory";
  }
  return "apparel";
}

function guideCellValue(row: unknown, index: number, header?: string): string {
  if (Array.isArray(row)) return String(row[index] ?? "").trim();
  if (!row || typeof row !== "object") return "";
  const record = row as Record<string, unknown>;
  if (header) {
    if (record[header] != null) return String(record[header]).trim();
    const matchedKey = Object.keys(record).find((key) => key.trim().toLowerCase() === header.trim().toLowerCase());
    if (matchedKey && record[matchedKey] != null) return String(record[matchedKey]).trim();
  }
  const values = Object.values(record);
  return String(values[index] ?? "").trim();
}

function findGuideSizeColumn(headers: string[]): number {
  const exact = headers.findIndex((header) => /^(size|sizes|size label|variant|option)$/i.test(header.trim()));
  if (exact >= 0) return exact;
  const accessory = headers.findIndex((header) =>
    /\b(size|ring size|bracelet size|watch size|necklace size|length|circumference|diameter)\b/i.test(header)
    && !/\b(measurement|guide|body|chest|bust|waist|hip|shoulder|sleeve|height|weight)\b/i.test(header)
  );
  return accessory >= 0 ? accessory : 0;
}

function extractGuideSizes(headers?: string[], rows?: unknown[]): string[] {
  const safeHeaders = Array.isArray(headers) ? headers.map((header) => String(header || "").trim()).filter(Boolean) : [];
  const safeRows = Array.isArray(rows) ? rows : [];
  const seen = new Set<string>();
  const out: string[] = [];
  const add = (value: unknown) => {
    const label = String(value ?? "").trim();
    if (!label || label === "—") return;
    const key = label.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(label);
  };

  const firstHeader = safeHeaders[0] || "";
  if (safeHeaders.length > 1 && /\b(measurement|measure|dimension|body|area)\b/i.test(firstHeader)) {
    safeHeaders.slice(1).forEach(add);
    return out;
  }

  const sizeColumn = safeHeaders.length > 0 ? findGuideSizeColumn(safeHeaders) : 0;
  safeRows.forEach((row) => add(guideCellValue(row, sizeColumn, safeHeaders[sizeColumn])));
  return out;
}

function buildAccessoryGuideOnlySizingResult(sizeGuide: SizeGuide | null): SizingResult {
  const rootSizes = extractGuideSizes(sizeGuide?.headers, sizeGuide?.rows);
  const sectionEntries = sizeGuide?.sections ? Object.entries(sizeGuide.sections) : [];
  const allSizes = [...rootSizes];
  const addAll = (value: string) => {
    if (!allSizes.some((size) => size.toLowerCase() === value.toLowerCase())) allSizes.push(value);
  };

  const sections = sectionEntries.length > 0
    ? Object.fromEntries(sectionEntries.map(([name, section]) => {
      const sizes = extractGuideSizes(section.headers, section.rows);
      sizes.forEach(addAll);
      const hasMultiple = sizes.length > 1;
      const label = hasMultiple ? "See available sizes" : (sizes[0] || "One Size");
      return [name, {
        recommendedSize: label,
        matchDetails: [],
        found: true,
        guideOnly: true,
        oneSize: !hasMultiple,
        sizeLabel: label,
        availableSizes: sizes,
      }];
    }))
    : undefined;

  const hasMultiple = allSizes.length > 1;
  const label = hasMultiple ? "See available sizes" : (allSizes[0] || "One Size");
  return {
    recommendedSize: label,
    confidence: "guide",
    reasoning: hasMultiple
      ? "This accessory has multiple available sizes. Review the size guide below."
      : "This accessory is treated as one size for try-on.",
    matchDetails: [],
    found: true,
    method: "deterministic",
    guideOnly: true,
    oneSize: !hasMultiple,
    sizeLabel: label,
    availableSizes: allSizes,
    ...(sections ? { sections } : {}),
  } as SizingResult;
}

function shouldUseShopifyCartAttribution(apiUrl?: string): boolean {
	if (typeof window === "undefined") return false;
	try {
		const w = window as unknown as { Shopify?: { shop?: string } };
		if (w.Shopify?.shop) return true;
		const resolved = getApiUrl(apiUrl);
		return /\/apps\/primestyle\/?$/.test(resolved);
	} catch {
		return false;
	}
}

type PrimeStyleTryonInnerProps = PrimeStyleTryonProps & {
  initialView?: ViewState;
  initialBodyProfileStep?: "photo" | "basics";
};

function createSdkOpenId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `ps_open_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

export function PrimeStyleTryonInner({
  productImage,
  productImages,
  productCarouselItems,
  garmentReferenceImage,
  garmentDetailImage,
  productTitle = "Product",
  productUrl,
  productId,
  productCategory,
  productGender,
  productSubcategory,
  productFitType,
  productType,
  productVendor,
  productTags,
  productDescription,
  outfitBuilderSource = "sdk",
  instantOutfitLooks,
  productMaterial,
  buttonText,
  limitOneColorTryOnPerProduct = false,
  tryOnProductIpLimitEnabled = false,
  tryOnProductIpLimitMaxAttempts = 1,
  apiUrl,
  showPoweredBy = true,
  showIcon = true,
  buttonIcon,
  locale,
  buttonStyles: btnS = {},
	  modalStyles: mdlS = {},
	  classNames: cn = {},
	  className,
	  style,
	  portalContainer,
	  onOpen, onClose, onUpload, onProcessing, onComplete, onError,
	  onAddToBag,
	  addToBagLabel,
	  continueShoppingLabel,
	  backToProductPageLabel,
	  sizeGuideData,
	  clarityProjectId,
	  initialView,
	  initialBodyProfileStep,
	}: PrimeStyleTryonInnerProps) {
  // Stable cache key — falls back to productImage if no explicit id passed
  const effectiveProductId = productId || productImage;
  const productImagesKey = (productImages ?? []).filter(Boolean).join("\u0001");
  const stableProductImages = useMemo(
    () => (productImages ?? []).filter(Boolean),
    [productImagesKey],
  );
  const productCarouselItemsKey = (productCarouselItems ?? [])
    .filter((item) => !!item.image)
    .map((item) => `${item.image}\u0001${item.title ?? ""}\u0001${item.href ?? ""}`)
    .join("\u0002");
  const stableProductCarouselItems = useMemo(
    () => (productCarouselItems ?? []).filter((item) => !!item.image),
    [productCarouselItemsKey],
  );
  const productTagsKey = Array.isArray(productTags)
    ? `array:${productTags.map((tag) => tag.trim()).filter(Boolean).join("\u0001")}`
    : `string:${String(productTags ?? "")}`;
  const productTagsList = useMemo(() => {
    const raw = productTagsKey.slice(productTagsKey.indexOf(":") + 1);
    if (!raw) return [];
    if (productTagsKey.startsWith("array:")) return raw.split("\u0001").map((tag) => tag.trim()).filter(Boolean);
    return raw.split(",").map((tag) => tag.trim()).filter(Boolean);
  }, [productTagsKey]);

  const effectiveProductImages = stableProductImages.length > 0 ? stableProductImages : undefined;
  const effectiveProductCarouselItems = stableProductCarouselItems.length > 0 ? stableProductCarouselItems : undefined;
  const effectiveProductUrl = useMemo(() => {
    const source = productUrl?.trim();
    if (source) {
      try {
        return typeof window !== "undefined" ? new URL(source, window.location.origin).href : source;
      } catch {
        return source;
      }
    }
    return typeof window !== "undefined" ? window.location.href : undefined;
  }, [productUrl]);
  const resolvedProductFitType = useMemo(
    () => detectProductFitType({
      productFitType,
      productTitle,
      productCategory,
      productSubcategory,
      productDescription,
      productType,
      productTags: productTagsList,
    }),
    [productFitType, productTitle, productCategory, productSubcategory, productDescription, productType, productTagsList],
  );
  const measurementType = productFitTypeToMeasurementType(resolvedProductFitType);
  const deterministicProductGender = useMemo(
    () => detectProductGender({
      productGender,
      productTitle,
      productCategory,
      productSubcategory,
      productDescription,
      productType,
      productTags: productTagsList,
    }, sizeGuideData),
    [productGender, productTitle, productCategory, productSubcategory, productDescription, productType, productTagsList, sizeGuideData],
  );
  const [aiProductGender, setAiProductGender] = useState<ProductGender | null>(null);
  const effectiveProductGender = deterministicProductGender ?? aiProductGender;
	  const productContext = useMemo(() => ({
	    title: productTitle,
	    productId: effectiveProductId,
	    productImage,
	    productImages: effectiveProductImages,
	    category: productCategory,
    gender: effectiveProductGender,
	    subcategory: productSubcategory,
	    productType,
    productFitType: resolvedProductFitType,
    vendor: productVendor,
    tags: productTagsList,
    description: productDescription || "",
    material: productMaterial,
    variants: [],
  }), [
	    productTitle,
	    effectiveProductId,
	    productImage,
    effectiveProductImages,
    productCategory,
    effectiveProductGender,
    productSubcategory,
    productType,
    resolvedProductFitType,
    productVendor,
    productTagsList,
    productDescription,
	    productMaterial,
	  ]);

  const styleMatchProduct = useMemo<StyleMatchRequestProduct>(() => ({
    productId: optionalStyleText(effectiveProductId),
    title: optionalStyleText(productTitle),
    image: optionalStyleText(productImage),
    images: (effectiveProductImages || []).filter((image) => optionalStyleText(image)),
    url: optionalStyleText(effectiveProductUrl),
    category: optionalStyleText(productCategory),
    subcategory: optionalStyleText(productSubcategory),
    type: optionalStyleText(productType),
    fitType: optionalStyleText(resolvedProductFitType),
    gender: optionalStyleText(effectiveProductGender),
    vendor: optionalStyleText(productVendor),
    tags: productTagsList.map((tag) => tag.trim()).filter(Boolean),
    description: optionalStyleText(productDescription),
    material: optionalStyleText(productMaterial),
    variants: [],
  }), [
    effectiveProductId,
    productTitle,
    productImage,
    effectiveProductImages,
    effectiveProductUrl,
    productCategory,
    productSubcategory,
    productType,
    resolvedProductFitType,
    effectiveProductGender,
    productVendor,
    productTagsList,
    productDescription,
    productMaterial,
  ]);

  // ── i18n ──
  const [activeLocale, setActiveLocale] = useState(() => locale || "");
  // Sync if parent changes the prop
  useEffect(() => { if (locale !== undefined) setActiveLocale(locale); }, [locale]);
	  const t: TranslateFn = useMemo(() => createT(activeLocale || undefined), [activeLocale]);
	  const resolvedButtonText = buttonText ?? (
	    measurementType === "foot" ? t("Fit with AI") : t("Virtual Try-On")
	  );
  const productIpLimitMaxAttempts = useMemo(() => (
    Math.max(1, Math.floor(Number(tryOnProductIpLimitMaxAttempts) || 1))
  ), [tryOnProductIpLimitMaxAttempts]);
  const shouldUseStorefrontSavedFit = useMemo(() => (
    shouldUseShopifyCartAttribution(apiUrl)
  ), [apiUrl]);
  const shouldUseCurrentRunSavedFit = useMemo(() => (
    limitOneColorTryOnPerProduct ||
    productIpLimitMaxAttempts <= 1
  ), [limitOneColorTryOnPerProduct, productIpLimitMaxAttempts]);
	  const resolvedPortalContainer =
	    portalContainer ?? (typeof document !== "undefined" ? document.body : null);

  // ── FOUC guard: hide button until CSS is confirmed injected ──
  const [cssReady, setCssReady] = useState(false);
  useEffect(() => { setCssReady(true); }, []);

  // ── Core state ──
  const [view, setView] = useState<ViewState>(initialView ?? "idle");
  const [noSizeReason, setNoSizeReason] = useState<"no-chart" | "no-match">("no-chart");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [resultImageUrl, setResultImageUrl] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  // Wall-clock start time for the in-flight try-on so the button can render
  // an elapsed counter (e.g. "Generating try-on… 8s"). null = not running.
  const [tryOnStartedAt, setTryOnStartedAt] = useState<number | null>(null);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  // User-picked size override via "Use this for Try On" — read by
  // handleTryOnSubmit / handleRetryWithFit to rebuild silhouette context.
  // Holds the full override object so we don't need to re-look up chart
  // cells in the parent (SectionDetailView already did that math — we pass
  // its `matchDetails` through verbatim).
  type CustomOverride = {
    sectionName: string;
    selectedSize: string;
    selectedLength?: string;
    displayLabel: string;
    matchDetails?: Array<{ measurement: string; userValue: string; chartRange: string; fit: string }>;
  };
  // Per-section overrides. User can customize Jacket (42/Short) AND Pants
  // (33/30) independently — both are remembered and both flow into the next
  // try-on / silhouette prompt. Keyed by section name (e.g. "Jacket").
  const [pendingCustomSizes, setPendingCustomSizes] = useState<Record<string, CustomOverride>>({});
  const pendingCustomSizesRef = useRef<Record<string, CustomOverride>>({});
  useEffect(() => { pendingCustomSizesRef.current = pendingCustomSizes; }, [pendingCustomSizes]);
  // Mirror sizingResult / sizeGuide into refs so handleRetryWithFit (which
  // doesn't list them in its useCallback deps) reads fresh values instead of
  // a stale closure — without that, the retry call to buildSilhouetteContext
  // gets an empty sizingResult and drops userMeasurementsText, leaving Gemini
  // with unitless body-vs-garment numbers.
  const sizingResultRef = useRef<import("./types").SizingResult | null>(null);
  const sizeGuideRef = useRef<SizeGuide | null>(null);
  // Mirror resultImageUrl too — handleRetryWithFit decides between sending
  // the previous PNG result or recompressing the user's raw photo, and
  // without the ref it captures a stale closure on subsequent retries
  // (sends the original PNG over and over even after newer ones land).
  const resultImageUrlRef = useRef<string | null>(null);

  // Section-scoped mutator. `null` clears that section's override.
  // `setPendingCustomSizes({})` (empty) would clear everything — used below
  // when the backend returns a new recommendation (new profile / photo).
  // Also writes pendingCustomSizesRef synchronously inside the updater so a
  // try-on submit fired the moment after this returns reads the new value
  // instead of waiting for the mirror useEffect to flush — that race was
  // dropping the user's first-try inseam pick.
  const setPendingCustomSizeBySection = useCallback((sectionName: string, override: CustomOverride | null) => {
    setPendingCustomSizes(prev => {
      const next = { ...prev };
      if (override === null) delete next[sectionName];
      else next[sectionName] = { ...override, sectionName };
      pendingCustomSizesRef.current = next;
      return next;
    });
  }, []);

  // Reset all section overrides when the backend returns a new recommendation
  // (new photo upload, different profile). Owner lives here (not in
  // SizeResultView) so all downstream readers see the cleared map atomically.

  // ── Sizing state ──
  const [sizingMethod, setSizingMethod] = useState<"exact" | "quick" | null>(null);
  const [sizingResult, setSizingResult] = useState<import("./types").SizingResult | null>(null);
  const [sizingLoading, setSizingLoading] = useState(false);
  // Gates the Try-On affordance — only true when sizing came in via the
  // AI photo flow (we have the user's photo). Manual measurements path
  // skips Try-On entirely and shows "Continue Shopping" instead.
  const [usedAiPhotoSizing, setUsedAiPhotoSizing] = useState(false);
  // Reset all section overrides when the backend returns a new recommendation
  // (fresh profile / photo) — stale picks from a previous shape no longer
  // apply to the new chart rows.
  useEffect(() => {
    if (!sizingResult?.recommendedSize) return;
    console.log("[ps-sdk:reset] new sizingResult — clearing all section overrides", {
      recommendedSize: sizingResult.recommendedSize,
    });
    setPendingCustomSizes({});
  }, [sizingResult?.recommendedSize]);
  useEffect(() => {
    const recommendedSize = sizingResult?.recommendedSize;
    if (!recommendedSize || !shouldUseShopifyCartAttribution(apiUrl)) return;

    setLastCompletedProduct(effectiveProductId ?? null, productTitle || null);
    setLastSizeSelection({
      productId: effectiveProductId ?? null,
      productTitle: productTitle || null,
      size: recommendedSize,
      recommendedSize,
    });
  }, [apiUrl, effectiveProductId, productTitle, sizingResult?.recommendedSize]);
  // True once /sizing/estimate has resolved (real milestone, not a timer).
  // Drives the desktop snap-step card's transition from "Analyzing your size"
  // (spinner) → ✓ to "Finding best fit for you" (spinner).
  const [estimationDone, setEstimationDone] = useState(false);
  const [tryOnProcessing, setTryOnProcessing] = useState(false);
  const styleMatchSelectionsRef = useRef<StyleMatchSelection[]>([]);
  const [resultStyleMatchSelections, setResultStyleMatchSelections] = useState<StyleMatchSelection[]>([]);
  const [sizeGuide, setSizeGuide] = useState<SizeGuide | null>(null);
  // When sizing reports the user is off-chart (found:false or match% < 50)
  // we suppress the in-flight try-on result so the UI shows "no size found"
  // immediately instead of waiting for Gemini to come back with an image
  // we'd discard anyway. Reset on every new sizing submission.
  const noFitFoundRef = useRef(false);
  useEffect(() => { sizingResultRef.current = sizingResult; }, [sizingResult]);
  useEffect(() => { sizeGuideRef.current = sizeGuide; }, [sizeGuide]);
  useEffect(() => { resultImageUrlRef.current = resultImageUrl; }, [resultImageUrl]);
  const transientTryOnResultUrlRef = useRef<string | null>(null);
  const [sizeGuideFetching, setSizeGuideFetching] = useState(false);
  const sizeGuideFetchedRef = useRef(false);
  const [sizingCountry, setSizingCountry] = useState(detectLocale);
  const imperial = isImperial(sizingCountry);
  const [sizingUnit, setSizingUnit] = useState(imperial ? "in" : "cm");
  // heightUnit values: "cm" (metric, value is cm) or "in" (imperial, value is total inches).
  const [heightUnit, setHeightUnit] = useState(imperial ? "in" : "cm");
  const [weightUnit, setWeightUnit] = useState(imperial ? "lbs" : "kg");

  // Shoe size charts are universally keyed in cm (foot length). Imperial shoe
  // sizing (US/UK numbers) is a conversion the backend applies from cm — the
  // SDK input should always be cm so the user isn't typing inches that the
  // backend has to convert. Force cm on mount + whenever the product changes
  // to a shoe, regardless of the user's detected locale.
  useEffect(() => {
    // Accessory products use unit systems that don't map to the body
    // imperial/metric split: shoes use cm, hats use cm, eyewear uses mm.
    // Force those defaults so the CTA unit label ("Metric" / "mm") matches
    // what the accessory view actually shows, regardless of locale.
    if (measurementType === "foot" || measurementType === "head" || measurementType === "wrist" || measurementType === "body-basic") setSizingUnit("cm");
    else if (measurementType === "face") setSizingUnit("mm");
  }, [measurementType, imperial]);
  const formRef = useRef<Record<string, string>>({});
  const [formGender, setFormGender] = useState<string>("male");
  const [formKey, setFormKey] = useState(0);
  const [bodyProfileInitialStep, setBodyProfileInitialStep] = useState<"photo" | "basics" | null>(initialBodyProfileStep ?? null);

  // ── Estimation state (quick → estimated measurements → exact) ──
  const [estimatedValues, setEstimatedValues] = useState<Record<string, number> | null>(null);
  const [estimationLoading, setEstimationLoading] = useState(false);

  // ── Profile & History ──
  const initialProfileSession = useMemo(() => getStoredProfileSession(), []);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [profileSession, setProfileSession] = useState<ProfileAuthSession | null>(() => initialProfileSession);
  const profileSessionRef = useRef<ProfileAuthSession | null>(profileSession);
  const [profileCompletionDraft, setProfileCompletionDraft] = useState<ProfileCompletionDraft | null>(null);
  const [profileAuthError, setProfileAuthError] = useState<string | null>(null);
  const [profileAuthLoadingProvider, setProfileAuthLoadingProvider] = useState<SocialProvider | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>(() => lsGet<HistoryEntry[]>("history", []).map(normalizeHistoryEntry));
  const [backendSavedFitEntry, setBackendSavedFitEntry] = useState<HistoryEntry | null>(null);
  const [backendSavedFitLookup, setBackendSavedFitLookup] = useState(false);
  const [storeTryOnLimitReached, setStoreTryOnLimitReached] = useState(false);
  const storeTryOnLimitReachedRef = useRef(false);
  const [currentRunSavedFitEntry, setCurrentRunSavedFitEntry] = useState<HistoryEntry | null>(null);
  const [pendingBackendRestoreEntry, setPendingBackendRestoreEntry] = useState<HistoryEntry | null>(null);
  const currentHistoryEntryIdRef = useRef<string | null>(null);
  const currentTryOnJobIdRef = useRef<string | null>(null);
  const historySavedRef = useRef(false);
  const historyTryonSavedRef = useRef(false);
  const sdkOpenIdRef = useRef<string | null>(null);
  const submittedFeedbackKeysRef = useRef<Set<string>>(new Set(lsGet<string[]>(TRYON_FEEDBACK_SUBMITTED_KEY, [])));
  useEffect(() => { storeTryOnLimitReachedRef.current = storeTryOnLimitReached; }, [storeTryOnLimitReached]);
  // Override the productImage/productTitle props when restoring a history
  // entry from a different product than the page is currently on. Without
  // this, the size info reflects the historical entry but the left-side
  // product image stays as the current product — visually out of sync.
  // Cleared whenever the user starts a fresh sizing run on the current page.
  // Mobile-only hamburger menu — collapses Profile / History / language
  // switcher into a small popover on mobile so the header doesn't have to
  // fit five separate controls in a 360 px viewport.
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileMenuBtnRef = useRef<HTMLButtonElement | null>(null);
  const [restoredProductImage, setRestoredProductImage] = useState<string | null>(null);
  const [restoredProductImages, setRestoredProductImages] = useState<string[] | null>(null);
  const [restoredProductCarouselItems, setRestoredProductCarouselItems] = useState<Array<{ image: string; title?: string; href?: string }> | null>(null);
  const [restoredProductTitle, setRestoredProductTitle] = useState<string | null>(null);
  const [restoredProductUrl, setRestoredProductUrl] = useState<string | null>(null);
  const [restoredHistoryEntryId, setRestoredHistoryEntryId] = useState<string | null>(null);
  const [restoredHistoryMatchesCurrentProduct, setRestoredHistoryMatchesCurrentProduct] = useState(false);
  const [activeProfileId, setActiveProfileIdState] = useState<string | null>(null);
  // Profile IDs currently running background estimation — used to render a
  // "calculating..." spinner in ProfileMeasurementsView instead of "—" dashes.
  const [estimatingProfileIds, setEstimatingProfileIds] = useState<Set<string>>(() => new Set());
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const setActiveProfileId = useCallback((id: string | null) => {
    setActiveProfileIdState(id);
    lsRemove("active_profile_id");
  }, []);
  useEffect(() => {
    profileSessionRef.current = profileSession;
  }, [profileSession]);
  const [profileSaved, setProfileSaved] = useState(false);
  const [drawer, setDrawer] = useState<DrawerType>(null);
  const [profileDetail, setProfileDetail] = useState<Profile | null>(null);
  // Track the view before opening profiles/history so we can restore it on back
  const prevViewRef = useRef<ViewState | null>(null);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [drawer, view]);

  // Fix 5: refs for profile screen back-button interception
  const profilesCanGoBackRef = useRef<() => boolean>(() => false);
  const profilesGoBackRef = useRef<() => void>(() => {});
  const profileSyncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const profileSyncHydratingRef = useRef(false);
  const [profileStoreHydrating, setProfileStoreHydrating] = useState(false);
  const profileSyncSnapshotRef = useRef<string>("");
  const handleRegisterProfilesBack = useCallback((canGoBack: () => boolean, goBack: () => void) => {
    profilesCanGoBackRef.current = canGoBack;
    profilesGoBackRef.current = goBack;
  }, []);

  const clearProfileCompletionDraft = useCallback(() => {
    lsRemove(PROFILE_COMPLETION_DRAFT_KEY);
    setProfileCompletionDraft(null);
  }, []);

  const buildProfileCompletionDraft = useCallback((): ProfileCompletionDraft => {
    const f = formRef.current || {};
    const active = profiles.find((p) => p.id === activeProfileId) || null;
    const gender = f.gender === "female" || active?.gender === "female" ? "female" : "male";
    const customMeasurements: Record<string, number> = {};
    const wristCircumference = positiveNumber(f.wristCircumference);
    if (wristCircumference != null) customMeasurements.wristCircumference = wristCircumference;
    const resultMeasurements = cleanProfileMeasurements((sizingResult as any)?.estimates);
    const resultMeasurementsUnit = (sizingResult as any)?.estimatesUnit === "in" ? "in" : "cm";
    const formHeight = positiveNumber(f.height);
    const activeHeight = positiveNumber(active?.height ?? active?.heightCm);
    const estimateHeight = positiveNumber(resultMeasurements?.height);
    const formWeight = positiveNumber(f.weight);
    const activeWeight = positiveNumber(active?.weight ?? active?.weightKg);
    const estimateWeight = positiveNumber((resultMeasurements as any)?.weight);
    const height = formHeight ?? activeHeight ?? estimateHeight;
    const weight = formWeight ?? activeWeight ?? estimateWeight;
    const heightUnitForDraft = formHeight != null
      ? (f.heightUnit || active?.heightUnit || heightUnit)
      : activeHeight != null
        ? (active?.heightUnit || heightUnit)
        : estimateHeight != null
          ? resultMeasurementsUnit
          : (f.heightUnit || active?.heightUnit || heightUnit);
    const weightUnitForDraft = formWeight != null
      ? (f.weightUnit || active?.weightUnit || weightUnit)
      : activeWeight != null
        ? (active?.weightUnit || weightUnit)
        : estimateWeight != null
          ? resultMeasurementsUnit
          : (f.weightUnit || active?.weightUnit || weightUnit);
    const formPhotoBase64 =
      typeof f.photoBase64 === "string" && f.photoBase64.trim()
        ? f.photoBase64.trim()
        : typeof f.bodyImage === "string" && f.bodyImage.trim()
          ? f.bodyImage.trim()
          : null;
    const activePhotoUrl = active?.photoUrl?.trim() || null;
    const activePhotoBase64 = active?.photoBase64?.trim() || null;
    return {
      source: "size-result",
      createdAt: Date.now(),
      productId: effectiveProductId,
      productTitle,
      gender,
      ...(height != null ? { height } : {}),
      ...(weight != null ? { weight } : {}),
      heightUnit: heightUnitForDraft,
      weightUnit: weightUnitForDraft,
      sizingUnit: active?.sizingUnit || sizingUnit,
      age: positiveNumber(f.age) ?? positiveNumber(active?.age),
      chestProfile: validChestProfile(f.chestProfile || active?.chestProfile),
      midsectionProfile: validMidsectionProfile(f.midsectionProfile || active?.midsectionProfile),
      hipProfile: validHipProfile(f.hipProfile || active?.hipProfile),
      bandSize: f.bandSize || active?.bandSize,
      cupSize: f.cupSize || active?.cupSize,
      braSizeRegion: f.braSizeRegion || (active as any)?.braSizeRegion || "US",
      ...(resultMeasurements ? { measurements: resultMeasurements, measurementsUnit: resultMeasurementsUnit } : {}),
      ...(Object.keys(customMeasurements).length > 0 ? { customMeasurements } : {}),
      ...(formPhotoBase64
        ? { photoBase64: formPhotoBase64 }
        : activePhotoUrl
          ? { photoUrl: activePhotoUrl }
          : activePhotoBase64
            ? { photoBase64: activePhotoBase64 }
            : {}),
    };
  }, [activeProfileId, effectiveProductId, heightUnit, productTitle, profiles, sizingResult, sizingUnit, weightUnit]);

  const handleCompleteProfileFromResult = useCallback(async () => {
    const draft = buildProfileCompletionDraft();
    setProfileCompletionDraft(draft);
    setProfileAuthError(null);
    setView("profiles");
  }, [buildProfileCompletionDraft]);

  const applyProfileStore = useCallback((nextProfiles: Profile[], nextActiveProfileId: string | null) => {
    const cleanProfiles = sanitizeProfilesForPersistence(nextProfiles);
    setProfiles(cleanProfiles);
    setActiveProfileIdState(nextActiveProfileId);
    clearProfileLocalStorage();
    profileSyncSnapshotRef.current = JSON.stringify({ profiles: cleanProfiles, activeProfileId: nextActiveProfileId });
  }, []);

  const clearInvalidProfileSession = useCallback(() => {
    if (profileSyncTimerRef.current) {
      clearTimeout(profileSyncTimerRef.current);
      profileSyncTimerRef.current = null;
    }
    clearStoredProfileSession();
    clearProfileLocalStorage();
    profileSessionRef.current = null;
    setProfileSession(null);
    applyProfileStore([], null);
    setProfileCompletionDraft(null);
    setProfileSaved(false);
    setProfileDetail(null);
    setProfileAuthLoadingProvider(null);
  }, [applyProfileStore]);

  const hydrateProfileStore = useCallback(async (session: ProfileAuthSession, seedProfiles: Profile[], seedActiveProfileId: string | null) => {
    profileSyncHydratingRef.current = true;
    setProfileStoreHydrating(true);
    try {
      const remoteStore = await fetchRemoteProfiles(apiUrl, session.accessToken);
      if (remoteStore.profiles.length > 0) {
        applyProfileStore(remoteStore.profiles, remoteStore.activeProfileId);
        return;
      }

      if (seedProfiles.length > 0) {
        const savedStore = await saveRemoteProfiles(apiUrl, session.accessToken, sanitizeProfilesForPersistence(seedProfiles), seedActiveProfileId);
        applyProfileStore(savedStore.profiles, savedStore.activeProfileId);
        return;
      }

      applyProfileStore([], null);
    } finally {
      profileSyncHydratingRef.current = false;
      setProfileStoreHydrating(false);
    }
  }, [apiUrl, applyProfileStore]);

  const translateProfileAuthError = useCallback((error: unknown): string => {
    if (!(error instanceof Error)) return t("Social login failed. Please try again.");

    switch (error.message) {
      case "Social login must run in the browser.":
      case "Popup was blocked. Please allow popups and try again.":
      case "Social login failed.":
      case "Login did not finish. Please close the sign-in window and try again.":
        return t(error.message);
      default:
        return error.message || t("Social login failed. Please try again.");
    }
  }, [t]);

  const handleSocialProfileLogin = useCallback(async (provider: SocialProvider) => {
    setProfileAuthError(null);
    setProfileAuthLoadingProvider(provider);
    try {
      const session = await startSocialProfileLogin(provider, apiUrl);
      setStoredProfileSession(session);
      profileSessionRef.current = session;
      setProfileSession(session);
      await hydrateProfileStore(session, [], null);
    } catch (error) {
      if (isProfileAuthError(error)) {
        clearInvalidProfileSession();
        setProfileAuthError(t("Please sign in again to load your saved profiles."));
        return;
      }
      setProfileAuthError(translateProfileAuthError(error));
    } finally {
      setProfileAuthLoadingProvider(null);
    }
  }, [apiUrl, clearInvalidProfileSession, hydrateProfileStore, t, translateProfileAuthError]);

  const handleProfileLogout = useCallback(() => {
    clearStoredProfileSession();
    clearProfileLocalStorage();
    profileSessionRef.current = null;
    setProfileSession(null);
    setProfiles([]);
    setActiveProfileIdState(null);
    setProfileCompletionDraft(null);
    setProfileSaved(false);
    setProfileDetail(null);
    formRef.current = {};
    setFormKey((k) => k + 1);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setSelectedFile(null);
    selectedFileRef.current = null;
    setPreviewUrl(null);
    setBodyLandmarks(null);
    setFaceLandmarks(null);
    setEstimatedValues(null);
    setEstimatingProfileIds(new Set());
    setProfileAuthError(null);
    setProfileAuthLoadingProvider(null);
  }, [previewUrl]);

  // ── Refs ──
  const fileInputRef = useRef<HTMLInputElement>(null);
  const apiRef = useRef<ApiClient | null>(null);
  const sseRef = useRef<SseClient | null>(null);
  const unsubRef = useRef<(() => void) | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const completedRef = useRef(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const modelPoseRef = useRef<MeasurementLines | null>(null);
  const [bodyLandmarks, setBodyLandmarks] = useState<BodyLandmarks | null>(null);
  const [faceLandmarks, setFaceLandmarks] = useState<FaceLandmarks | null>(null);
  const selectedFileRef = useRef<File | null>(null);
  // Backend-cached photo id: filled in by the first /tryon response, sent
  // back on every subsequent regen so the backend reuses the canonical
  // bytes from its in-memory cache instead of re-decoding a fresh JPEG.
  // Cleared whenever a new photo is selected (see file-upload paths).
  const modelImageIdRef = useRef<string | null>(null);
  // True once the auto-fire effect has submitted try-on for the current
  // sizing flow. Reset when sizingLoading flips back to false so a fresh
  // sizing pass re-fires.
  const autoTryOnFiredRef = useRef(false);

  // Pre-picked garment-reference image — populated by a Gemini Flash call
  // fired the moment we know the productImages list. By the time the user
  // hits "Try On" the answer is sitting in the ref, no extra latency in
  // the try-on path. Falls back to the in-browser MediaPipe scorer when
  // the backend is unreachable.
  const bestGarmentImageRef = useRef<string | null>(null);
  const bestGarmentDetailImageRef = useRef<string | null>(null);

  const resetFreshSizingRunState = useCallback(() => {
    completedRef.current = false;
    autoTryOnFiredRef.current = false;
    modelImageIdRef.current = null;
    noFitFoundRef.current = false;
    historySavedRef.current = false;
    historyTryonSavedRef.current = false;
    currentHistoryEntryIdRef.current = null;
    currentTryOnJobIdRef.current = null;
    resultImageUrlRef.current = null;
    sizingResultRef.current = null;

    setTryOnProcessing(false);
    setTryOnStartedAt(null);
    setSizingResult(null);
	    setResultImageUrl(null);
	    setBackendSavedFitEntry(null);
	    setBackendSavedFitLookup(false);
	    setCurrentRunSavedFitEntry(null);
    setPendingBackendRestoreEntry(null);
    setRestoredProductImage(null);
    setRestoredProductImages(null);
    setRestoredProductCarouselItems(null);
	    setRestoredProductTitle(null);
	    setRestoredProductUrl(null);
	    setRestoredHistoryEntryId(null);
	    setRestoredHistoryMatchesCurrentProduct(false);
    setActiveSection(null);
    setNoSizeReason("no-chart");
  }, []);

  // Styles already injected at module scope (top of file)

  // ── Init API client ──
  useEffect(() => {
		try {
				const key = getApiKey();
				const url = getApiUrl(apiUrl);
				apiRef.current = new ApiClient(key, url);
				sseRef.current = null;
			} catch { /* handled on submit */ }
    return () => {
      unsubRef.current?.();
      sseRef.current?.disconnect();
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [apiUrl]);

	  useEffect(() => {
	    let cancelled = false;
	    setBackendSavedFitEntry(null);
	    if (!shouldUseShopifyCartAttribution(apiUrl) || !effectiveProductId) {
	      setBackendSavedFitLookup(false);
	      setStoreTryOnLimitReached(false);
	      return;
	    }
	    if (!shouldUseStorefrontSavedFit) {
	      setBackendSavedFitLookup(false);
	      setStoreTryOnLimitReached(false);
	      return;
	    }
	    const client = apiRef.current;
	    if (!client) {
	      setBackendSavedFitLookup(false);
	      setStoreTryOnLimitReached(false);
	      return;
	    }

	    setBackendSavedFitLookup(true);
	    client.getSavedTryOnResult({ productId: effectiveProductId })
	      .then((savedResult) => {
	        if (cancelled) return;
	        setStoreTryOnLimitReached(savedResult.storeLimitReached === true);
	        if (!savedResult.found) return;
	        if (!limitOneColorTryOnPerProduct && savedResult.productLimitReached === false) return;
	        setBackendSavedFitEntry(savedTryOnResultToHistoryEntry(savedResult, {
          productImage,
          productImages: effectiveProductImages,
          productCarouselItems: effectiveProductCarouselItems,
          productTitle,
          productUrl: effectiveProductUrl,
        }));
      })
	      .catch(() => {
	        /* non-fatal: local history fallback still works */
	      })
	      .finally(() => {
	        if (!cancelled) setBackendSavedFitLookup(false);
	      });

	    return () => {
	      cancelled = true;
	    };
  }, [
    apiUrl,
    effectiveProductId,
    effectiveProductImages,
    effectiveProductCarouselItems,
    effectiveProductUrl,
    limitOneColorTryOnPerProduct,
    productImage,
    shouldUseStorefrontSavedFit,
    productTitle,
  ]);

  const sdkReplayOpen = view !== "idle";

  useEffect(() => {
    if (!sdkReplayOpen) {
      sdkOpenIdRef.current = null;
      return;
    }
    if (!sdkOpenIdRef.current) sdkOpenIdRef.current = createSdkOpenId();
    startSdkClarity({
      projectId: clarityProjectId,
      sdkOpenId: sdkOpenIdRef.current,
      productId: effectiveProductId,
      productTitle: restoredProductTitle || productTitle,
      productUrl: restoredProductUrl || effectiveProductUrl,
      view,
      jobId: currentTryOnJobIdRef.current,
      source: shouldUseShopifyCartAttribution(apiUrl) ? "shopify-sdk" : "sdk",
    });
  }, [apiUrl, clarityProjectId, effectiveProductId, effectiveProductUrl, productTitle, restoredProductTitle, restoredProductUrl, sdkReplayOpen, view]);

  const reportSdkEvent = useCallback((
    eventType: SdkBehaviorEventType,
    input: {
      jobId?: string;
      recommendedSize?: string;
      metadata?: Record<string, unknown>;
    } = {},
  ) => {
    if (shouldUseShopifyCartAttribution(apiUrl)) return;
    const client = apiRef.current;
    if (!client) return;
    const sdkOpenId = sdkOpenIdRef.current ?? undefined;
    void client.reportEvent({
      eventType,
      sdkOpenId,
      productId: effectiveProductId,
      productTitle: restoredProductTitle || productTitle,
      productUrl: restoredProductUrl || effectiveProductUrl,
      jobId: input.jobId ?? currentTryOnJobIdRef.current ?? undefined,
      recommendedSize: input.recommendedSize ?? sizingResultRef.current?.recommendedSize,
      metadata: {
        ...(sdkOpenId ? { sdkOpenId } : {}),
        ...(input.metadata ?? {}),
      },
    }).catch(() => {});
  }, [apiUrl, effectiveProductId, effectiveProductUrl, productTitle, restoredProductTitle, restoredProductUrl]);

  const reportProfileEvent = useCallback((
    eventType: Extract<SdkBehaviorEventType, "SDK_PROFILE_CREATED" | "SDK_PROFILE_UPDATED" | "SDK_PROFILE_SELECTED">,
    profile: Pick<Profile, "id" | "name"> | null | undefined,
    metadata: Record<string, unknown> = {},
  ) => {
    const sdkOpenId = sdkOpenIdRef.current ?? undefined;
    const payload = {
      eventType,
      sdkOpenId,
      productId: effectiveProductId,
      productTitle: restoredProductTitle || productTitle,
      productUrl: restoredProductUrl || effectiveProductUrl,
      profileLoggedIn: !!profileSessionRef.current,
      profileId: profile?.id,
      profileName: profile?.name,
      profileOrigin: shouldUseShopifyCartAttribution(apiUrl) ? "shopify-storefront" : "direct-sdk",
      metadata: {
        ...(sdkOpenId ? { sdkOpenId } : {}),
        ...metadata,
      },
    };
    if (shouldUseShopifyCartAttribution(apiUrl)) {
      logProfileEvent(payload);
      return;
    }
    const client = apiRef.current;
    if (!client) return;
    void client.reportEvent(payload).catch(() => {});
  }, [apiUrl, effectiveProductId, effectiveProductUrl, productTitle, restoredProductTitle, restoredProductUrl]);

  const reportSdkClientError = useCallback((input: {
    message: string;
    code?: string;
    stack?: string;
    component?: string;
    view?: string;
    severity?: "low" | "medium" | "high" | "critical";
    jobId?: string;
    metadata?: Record<string, unknown>;
  }) => {
    if (shouldUseShopifyCartAttribution(apiUrl)) return;
    const client = apiRef.current;
    if (!client) return;
    const sdkOpenId = sdkOpenIdRef.current ?? undefined;
    void client.reportClientError({
      ...input,
      productId: effectiveProductId,
      productTitle: restoredProductTitle || productTitle,
      productUrl: restoredProductUrl || effectiveProductUrl,
      jobId: input.jobId ?? currentTryOnJobIdRef.current ?? undefined,
      metadata: {
        ...(sdkOpenId ? { sdkOpenId } : {}),
        ...(input.metadata ?? {}),
      },
    }).catch(() => {});
  }, [apiUrl, effectiveProductId, effectiveProductUrl, productTitle, restoredProductTitle, restoredProductUrl]);

  const productViewLoggedRef = useRef<string | null>(null);
  useEffect(() => {
    const key = `${effectiveProductId || ""}|${effectiveProductUrl || ""}`;
    if (!key.trim() || productViewLoggedRef.current === key) return;
    productViewLoggedRef.current = key;
    if (shouldUseShopifyCartAttribution(apiUrl)) {
      logProductView({ productId: effectiveProductId, productTitle });
      return;
    }
    reportSdkEvent("PRODUCT_VIEW", {
      metadata: {
        productUrl: effectiveProductUrl,
        productFitType: resolvedProductFitType,
        productCategory,
        productSubcategory,
      },
    });
  }, [apiUrl, effectiveProductId, effectiveProductUrl, productCategory, productSubcategory, productTitle, reportSdkEvent, resolvedProductFitType]);

  // ── Pre-pick the best garment-reference image (Gemini Flash, async) ──
  // Fires the moment we know the product image list. Backend caches per
  // image-set hash, so repeat opens for the same product land instantly
  // and the answer is ready well before the user finishes the size form.
  const pickFireCountRef = useRef(0);
  const toBackendFetchableImageUrl = useCallback((url: string | null | undefined): string | null => {
    const trimmed = String(url || "").trim();
    if (!trimmed) return null;
    const candidate = trimmed.startsWith("//") ? `https:${trimmed}` : trimmed;
    try {
      if (typeof window !== "undefined") {
        return new URL(candidate, window.location.origin).toString();
      }
      return new URL(candidate).toString();
    } catch {
      return null;
    }
  }, []);
  useEffect(() => {
    pickFireCountRef.current += 1;
    const fireN = pickFireCountRef.current;
    const reason = !effectiveProductImages
      ? "no productImages"
      : effectiveProductImages.length < 2
        ? `productImages.length=${effectiveProductImages.length}`
        : `len=${effectiveProductImages.length} firstUrl=${effectiveProductImages[0]?.slice(0, 40)}`;
    console.log(`[ps-sdk:pick] effect FIRE #${fireN} — ${reason}`, {
      productImagesRef: effectiveProductImages,
      garmentReferenceImageSet: !!garmentReferenceImage,
      apiUrl,
      productTitle,
	    });
	    bestGarmentImageRef.current = null;
	    bestGarmentDetailImageRef.current = garmentDetailImage || null;
	    if (garmentReferenceImage) {
	      bestGarmentImageRef.current = garmentReferenceImage;
	      console.log(`[ps-sdk:pick] #${fireN} early-return — using prop override`);
	      return;
    }
    if (!effectiveProductImages || effectiveProductImages.length < 2) {
      console.log(`[ps-sdk:pick] #${fireN} early-return — too few images, no /pick-best call`);
      return;
    }
    const pickerImages = effectiveProductImages
      .map((url) => toBackendFetchableImageUrl(url))
      .filter((url): url is string => Boolean(url));
    if (pickerImages.length < 2) {
      console.log(`[ps-sdk:pick] #${fireN} early-return — fewer than 2 backend-fetchable image URLs`);
      return;
    }
    const baseUrl = getApiUrl(apiUrl);
    let cancelled = false;
    const t0 = Date.now();
    console.log(`[ps-sdk:pick] #${fireN} → POST /api/catalog/pick-best-garment-image`);
    fetch(`${baseUrl}/api/catalog/pick-best-garment-image`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ images: pickerImages, productTitle }),
    })
      .then((r) => (r.ok ? r.json() : null))
	      .then((j: { bestUrl?: string; modelUrl?: string; detailUrl?: string } | null) => {
	        if (cancelled) {
	          console.log(`[ps-sdk:pick] #${fireN} ← stale response ignored in ${Date.now() - t0}ms`);
	          return;
	        }
	        if (j?.bestUrl) {
	          bestGarmentImageRef.current = j.modelUrl || j.bestUrl;
	          bestGarmentDetailImageRef.current = garmentDetailImage || j.detailUrl || null;
	          console.log(`[ps-sdk:pick] #${fireN} ← OK in ${Date.now() - t0}ms → worn=${bestGarmentImageRef.current} detail=${bestGarmentDetailImageRef.current || "none"}`);
	        } else {
	          console.log(`[ps-sdk:pick] #${fireN} ← no bestUrl in response (${Date.now() - t0}ms)`);
	        }
      })
      .catch((e) => {
        if (!cancelled) {
          console.log(`[ps-sdk:pick] #${fireN} ← network error in ${Date.now() - t0}ms`, e?.name || e);
        }
      });
    return () => {
      cancelled = true;
      console.log(`[ps-sdk:pick] #${fireN} cleanup — stale response will be ignored`);
    };
	  }, [effectiveProductImages, garmentReferenceImage, garmentDetailImage, apiUrl, productTitle, toBackendFetchableImageUrl]);

  const pickBestGarmentImageViaBackend = useCallback(async (
    images: string[] | null | undefined,
  ): Promise<{ worn: string; detail: string | null } | null> => {
    const pickerImages = (images ?? [])
      .map((url) => toBackendFetchableImageUrl(url))
      .filter((url): url is string => Boolean(url));
    if (pickerImages.length < 2) return null;

    const t0 = Date.now();
    try {
      const response = await fetch(`${getApiUrl(apiUrl)}/api/catalog/pick-best-garment-image`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ images: pickerImages, productTitle }),
      });
      if (!response.ok) {
        console.log(`[ps-sdk:pick] submit retry failed HTTP ${response.status} in ${Date.now() - t0}ms`);
        return null;
      }
      const result = await response.json() as { bestUrl?: string; modelUrl?: string; detailUrl?: string } | null;
      const worn = result?.modelUrl || result?.bestUrl;
      if (!worn) {
        console.log(`[ps-sdk:pick] submit retry returned no worn image in ${Date.now() - t0}ms`);
        return null;
      }
      const detail = garmentDetailImage || result?.detailUrl || null;
      console.log(`[ps-sdk:pick] submit retry OK in ${Date.now() - t0}ms → worn=${worn} detail=${detail || "none"}`);
      return { worn, detail };
    } catch (error) {
      console.log(`[ps-sdk:pick] submit retry network error in ${Date.now() - t0}ms`, (error as Error)?.name || error);
      return null;
    }
  }, [apiUrl, garmentDetailImage, productTitle, toBackendFetchableImageUrl]);

  function summarizeSizeGuideForGender(value: unknown): unknown {
    if (!value || typeof value !== "object") return undefined;
    const obj = value as Record<string, unknown>;
    const summary: Record<string, unknown> = {};
    for (const key of ["title", "headers", "requiredFields", "fields", "unit", "found"]) {
      if (obj[key] != null) summary[key] = obj[key];
    }
    if (obj.sections && typeof obj.sections === "object") {
      summary.sections = Object.entries(obj.sections as Record<string, unknown>).slice(0, 8).map(([name, section]) => {
        if (!section || typeof section !== "object") return { name };
        const sec = section as Record<string, unknown>;
        return {
          name,
          headers: sec.headers,
          requiredFields: sec.requiredFields,
        };
      });
    }
    return Object.keys(summary).length ? summary : undefined;
  }

  // ── Pre-classify product gender only when local metadata has no answer ──
  // This is fired on mount/product change, before the shopper opens the SDK.
  // The backend route fails open with 200/null, so this never blocks the flow.
  useEffect(() => {
    setAiProductGender(null);
    if (measurementType !== "body" || deterministicProductGender) return;
    const baseUrl = getApiUrl(apiUrl);
    const ctrl = new AbortController();
    const t0 = Date.now();
    fetch(`${baseUrl}/api/catalog/classify-product-gender`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        productId: effectiveProductId,
        title: productTitle,
        category: productCategory,
        subcategory: productSubcategory,
        productType,
        vendor: productVendor,
        tags: productTagsList,
        description: productDescription,
        material: productMaterial,
        sizeGuideSummary: summarizeSizeGuideForGender(sizeGuide ?? sizeGuideData),
      }),
      signal: ctrl.signal,
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((j: { gender?: string | null; source?: string; confidence?: string; latencyMs?: number } | null) => {
        const gender = normalizeProductGender(j?.gender ?? null);
        if (gender) {
          setAiProductGender(gender);
          console.log(`[ps-sdk:gender] ${gender} via ${j?.source || "unknown"} in ${Date.now() - t0}ms`);
        } else {
          console.log(`[ps-sdk:gender] no Gemini gender answer in ${Date.now() - t0}ms`);
        }
      })
      .catch((e) => {
        if (e?.name !== "AbortError") console.log(`[ps-sdk:gender] prefetch failed open`, e?.name || e);
      });
    return () => ctrl.abort();
  }, [
    measurementType,
    deterministicProductGender,
    apiUrl,
    effectiveProductId,
    productTitle,
    productCategory,
    productSubcategory,
    productType,
    productVendor,
    productTagsList,
    productDescription,
    productMaterial,
    sizeGuide,
    sizeGuideData,
  ]);

  // ── Progress bar timer (DOM-direct to avoid re-renders) ──
  //
  // Wall-clock target. The backend SSE only emits "processing → completed",
  // so this is an estimate, not ground truth. Gemini 3 Pro Image Preview
  // latency observed between 15-25s; 22 lands near the p50.
  const TARGET_SECONDS = 22;
  const progressRef = useRef(0);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const progressTextRef = useRef<HTMLSpanElement>(null);
  const progressStatusRef = useRef<HTMLDivElement>(null);
  const progressEtaRef = useRef<HTMLSpanElement>(null);
  const progressRingRef = useRef<SVGCircleElement>(null);
  const progressStartTsRef = useRef<number | null>(null);
  const progressLastStageRef = useRef<string>("");
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (view === "processing") {
      // Only start fresh if not already running
      if (progressIntervalRef.current) return;
      progressRef.current = 0;
      progressStartTsRef.current = Date.now();
      progressLastStageRef.current = "";
      const statuses = [
        { at: 0, text: t("Preparing your image...") },
        { at: 15, text: t("Analyzing body proportions...") },
        { at: 30, text: t("Matching garment to your photo...") },
        { at: 50, text: t("Generating virtual try-on...") },
        { at: 75, text: t("Refining details...") },
        { at: 90, text: t("Almost there...") },
      ];
      // Ring circumference — must match ProcessingView SVG circle radius (38 → 2πr ≈ 238.76).
      const RING_CIRCUMFERENCE = 2 * Math.PI * 38;
      progressIntervalRef.current = setInterval(() => {
        if (completedRef.current) return;
        const startTs = progressStartTsRef.current || Date.now();
        const elapsed = (Date.now() - startTs) / 1000;
        const target = Math.min(95, (elapsed / TARGET_SECONDS) * 100);
        progressRef.current = target;
        const val = Math.round(target);
        if (progressBarRef.current) progressBarRef.current.style.width = `${val}%`;
        if (progressTextRef.current) progressTextRef.current.textContent = `${val}%`;
        if (progressRingRef.current) {
          const offset = RING_CIRCUMFERENCE * (1 - target / 100);
          progressRingRef.current.style.strokeDashoffset = String(offset);
        }
        if (progressEtaRef.current) {
          const remaining = Math.max(0, TARGET_SECONDS - Math.floor(elapsed));
          progressEtaRef.current.textContent = elapsed >= TARGET_SECONDS
            ? "•••"
            : `~${remaining}s`;
        }
        if (progressStatusRef.current) {
          const status = [...statuses].reverse().find(s => val >= s.at);
          if (status && status.text !== progressLastStageRef.current) {
            // Crossfade: instant fade-out, swap text, fade back in.
            const el = progressStatusRef.current;
            el.style.opacity = "0";
            setTimeout(() => {
              el.textContent = status.text;
              el.style.opacity = "1";
            }, 180);
            progressLastStageRef.current = status.text;
          }
        }
      }, 200);
      return () => { clearInterval(progressIntervalRef.current!); progressIntervalRef.current = null; };
    } else {
      // Leaving processing — clean up
      if (progressIntervalRef.current) { clearInterval(progressIntervalRef.current); progressIntervalRef.current = null; }
      progressStartTsRef.current = null;
    }
  }, [view, t]);

  // ── Cleanup preview URL ──
  useEffect(() => { return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }; }, [previewUrl]);

  // ── Lock body scroll ──
  // Lock-once-unlock-once via a ref guard. The previous effect re-fired on
  // every view change (idle → body-profile → profiles → …), and the
  // cleanup's window.scrollTo(0, savedScrollY) yanked the underlying page
  // back to its capture point on every internal step. That's why opening
  // Profiles felt like the host page was scrolling.
  const lockedScrollRef = useRef<{
    scrollY: number;
    prevHtmlOverflow: string;
    prevHtmlOverscroll: string;
    prevOverflow: string;
    prevOverscroll: string;
  } | null>(null);
  const unlockBodyScroll = useCallback((restoreScroll: boolean) => {
    if (typeof document === "undefined" || typeof window === "undefined") return;
    const locked = lockedScrollRef.current;
    if (!locked) return;
    lockedScrollRef.current = null;
    document.documentElement.style.overflow = locked.prevHtmlOverflow;
    document.documentElement.style.overscrollBehavior = locked.prevHtmlOverscroll;
    document.body.style.overflow = locked.prevOverflow;
    document.body.style.overscrollBehavior = locked.prevOverscroll;
    if (restoreScroll) window.scrollTo(0, locked.scrollY);
  }, []);

  useEffect(() => {
    const isOpen = view !== "idle";
    if (isOpen && lockedScrollRef.current == null) {
      lockedScrollRef.current = {
        scrollY: window.scrollY,
        prevHtmlOverflow: document.documentElement.style.overflow,
        prevHtmlOverscroll: document.documentElement.style.overscrollBehavior,
        prevOverflow: document.body.style.overflow,
        prevOverscroll: document.body.style.overscrollBehavior,
      };
      document.documentElement.style.overflow = "hidden";
      document.documentElement.style.overscrollBehavior = "none";
      document.body.style.overflow = "hidden";
      document.body.style.overscrollBehavior = "none";
      // NOTE: do NOT set position:fixed on body — it breaks position:fixed portals
      // (the modal overlay) by making body the containing block instead of the viewport.
      // overflow:hidden alone is sufficient to prevent scrolling.
      return;
    }
    if (!isOpen && lockedScrollRef.current != null) {
      unlockBodyScroll(true);
    }
  }, [view, unlockBodyScroll]);

  useEffect(() => {
    return () => unlockBodyScroll(false);
  }, [unlockBodyScroll]);

  // Migrate older history rows that stored data:/blob: result URLs directly
  // in localStorage. Keep them renderable in-memory, but move the binary
  // image into IndexedDB so future localStorage writes stay small.
  useEffect(() => {
    const entries = history.filter(entry => entry.resultImageUrl && isTransientHistoryImageUrl(entry.resultImageUrl));
    if (!entries.length) return;
    let cancelled = false;
    void Promise.all(entries.map(async (entry) => {
      try {
        const res = await fetch(entry.resultImageUrl!);
        if (!res.ok) return null;
        const blob = await res.blob();
        return await saveResultIDB(entry.id, blob) ? entry.id : null;
      } catch {
        return null;
      }
    })).then((ids) => {
      if (cancelled) return;
      const storedIds = new Set(ids.filter((id): id is string => !!id));
      if (!storedIds.size) return;
      setHistory(prev => prev.map(entry => storedIds.has(entry.id) ? {
        ...entry,
        resultImageUrl: durableHistoryImageUrl(entry.resultImageUrl),
        hasResult: true,
      } : entry));
    });
    return () => { cancelled = true; };
  }, [history]);

  // ── Persist history only. Profiles are backend-owned for signed-in users. ──
  useEffect(() => {
    clearProfileLocalStorage();
  }, []);
  useEffect(() => { lsSet("history", history.map(sanitizeHistoryEntry)); }, [history]);

  // ── Remote profile sync after social sign-in ──
  useEffect(() => {
    if (!profileSession) return;
    void hydrateProfileStore(profileSession, profiles, activeProfileId).catch((error) => {
      if (isProfileAuthError(error)) {
        clearInvalidProfileSession();
        setProfileAuthError(t("Please sign in again to load your saved profiles."));
        return;
      }
      setProfileAuthError(error instanceof Error ? error.message : t("Could not load your saved profiles."));
    });
    // Run only when the signed-in SDK user changes. Profile mutations are
    // handled by the debounced sync below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileSession?.accessToken]);

  useEffect(() => {
    if (!profileSession || profileSyncHydratingRef.current) return;

    const snapshot = JSON.stringify({ profiles, activeProfileId });
    if (snapshot === profileSyncSnapshotRef.current) return;

    if (profileSyncTimerRef.current) clearTimeout(profileSyncTimerRef.current);
    profileSyncTimerRef.current = setTimeout(() => {
      saveRemoteProfiles(apiUrl, profileSession.accessToken, profiles, activeProfileId)
        .then((store) => {
          applyProfileStore(store.profiles, store.activeProfileId);
        })
        .catch((error) => {
          if (isProfileAuthError(error)) {
            clearInvalidProfileSession();
            setProfileAuthError(t("Please sign in again to save your profiles."));
            return;
          }
          setProfileAuthError(error instanceof Error ? error.message : t("Profile sync failed. Your local changes are still saved here."));
        });
    }, 650);

    return () => {
      if (profileSyncTimerRef.current) clearTimeout(profileSyncTimerRef.current);
    };
  }, [activeProfileId, apiUrl, applyProfileStore, clearInvalidProfileSession, profileSession, profiles, t]);

  // ── Pre-fetch size guide on mount (so it's ready before user opens the modal) ──
  useEffect(() => {
    if (sizeGuideFetchedRef.current) return;
    sizeGuideFetchedRef.current = true;

    // No size guide data provided — skip
    if (!sizeGuideData) {
      setSizeGuide({ found: false });
      return;
    }

    if (!apiRef.current) { setSizeGuide({ found: false }); return; }
    setSizeGuideFetching(true);
    const baseUrl = getApiUrl(apiUrl);
    const key = getApiKey();

    fetch(`${baseUrl}/api/v1/sizing/sizeguide`, {
      method: "POST",
      headers: jsonHeaders(key),
      body: JSON.stringify({
        product: productContext,
        sizeGuideRaw: sizeGuideData,
      }),
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setSizeGuide(data); else setSizeGuide({ found: false }); })
      .catch(() => { setSizeGuide({ found: false }); })
      .finally(() => setSizeGuideFetching(false));
  }, [apiUrl, productContext, sizeGuideData]);

  // ── Step index ──
  const stepIndex = useMemo(() => {
    switch (view) {
      case "welcome": case "body-profile": return 1;
      case "estimation-review": case "size-result": return 2;
      case "upload": case "processing": case "result": return 3;
      default: return 1;
    }
  }, [view]);

  // ── Handlers ──

  // ─────────────────────────────────────────────────────────────────
  // Profile persistence: updates an existing active profile with the
  // LLM-computed measurements + the suggested size for the current
  // product. New profile creation is handled by the authenticated
  // profile wizard, not silently from an anonymous result.
  //
  // The full body measurements come from a parallel /sizing/estimate
  // call so the profile has chest/hips/sleeve/inseam/etc. for ANY
  // future product (not just this one). The headless
  // recommendForProduct() function then uses these stored measurements
  // to auto-size other products without re-running the modal.
  // ─────────────────────────────────────────────────────────────────
  const updateProfilesForCurrentSession = useCallback((updater: (profiles: Profile[]) => Profile[]) => {
    setProfiles((prev) => {
      const next = updater(prev);
      return sanitizeProfilesForPersistence(next);
    });
  }, []);

  const deleteProfileForCurrentSession = useCallback(async (id: string) => {
    const session = profileSessionRef.current;
    if (!session) {
      updateProfilesForCurrentSession((prev) => prev.filter((p) => p.id !== id));
      if (activeProfileId === id) setActiveProfileId(null);
      return;
    }

    if (profileSyncTimerRef.current) {
      clearTimeout(profileSyncTimerRef.current);
      profileSyncTimerRef.current = null;
    }

    try {
      const store = await deleteRemoteProfile(apiUrl, session.accessToken, id);
      applyProfileStore(store.profiles, store.activeProfileId);
    } catch (error) {
      if (isProfileAuthError(error)) {
        clearInvalidProfileSession();
        setProfileAuthError(t("Please sign in again to save your profiles."));
        return;
      }
      setProfileAuthError(error instanceof Error ? error.message : t("Profile sync failed. Your local changes are still saved here."));
      throw error;
    }
  }, [activeProfileId, apiUrl, applyProfileStore, clearInvalidProfileSession, t, updateProfilesForCurrentSession]);

  const persistResultToProfile = useCallback(
    (
      formData: {
        gender: "male" | "female";
        height: number;
        weight: number;
        heightUnit: "cm" | "in" | "ft";
        weightUnit: "kg" | "lbs";
        age?: number;
        chestProfile?: import("./types").ChestProfile;
        midsectionProfile?: import("./types").MidsectionProfile;
        hipProfile?: import("./types").HipProfile;
        bandSize?: string;
        cupSize?: string;
        braSizeRegion?: string;
        bodyImage?: string;
      },
      recommendation: import("./types").SizingResult | null,
      options?: { skipBodyEstimate?: boolean },
    ) => {
      void formData;
      // 1. Find the active profile. Anonymous size results stay as a
      // completion draft until the user signs in and adds a profile photo.
      let targetId = activeProfileId;
      let target = profiles.find((p) => p.id === targetId);
      if (!target) {
        setEstimationDone(true);
        return;
      }
      // Suppress lint — target is used implicitly via targetId in storage helpers
      void target;

      if (targetId && formData.gender === "female" && (formData.bandSize || formData.cupSize || formData.braSizeRegion)) {
        const patch: Partial<Profile> = {
          ...(formData.bandSize ? { bandSize: formData.bandSize } : {}),
          ...(formData.cupSize ? { cupSize: formData.cupSize } : {}),
          ...(formData.braSizeRegion ? { braSizeRegion: formData.braSizeRegion } : {}),
        };
        updateProfilesForCurrentSession((list) => list.map((p) => p.id === targetId ? { ...p, ...patch, lastEditedAt: Date.now() } : p));
      }

      const persistMeasurements = (est: Record<string, number>, unit: "cm" | "in") => {
        if (!targetId) return;
        updateProfilesForCurrentSession((list) => list.map((p) => p.id === targetId ? {
          ...p,
          measurements: est,
          measurementsUnit: unit,
          lastEditedAt: Date.now(),
        } : p));
      };

      const persistSizeHistory = (entry: import("./types").SizeHistoryEntry) => {
        if (!targetId) return;
        updateProfilesForCurrentSession((list) => list.map((p) => {
          if (p.id !== targetId) return p;
          const sizeHistory = (p.sizeHistory || []).filter((h) => h.productId !== entry.productId);
          sizeHistory.unshift(entry);
          return {
            ...p,
            sizeHistory: sizeHistory.slice(0, 50),
            lastUsedAt: Date.now(),
          };
        }));
      };

      // 2. Persist body measurements to the profile.
      //    /recommend now returns the full estimates inline (chest/waist/
      //    hips/inseam/etc.), so we no longer fire a separate /sizing/estimate
      //    call — saves ~10–15 s on every photo flow and keeps the numbers
      //    on screen identical to the numbers stored in the profile (no
      //    drift between two independent Gemini calls).
      //
      //    SKIP for face/head accessories (sunglasses, hats): /recommend
      //    for those products doesn't compute body estimates anyway.
      if (options?.skipBodyEstimate) {
        console.log("[ps-sdk:persist] skipping body estimates — face/head flow (no body context)");
      } else if (targetId && (recommendation as any)?.estimates) {
        const est = (recommendation as any).estimates as Record<string, number>;
        const unit = ((recommendation as any).estimatesUnit as "cm" | "in") || "cm";
        persistMeasurements(est, unit);
      }
      setEstimationDone(true);

      // 3. Save the per-product recommendation to history (for cache reuse)
      if (recommendation?.recommendedSize && targetId) {
        const sectionsMap: Record<string, string> | undefined = recommendation.sections
          ? Object.fromEntries(
              Object.entries(recommendation.sections).map(([name, sec]) => [name, sec.recommendedSize])
            )
          : undefined;
        persistSizeHistory({
          productId: effectiveProductId,
          productTitle,
          productImage,
          recommendedSize: recommendation.recommendedSize,
          confidence: recommendation.confidence,
          sections: sectionsMap,
          savedAt: Date.now(),
        });
      }
    },
    [activeProfileId, profiles, productImage, productTitle, effectiveProductId, updateProfilesForCurrentSession]
  );

  // ── Fast-path: use active profile directly ──
  // Triggered when the user has an active profile and clicks "Find My Best
  // Fit" on the basics step. Skips the rest of the body-profile wizard and
  // runs /sizing/recommend with whatever the profile has.
  //
  // IMPORTANT: this used to gate on `p.measurements` being populated, but
  // `recommendForProduct` works fine without it — the backend will estimate
  // any missing body parts from height/weight/age/gender/profiles via Gemini
  // text mode. The old gate caused freshly-created profiles to silently
  // absorb the click while the background `estimateFullMeasurements` race
  // was still running. Now we only require basic identity (height + weight).
  //
  // snapSubmitRef — forward-ref so handleUseActiveProfile (defined first)
  // can call handleSnapSubmit (defined later in the same render).
  const snapSubmitRef = useRef<((data: SnapSubmitData) => Promise<void>) | null>(null);

  // Profile the user is about to confirm before we fire the fast-path
  // recommendation. Non-null → ConfirmMeasurementsModal is visible.
  const [confirmProfile, setConfirmProfile] = useState<Profile | null>(null);

  /**
   * Actually run the fast-path recommend against a profile. Split out of
   * handleUseActiveProfile so the confirmation modal can call it after the
   * user confirms. Idempotent: safe to call multiple times.
   */
  const runRecommendWithProfile = useCallback(async (
    p: Profile,
    styleMatchSelections?: StyleMatchSelection[],
    requestedSizingUnit?: "cm" | "in",
  ) => {
    const nextStyleMatchSelections = (styleMatchSelections || [])
      .filter((item) => item && item.image && item.title)
      .map((item) => ({
        slot: item.slot,
        productId: item.productId,
        title: item.title,
        image: item.displayImage || item.image,
        displayImage: item.displayImage || item.image,
        url: item.url,
        color: item.color,
        garmentType: item.garmentType,
        recommendedSize: item.recommendedSize,
      }));
    styleMatchSelectionsRef.current = nextStyleMatchSelections;
    setResultStyleMatchSelections(nextStyleMatchSelections);
    const profileHeight = (p.height ?? (p as any).heightCm ?? 0) as number;
    const profileWeight = (p.weight ?? (p as any).weightKg ?? 0) as number;
    const hasStored = !!p.measurements && Object.keys(p.measurements).length > 0;

    // Use the photo path whenever the active profile has a saved photo so
    // the SDK shows the same landmark scan UI and 6-second cadence as a
    // fresh photo sizing run. Stored measurements are sent as hard overrides.
    const storedPhoto = ((p as any).photoUrl || (p as any).photoBase64) as string | undefined;
    if (storedPhoto && profileHeight > 0 && snapSubmitRef.current) {
      try {
        const dataUrl = await profilePhotoToDataUrl(storedPhoto);
        const blob = await fetch(dataUrl).then(r => r.blob());
        const file = new File([blob], "profile-photo.jpg", { type: blob.type || "image/jpeg" });
        await snapSubmitRef.current({
          photoFile: file,
          photoBase64: dataUrl,
          height: profileHeight,
          weight: profileWeight,
          heightUnit: (p.heightUnit as string) || "cm",
          weightUnit: (p.weightUnit as string) || "kg",
          sizingUnit: requestedSizingUnit || p.sizingUnit || p.measurementsUnit || ((p.heightUnit as string) === "in" || (p.heightUnit as string) === "ft" ? "in" : "cm"),
          gender: p.gender,
          age: (p as any).age,
          ...(hasStored && p.measurements ? { knownMeasurements: p.measurements as Record<string, number> } : {}),
          ...(styleMatchSelections?.length ? { styleMatchSelections } : {}),
          ...snapBraFields({
            gender: p.gender,
            bandSize: p.bandSize,
            cupSize: p.cupSize,
            braSizeRegion: (p as any).braSizeRegion,
            braRegion: (p as any).braRegion,
          }),
        });
      } catch {
        // Fall through to measurements-only path if photo conversion fails
      }
      return;
    }

    resetFreshSizingRunState();
    setSizingLoading(true);
    // If the profile already has stored measurements, mark estimation done so
    // the spinner UI doesn't show a fake "estimating..." step.
    setEstimationDone(hasStored);
    // Fast path: no photo processing → clear preview + landmarks state so the
    // SizeResultView renders the simple spinner instead of the mobile snap-scan UI.
    if (hasStored) {
      setPreviewUrl(null);
      setBodyLandmarks(null);
    }
    // Pre-populate selectedFile + previewUrl from the profile's stored photo
    // if one exists. This way, when the user clicks "Try It On" on the size
    // result page, the left-side upload area already shows their profile photo
    // instead of opening an empty photo-guide — matching the snap flow.
    const profilePhoto = ((p as any).photoUrl || (p as any).photoBase64) as string | undefined;
    if (profilePhoto) {
      try {
        profilePhotoToBlob(profilePhoto).then(blob => {
          const file = new File([blob], "profile-photo.jpg", { type: blob.type || "image/jpeg" });
          setSelectedFile(file);
          selectedFileRef.current = file;
          // Revoke any prior object URL to avoid leaks.
          if (previewUrl) URL.revokeObjectURL(previewUrl);
          setPreviewUrl(URL.createObjectURL(file));
        }).catch(() => { /* fall through: user will be asked to upload */ });
      } catch { /* ignore */ }
    }
    setView("size-result");
    const minVisible = new Promise<void>((resolve) => setTimeout(resolve, 6000));
    recommendForProduct({
      productId: effectiveProductId,
      productTitle,
      productImage,
      productCategory,
      productSubcategory,
      productFitType: resolvedProductFitType,
      productType,
      productTags: productTagsList,
      productDescription,
      sizeGuideData,
      profile: p,
      apiUrl,
      skipCache: true,
      })
      .then((res) => {
        if (res?.raw) setSizingResult(res.raw);
        setEstimationDone(true);
      })
      .catch(() => { /* non-fatal */ })
      .finally(() => {
        void minVisible.then(() => setSizingLoading(false));
      });
  }, [effectiveProductId, productTitle, productImage, productCategory, productSubcategory, resolvedProductFitType, productType, productTagsList, productDescription, sizeGuideData, apiUrl, previewUrl, resetFreshSizingRunState]);

  /**
   * Triggered by BodyProfileView's "Find My Best Fit" button. Instead of
   * running the recommend immediately, show a confirmation modal that lets
   * the user verify the unit system (Metric/Imperial) and values we're
   * about to submit. Prevents the "I typed 185 cm but it was interpreted
   * as 185 in" class of confusion.
   */
  const handleUseActiveProfile = useCallback(async (
    styleMatchSelections?: StyleMatchSelection[],
    requestedSizingUnit?: "cm" | "in",
  ) => {
    const p = profiles.find((x) => x.id === activeProfileId);
    if (!p) return;
    // Support BOTH the canonical `height/weight` and legacy `heightCm/weightKg`
    // fields. Older profiles created before the migration may only have the
    // legacy keys, and we don't want to silently absorb the click for those.
    const profileHeight = (p.height ?? (p as any).heightCm ?? 0) as number;
    const profileWeight = (p.weight ?? (p as any).weightKg ?? 0) as number;
    const hasIdentity = profileHeight > 0 && profileWeight > 0;
    if (!hasIdentity) return;

    // Skip the "Confirm your measurements" modal — the user just typed (or
    // edited) these values one screen earlier; a second confirm step is
    // friction. Run the recommendation immediately. setConfirmProfile is
    // still available for callers that want the explicit gate.
    void runRecommendWithProfile(p, styleMatchSelections, requestedSizingUnit);
  }, [profiles, activeProfileId, runRecommendWithProfile]);

  const proceedFromConfirmProfile = useCallback(() => {
    if (!confirmProfile) return;
    const p = confirmProfile;
    setConfirmProfile(null);
    void runRecommendWithProfile(p);
  }, [confirmProfile, runRecommendWithProfile]);

  const cancelFromConfirmProfile = useCallback(() => {
    setConfirmProfile(null);
    // Bump formKey to remount BodyProfileView so it resets to the basics
    // step with the existing inputs intact (BodyProfileView derives its
    // internal step state from props on mount).
    setBodyProfileInitialStep("basics");
    setFormKey((k) => k + 1);
    setView("body-profile");
  }, []);

  // Store applyProfile in a ref so handleOpen can use it without circular dependency
  const applyProfileRef = useRef<(id: string) => void>(() => {});
  const handleOpen = useCallback(() => {
    console.log("[ps-sdk] handleOpen fired — opening modal");
    styleMatchSelectionsRef.current = [];
    setResultStyleMatchSelections([]);
    reportSdkEvent("SDK_OPENED", { metadata: { view: "body-profile" } });
    setBodyProfileInitialStep(null);
    setView("body-profile");
    // No auto-apply of any profile values into the form. Selecting a
    // profile must NOT change the height/weight/age inputs. The basics
    // step's "Find My Best Fit" button uses profile.measurements
    // directly via handleUseActiveProfile, so the inputs are purely
    // for the manual fill-the-form flow.
    onOpen?.();
  }, [onOpen, reportSdkEvent]);

  const handleClose = useCallback(() => {
    // If a try-on is in flight when the user closes the modal, KEEP the
    // SSE / polling listener alive in the background so the in-flight
    // result still lands in history. The save-on-result effect (further
    // down) writes the result image into the most-recent history entry
    // + IDB blob even when view is "idle".
    const tryOnInFlight = tryOnProcessing;
    setView("idle"); setSelectedFile(null); setDrawer(null); setProfileDetail(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null); setErrorMessage(null);
    setSizingMethod(null); setSizingLoading(false); setEstimatedValues(null); setEstimationLoading(false);
    setUsedAiPhotoSizing(false);
    setProfileSaved(false); formRef.current = {}; setFormGender("male");
    setRestoredProductImage(null);
    setRestoredProductImages(null);
    setRestoredProductCarouselItems(null);
    setRestoredProductTitle(null);
    setRestoredProductUrl(null);
    setRestoredHistoryEntryId(null);
    setRestoredHistoryMatchesCurrentProduct(false);
    if (!tryOnInFlight) {
      // Normal close — reset everything safe to drop.
      setResultImageUrl(null);
      setSizingResult(null);
      historySavedRef.current = false;
      historyTryonSavedRef.current = false;
      currentHistoryEntryIdRef.current = null;
      currentTryOnJobIdRef.current = null;
      unsubRef.current?.(); unsubRef.current = null;
      if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
    }
    // Try-on still running: keep resultImageUrl, sizingResult,
    // historySavedRef, the SSE unsub handle, and the polling interval
    // intact so handleVtoUpdate can still run + save-on-result effect
    // can patch the history entry when the result lands.
    onClose?.();
  }, [onClose, previewUrl, tryOnProcessing]);

  const handleBack = useCallback(() => {
    // If a drawer (profiles/history) is open, close it and restore prev view
    if (drawer) {
      setDrawer(null);
      if (prevViewRef.current) {
        setView(prevViewRef.current);
        prevViewRef.current = null;
      }
      return;
    }
    switch (view) {
      case "body-profile": handleClose(); break;
      case "estimation-review": setView("body-profile"); break;

      case "size-result":
        // If in a section detail, go back to card picker first
        if (activeSection) { setActiveSection(null); break; }
        setView("body-profile"); break;
      case "upload": setView("size-result"); break;
      case "error": setView("body-profile"); break;
      case "no-chart": setView("body-profile"); break;
      case "profiles":
        // Fix 5: If profiles sub-screen has an internal step, go back within it
        if (profilesCanGoBackRef.current()) {
          profilesGoBackRef.current();
          break;
        }
        // Otherwise go back to the view we were on before opening profiles
        setView(prevViewRef.current || "body-profile");
        prevViewRef.current = null;
        break;

      default: handleClose(); break;
    }
  }, [view, drawer, activeSection, handleClose]);

  const showBackButton = view !== "idle" && view !== "processing" && view !== "result" && view !== "welcome" && view !== "body-profile" && view !== "size-result";

  const handleFileSelect = useCallback((file: File) => {
    if (!isValidImageFile(file)) {
      setErrorMessage(t("Please upload a JPEG, PNG, or WebP image."));
      setView("error");
      reportSdkClientError({
        message: "Invalid image file type",
        code: "INVALID_FILE",
        component: "PhotoUpload",
        view,
        severity: "low",
        metadata: { fileType: file.type, fileSize: file.size },
      });
      onError?.({ message: "Invalid file type", code: "INVALID_FILE" });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setErrorMessage(t("Image must be under 10MB."));
      setView("error");
      reportSdkClientError({
        message: "Image file too large",
        code: "FILE_TOO_LARGE",
        component: "PhotoUpload",
        view,
        severity: "low",
        metadata: { fileType: file.type, fileSize: file.size },
      });
      onError?.({ message: "File too large", code: "FILE_TOO_LARGE" });
      return;
    }
    setSelectedFile(file);
    selectedFileRef.current = file;
    modelImageIdRef.current = null; // new photo invalidates the backend-cached id
    const objUrl = URL.createObjectURL(file);
    setPreviewUrl(objUrl);
    reportSdkEvent("PHOTO_UPLOADED", {
      metadata: { fileType: file.type, fileSize: file.size, view },
    });
    onUpload?.(file);
    // Pre-compute pose landmarks on the model photo (background, non-blocking)
    modelPoseRef.current = null;
    detectMeasurementLines(objUrl)
      .then((lines) => { modelPoseRef.current = lines; })
      .catch((error) => {
        reportSdkClientError({
          message: error instanceof Error ? error.message : "Pose detection failed",
          code: "POSE_DETECTION_FAILED",
          component: "MediaPipe",
          view,
          severity: "low",
        });
      });
  }, [onUpload, onError, reportSdkClientError, reportSdkEvent, t, view]);

  const handleRemovePreview = useCallback(() => {
    setSelectedFile(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  }, [previewUrl]);

  const cleanupJob = useCallback(() => {
    if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
    unsubRef.current?.(); unsubRef.current = null;
  }, []);

  const handleVtoUpdate = useCallback((update: VtoUpdate) => {
    const completedImageUrl = update.imageUrl || update.resultImageUrl || update.outputUrl || update.remoteOutputUrl || null;
    // Sizing already short-circuited with "no fit" — discard whatever the
    // try-on returns. We still consume the SSE event so cleanupJob runs.
    if (noFitFoundRef.current) {
      if (update.status === "completed" || update.status === "failed") {
        completedRef.current = true;
        cleanupJob();
        setTryOnProcessing(false); setTryOnStartedAt(null);
      }
      return;
    }
    if (update.status === "completed" && completedImageUrl) {
      setErrorMessage(null);
      const isTransientCompletionImage = isTransientResultImageUrl(completedImageUrl);
      if (isTransientCompletionImage) {
        transientTryOnResultUrlRef.current = completedImageUrl;
      }
      currentTryOnJobIdRef.current = update.galleryId;
      if (shouldUseShopifyCartAttribution(apiUrl)) {
        setLastCompletedProduct(effectiveProductId ?? null, productTitle || null);
      }
      setResultImageUrl(prev => {
        // Do not swap the visible result from the immediate data URL to the
        // later CDN URL. That remounts the image and looks like the result
        // disappeared/reloaded. Keep the displayed pixels stable; use the
        // durable URL only for callbacks/history below.
        const next = prev?.startsWith("data:") && !completedImageUrl.startsWith("data:")
          ? prev
          : (!prev || prev.startsWith("data:") || !completedImageUrl.startsWith("data:"))
            ? completedImageUrl
            : prev;
        resultImageUrlRef.current = next;
        return next;
      });
      if (!completedRef.current) {
        progressRef.current = 100;
        if (progressBarRef.current) progressBarRef.current.style.width = "100%";
        if (progressTextRef.current) progressTextRef.current.textContent = "100%";
        if (progressRingRef.current) progressRingRef.current.style.strokeDashoffset = "0";
        if (progressEtaRef.current) progressEtaRef.current.textContent = t("Done");
        if (progressStatusRef.current) {
          const el = progressStatusRef.current;
          el.style.opacity = "0";
          setTimeout(() => { el.textContent = t("Complete!"); el.style.opacity = "1"; }, 180);
        }
        setTryOnProcessing(false); setTryOnStartedAt(null);
        if (isTransientCompletionImage) {
          // SSE/status can deliver a base64 data URL before the backend
          // finishes uploading the durable Cloudinary URL. Show it immediately
          // and keep polling only for durable callbacks/history.
          return;
        }
        completedRef.current = true;
        cleanupJob();
        const completedSizingResult = sizingResultRef.current;
        if (completedSizingResult?.recommendedSize) {
          setCurrentRunSavedFitEntry({
            id: currentHistoryEntryIdRef.current || `current_${update.galleryId}`,
            productId: effectiveProductId,
            productTitle,
            productUrl: effectiveProductUrl,
            jobId: update.galleryId,
            productImage,
            productImages: effectiveProductImages?.slice(0, 12),
            productCarouselItems: effectiveProductCarouselItems?.slice(0, 12),
            resultImageUrl: completedImageUrl,
            recommendedSize: completedSizingResult.recommendedSize,
            confidence: completedSizingResult.confidence,
            reasoning: completedSizingResult.reasoning,
            internationalSizes: completedSizingResult.internationalSizes,
            matchDetails: completedSizingResult.matchDetails,
            sizingResult: completedSizingResult,
            date: Date.now(),
            hasResult: true,
          });
        }
        dispatchTryOnCompleteEvent({
          jobId: update.galleryId,
          historyEntryId: currentHistoryEntryIdRef.current,
          productId: effectiveProductId,
          productTitle,
          recommendedSize: sizingResultRef.current?.recommendedSize,
          resultImageUrl: completedImageUrl,
        });
        // Stay on size-result view — no navigation needed
        onComplete?.({ jobId: update.galleryId, imageUrl: completedImageUrl });
      } else if (!isTransientCompletionImage) {
        completedRef.current = true;
        cleanupJob();
      }
    } else if (update.status === "failed") {
      if (!completedRef.current) {
        completedRef.current = true;
        cleanupJob();
        setTryOnProcessing(false); setTryOnStartedAt(null);
        const msg = update.error || t("Try-on generation failed");
        reportSdkClientError({
          message: msg,
          code: "TRYON_GENERATION_FAILED",
          component: "TryOn",
          view,
          severity: "medium",
          jobId: update.galleryId,
          metadata: { status: update.status },
        });
        setErrorMessage(msg);
        if (!sizingResultRef.current?.recommendedSize) setView("error");
        onError?.({ message: msg });
      }
    }
  }, [apiUrl, effectiveProductCarouselItems, effectiveProductId, effectiveProductImages, effectiveProductUrl, productImage, productTitle, onComplete, onError, cleanupJob, reportSdkClientError, t, view]);

  // ── Dynamic fields from size guide (or fallback) ──
  const dynamicFields = useMemo((): SizeGuideField[] => {
    if (sizeGuide?.found && sizeGuide.requiredFields && sizeGuide.requiredFields.length > 0) {
      return sizeGuide.requiredFields;
    }
    return formGender === "female" ? FALLBACK_FIELDS_FEMALE : FALLBACK_FIELDS_MALE;
  }, [sizeGuide, formGender]);

  // ── Sizing submit ──
  const submitSizing = useCallback(async (methodOverride?: "exact" | "quick") => {
    if (!apiRef.current) return;
    setActiveSection(null);
    noFitFoundRef.current = false;
    const method = methodOverride || sizingMethod;
    const baseUrl = getApiUrl(apiUrl);
    const key = getApiKey();
    reportSdkEvent("SIZING_STARTED", {
      metadata: {
        method,
        measurementType,
        productFitType: resolvedProductFitType,
        hasSizeGuide: !!sizeGuide?.found,
      },
    });

    // ── Face / head manual-entry path ──
    // Eyewear + headwear route to the isolated /sizing/face-recommend endpoint.
    // formRef holds the values typed into AccessorySizeView (mm for eyewear,
    // cm for headwear — whatever unit the AccessoryField was set to).
    if (measurementType === "face" || measurementType === "head") {
      const f = formRef.current;
      const toNum = (v: string | undefined): number | undefined => {
        if (!v) return undefined;
        const n = parseFloat(v);
        return Number.isFinite(n) && n > 0 ? n : undefined;
      };
      // Unit the user picked in the form (eyewear: mm or in; headwear: cm or in).
      const inputUnit = (f.sizingUnit || sizingUnit || (measurementType === "head" ? "cm" : "mm")) as "mm" | "cm" | "in";
      const toMm = (n?: number): number | undefined => {
        if (n == null) return undefined;
        if (inputUnit === "mm") return n;
        if (inputUnit === "cm") return n * 10;
        if (inputUnit === "in") return n * 25.4;
        return n;
      };
      const faceMm: Record<string, number> = {};
      const src: Record<string, number | undefined> = {
        bridgeWidth: toMm(toNum(f.bridgeWidth)),
        templeLength: toMm(toNum(f.templeLength) ?? toNum(f.armLength)),
        lensWidth: toMm(toNum(f.lensWidth)),
        lensHeight: toMm(toNum(f.lensHeight)),
        faceWidth: toMm(toNum(f.faceWidth) ?? toNum(f.frameWidth)),
        pd: toMm(toNum(f.pd)),
        // Headwear — circumference usually typed in cm; keep as mm internally.
        headCircumference: toMm(toNum(f.headCircumference)),
        headWidth: toMm(toNum(f.headWidth)),
      };
      for (const [k, v] of Object.entries(src)) if (typeof v === "number") faceMm[k] = v;

      const facePayload: Record<string, unknown> = {
        sessionId: getOrCreateSessionId(),
        product: productContext,
        sizeGuide: sizeGuide?.found ? sizeGuide : { found: false },
        sizingUnit: measurementType === "head" ? "cm" : "mm",
        category: measurementType,
        ...(Object.keys(faceMm).length > 0 && { faceMeasurementsMm: faceMm, irisConfidence: 1 }),
      };
      const bodyContext = {
        height: toNum(f.height),
        weight: toNum(f.weight),
        heightUnit: f.heightUnit as "cm" | "in" | "ft" | undefined,
        weightUnit: f.weightUnit as "kg" | "lbs" | undefined,
        gender: f.gender,
        age: toNum(f.age),
      };
      if (bodyContext.height || bodyContext.weight || bodyContext.gender || bodyContext.age) {
        facePayload.bodyContext = bodyContext;
      }

      // Hold the cycling stage UI on screen for a minimum beat even when the
      // backend returns near-instantly. Hats use the full apparel cadence so
      // uniform scrub caps do not flash past the scan states.
      const minVisible = new Promise<void>((r) => setTimeout(r, getFaceHeadScanMinVisibleMs(measurementType)));

      try {
        const resp = await fetch(`${baseUrl}/api/v1/sizing/face-recommend`, {
          method: "POST",
          headers: jsonHeaders(key),
          body: JSON.stringify(facePayload),
        });
        if (resp.ok) {
          const data = await resp.json();
          await minVisible;
          if (data?.found === false) {
            reportSdkEvent("SIZING_FAILED", {
              metadata: {
                reason: data?.reasoning || "NO_MATCH",
                measurementType,
                found: false,
              },
            });
            setNoSizeReason(data?.reasoning === "NO_SIZE_CHART" ? "no-chart" : "no-match");
            setView("no-chart");
            setEstimationDone(true);
            return;
          }
          setSizingResult(data);
          reportSdkEvent("SIZE_RECOMMENDATION_SHOWN", {
            recommendedSize: data?.recommendedSize,
            metadata: {
              confidence: data?.confidence,
              measurementType,
              source: "face-recommend",
            },
          });
          onComplete?.(data);
        } else {
          const body = await resp.text().catch(() => "");
          console.error("[PS-SDK] face-recommend failed:", resp.status, body);
          reportSdkEvent("SIZING_FAILED", {
            metadata: { status: resp.status, measurementType, source: "face-recommend" },
          });
          reportSdkClientError({
            message: body || "Face/head sizing request failed",
            code: "FACE_RECOMMEND_FAILED",
            component: "Sizing",
            view,
            severity: "medium",
            metadata: { status: resp.status, measurementType },
          });
          await minVisible;
          setErrorMessage(t("Unable to get a size suggestion. Please try again."));
          setView("error");
          setEstimationDone(true);
        }
      } catch (err) {
        console.error("[PS-SDK] face-recommend network error:", err);
        reportSdkEvent("SIZING_FAILED", {
          metadata: { measurementType, source: "face-recommend", network: true },
        });
        reportSdkClientError({
          message: err instanceof Error ? err.message : "Face/head sizing network error",
          code: "FACE_RECOMMEND_NETWORK_ERROR",
          stack: err instanceof Error ? err.stack : undefined,
          component: "Sizing",
          view,
          severity: "medium",
          metadata: { measurementType },
        });
        await minVisible;
        setErrorMessage(t("Unable to connect to sizing service. Please try again."));
        setView("error");
        setEstimationDone(true);
      } finally {
        setSizingLoading(false);
      }
      return;
    }

    const payload: Record<string, unknown> = {
      sessionId: getOrCreateSessionId(),
      method,
      locale: sizingCountry,
      product: productContext,
    };
    if (sizeGuide?.found) {
      payload.sizeGuide = sizeGuide;
      if (sizeGuide.sections) payload.sizeGuide = { ...sizeGuide, sections: sizeGuide.sections };
    }
    payload.sizingUnit = sizingUnit;
    // Debug: log what we send
    console.log("[PS-SDK] sizeGuide state:", JSON.stringify({ found: sizeGuide?.found, hasHeaders: !!sizeGuide?.headers, hasRows: !!sizeGuide?.rows, headers: sizeGuide?.headers, rowCount: sizeGuide?.rows?.length, hasSections: !!sizeGuide?.sections, sectionNames: sizeGuide?.sections ? Object.keys(sizeGuide.sections) : [] }));
    console.log("[PS-SDK] payload.sizeGuide:", payload.sizeGuide ? "present" : "MISSING");
    console.log("[PS-SDK] payload.sizeGuide row0 type:", payload.sizeGuide && (payload.sizeGuide as any).rows?.[0] ? (Array.isArray((payload.sizeGuide as any).rows[0]) ? "array" : typeof (payload.sizeGuide as any).rows[0]) : "none");
    console.log("[PS-SDK] payload:", JSON.stringify(payload).substring(0, 1000));
    console.log("[PS-SDK] ── submitSizing START ──");
    console.log("[PS-SDK] method:", method, "| sizingUnit:", sizingUnit, "| heightUnit:", heightUnit);
    console.log("[PS-SDK] formRef.current (all keys):", JSON.stringify(formRef.current));
    console.log("[PS-SDK] dynamicFields:", dynamicFields.map(f => `${f.key}(unit=${f.unit},req=${f.required})`).join(", "));

    if (method === "exact") {
      const m: Record<string, unknown> = { gender: formRef.current.gender || "male", sizingUnit };
      const numericKeys = new Set<string>();
      for (const f of dynamicFields) {
        if (f.unit !== "size" && !["shoeEU", "shoeUS", "shoeUK"].includes(f.key)) numericKeys.add(f.key);
      }
      for (const k of ["chest", "bust", "waist", "hips", "shoulderWidth", "sleeveLength", "inseam", "neckCircumference", "footLengthCm", "height", "length", "thighCircumference", "wristCircumference", "handCircumference", "headCircumferenceCm", "faceWidthMm", "bridgeWidthMm", "templeLengthMm"]) {
        if (formRef.current[k]) numericKeys.add(k);
      }
      console.log("[PS-SDK] numericKeys:", [...numericKeys].join(", "));

      // Values in formRef are in the user's display unit (cm or in).
      // EstimationReviewView.handleSubmit converts to cm before calling this.
      // Pass through as-is — no conversion here.
      for (const k of numericKeys) {
        const raw = formRef.current[k];
        if (!raw) continue;
        const parsed = parseFloat(raw);
        if (isNaN(parsed)) continue;
        m[k] = parsed;
      }
      if (formRef.current.shoeEU) m.shoeEU = formRef.current.shoeEU;
      if (formRef.current.shoeUS) m.shoeUS = formRef.current.shoeUS;
      if (formRef.current.shoeUK) m.shoeUK = formRef.current.shoeUK;
      if (formRef.current.fitPreference) m.fitPreference = formRef.current.fitPreference;
      if (formRef.current.bodyType) m.bodyType = formRef.current.bodyType;
      if (formRef.current.chestProfile) m.chestProfile = formRef.current.chestProfile;
      if (formRef.current.midsectionProfile) m.midsectionProfile = formRef.current.midsectionProfile;
      if (formRef.current.hipProfile) m.hipProfile = formRef.current.hipProfile;
      payload.measurements = m;
      console.log("[PS-SDK] FINAL measurements:", JSON.stringify(m));
    } else {
      // Quick path — send raw values + unit info, backend converts
      const qHeight = parseFloat(formRef.current.height || "0");
      const qWeight = parseFloat(formRef.current.weight || "0");
      if (!qHeight || !qWeight) {
        console.error("[PS-SDK] submitSizing ABORT — qHeight:", qHeight, "qWeight:", qWeight, "formRef:", JSON.stringify(formRef.current));
        reportSdkEvent("SIZING_FAILED", {
          metadata: { reason: "missing_height_or_weight", method, measurementType },
        });
        reportSdkClientError({
          message: "Sizing submitted without height or weight",
          code: "SIZING_INPUT_MISSING",
          component: "Sizing",
          view,
          severity: "low",
          metadata: { method, measurementType, qHeight, qWeight },
        });
        setSizingLoading(false);
        return;
      }
      const qe: Record<string, unknown> = {
        height: qHeight,
        weight: qWeight,
        heightUnit: formRef.current.heightUnit || heightUnit || "cm",
        weightUnit: formRef.current.weightUnit || weightUnit || "kg",
        gender: formRef.current.gender || "male",
      };
      if (formRef.current.age) qe.age = parseInt(formRef.current.age, 10);
      if (formRef.current.bodyType) qe.bodyType = formRef.current.bodyType;
      if (formRef.current.chestProfile) qe.chestProfile = formRef.current.chestProfile;
      if (formRef.current.midsectionProfile) qe.midsectionProfile = formRef.current.midsectionProfile;
      if (formRef.current.hipProfile) qe.hipProfile = formRef.current.hipProfile;
      payload.quickEstimate = qe;
    }
    setEstimationDone(false);
    try {
      const tReq = Date.now();
      const payloadBytes = JSON.stringify(payload).length;
      console.log(`[ps-sdk:T] ▶ POST /sizing/recommend (quick) payload=${Math.round(payloadBytes / 1024)}KB bodyImage=${!!(payload as any).bodyImage}`);
      const res = await fetch(`${baseUrl}/api/v1/sizing/recommend`, {
        method: "POST",
        headers: jsonHeaders(key),
        body: JSON.stringify(payload),
      });
      console.log(`[ps-sdk:T] ◀ /sizing/recommend (quick) status=${res.status} roundTrip=${Date.now() - tReq}ms`);
      if (res.ok) {
        const data = await res.json();
        console.log("[PS-SDK] Sizing recommend RESULT:", JSON.stringify(data));
        // Backend says no size chart available for this product → graceful
        // empty state instead of treating it as an error. User can skip
        // straight to try-on or continue shopping.
        if (data?.found === false) {
          reportSdkEvent("SIZING_FAILED", {
            metadata: {
              reason: data?.reasoning || "NO_MATCH",
              method,
              measurementType,
              found: false,
            },
          });
          setNoSizeReason(data?.reasoning === "NO_SIZE_CHART" ? "no-chart" : "no-match");
          setView("no-chart");
          setEstimationDone(true);
          return;
        }
        setSizingResult(data);
        reportSdkEvent("SIZE_RECOMMENDATION_SHOWN", {
          recommendedSize: data?.recommendedSize,
          metadata: {
            confidence: data?.confidence,
            method,
            measurementType,
            found: data?.found,
          },
        });
        onComplete?.(data);
        // Persist measurements + history to the active profile (auto-creates if none).
        // Runs estimateFullMeasurements internally as a parallel call so the profile
        // gets full body measurements for ANY future product, and flips estimationDone.
        const m = (payload.measurements as Record<string, unknown>) || {};
        const qe = (payload.quickEstimate as Record<string, unknown>) || {};
        const src = method === "exact" ? m : qe;
        persistResultToProfile(
          {
            gender: (src.gender as "male" | "female") || "male",
            height: Number(src.height || src.heightCm || 0),
            weight: Number(src.weight || src.weightKg || 0),
            heightUnit: (src.heightUnit as "cm" | "in" | "ft") || "cm",
            weightUnit: (src.weightUnit as "kg" | "lbs") || "kg",
            age: src.age != null ? Number(src.age) : undefined,
            chestProfile: src.chestProfile as import("./types").ChestProfile | undefined,
            midsectionProfile: src.midsectionProfile as import("./types").MidsectionProfile | undefined,
            hipProfile: src.hipProfile as import("./types").HipProfile | undefined,
            ...(snapBraFields({
              gender: src.gender as string | undefined,
              bandSize: formRef.current.bandSize,
              cupSize: formRef.current.cupSize,
              braSizeRegion: formRef.current.braSizeRegion,
            })),
          },
          data,
        );
      } else {
        const errBody = await res.text().catch(() => "");
        console.error("[PS-SDK] Sizing recommend failed:", res.status, errBody);
        reportSdkEvent("SIZING_FAILED", {
          metadata: { status: res.status, method, measurementType, source: "sizing-recommend" },
        });
        reportSdkClientError({
          message: errBody || "Sizing request failed",
          code: "SIZING_RECOMMEND_FAILED",
          component: "Sizing",
          view,
          severity: "medium",
          metadata: { status: res.status, method, measurementType },
        });
        setErrorMessage(t("Unable to get a size suggestion. Please try again."));
        setView("error");
        setEstimationDone(true);
      }
    } catch (err) {
      console.error("[PS-SDK] Sizing recommend network error:", err);
      reportSdkEvent("SIZING_FAILED", {
        metadata: { method, measurementType, source: "sizing-recommend", network: true },
      });
      reportSdkClientError({
        message: err instanceof Error ? err.message : "Sizing network error",
        code: "SIZING_RECOMMEND_NETWORK_ERROR",
        stack: err instanceof Error ? err.stack : undefined,
        component: "Sizing",
        view,
        severity: "medium",
        metadata: { method, measurementType },
      });
      setErrorMessage(t("Unable to connect to sizing service. Please try again."));
      setView("error");
      setEstimationDone(true);
    } finally {
      setSizingLoading(false);
    }
  }, [apiUrl, sizingMethod, sizingCountry, heightUnit, weightUnit, sizingUnit, sizeGuide, productContext, measurementType, resolvedProductFitType, dynamicFields, persistResultToProfile, reportSdkClientError, reportSdkEvent, t, view]);

  // ── Quick estimate → estimation review ──
  const handleQuickEstimate = useCallback(async (
    height: number, weight: number, heightUnit: string, weightUnit: string, gender: string,
    age?: number, bodyType?: string,
    chestProfile?: ChestProfile, midsectionProfile?: MidsectionProfile, hipProfile?: HipProfile,
    bodyImage?: string,
  ) => {
    if (!apiRef.current) {
      // Silently bailing here historically presented as "Find My Size does
      // nothing" on mobile when the API key wasn't picked up at mount time.
      // Surface a clear error so the user (and host developer) sees the
      // actual failure instead of a dead button.
      const msg = t("SDK not configured. Please refresh and try again.");
      console.warn("[ps-sdk] handleQuickEstimate BAILED — apiRef is null. API key not loaded.");
      setErrorMessage(msg);
      setView("error");
      onError?.({ message: msg, code: !apiRef.current ? "SDK_NOT_CONFIGURED" : "PHOTO_MISSING" });
      return;
    }
    const baseUrl = getApiUrl(apiUrl);
    const key = getApiKey();

    // Only request body measurement fields (not shoe sizes, not weight/height — those are inputs)
    const SKIP_ESTIMATE_KEYS = new Set(["weight", "weightKg", "height", "heightCm"]);

    // Collect required fields from ALL sections for multi-garment products (tuxedo, suit, etc.)
    const allFieldKeys = new Set<string>();
    if (sizeGuide?.sections) {
      for (const sec of Object.values(sizeGuide.sections)) {
        if (sec.requiredFields) {
          for (const f of sec.requiredFields) {
            if (f.required && f.unit === "cm" && !SKIP_ESTIMATE_KEYS.has(f.key)) {
              allFieldKeys.add(f.key);
            }
          }
        }
      }
    }
    // Also include top-level dynamicFields
    for (const f of dynamicFields) {
      if (f.required && f.unit === "cm" && !SKIP_ESTIMATE_KEYS.has(f.key)) {
        allFieldKeys.add(f.key);
      }
    }
    const requiredFieldKeys = Array.from(allFieldKeys);

    console.log("[PS-SDK] Quick estimate — dynamicFields:", dynamicFields.map(f => `${f.key}(${f.unit},req=${f.required})`));
    console.log("[PS-SDK] Quick estimate — requiredFieldKeys:", requiredFieldKeys);
    console.log("[PS-SDK] Quick estimate — profiles:", { chestProfile, midsectionProfile, hipProfile });

    if (requiredFieldKeys.length === 0) {
      // No body measurement fields — fall back to original quick flow
      console.warn("[PS-SDK] No body measurement fields found — skipping estimation review");
      // The user abandoned any photo they may have uploaded and is now on
      // the manual/quick path — clear the photo-mode state so the size-result
      // view falls back to the product image with no MediaPipe scan overlay.
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setSelectedFile(null);
      selectedFileRef.current = null;
      setPreviewUrl(null);
      setBodyLandmarks(null);
      setFaceLandmarks(null);
      setSizingMethod("quick");
      resetFreshSizingRunState();
      setSizingLoading(true);
      setView("size-result");
      submitSizing("quick");
      return;
    }

    // Store raw values — no conversion, backend handles unit math
    formRef.current.height = String(height);
    formRef.current.weight = String(weight);
    formRef.current.heightUnit = heightUnit;
    formRef.current.weightUnit = weightUnit;
    formRef.current.gender = gender;
    if (age) formRef.current.age = String(age);
    if (bodyType) formRef.current.bodyType = bodyType;
    if (chestProfile) formRef.current.chestProfile = chestProfile;
    if (midsectionProfile) formRef.current.midsectionProfile = midsectionProfile;
    if (hipProfile) formRef.current.hipProfile = hipProfile;

    resetFreshSizingRunState();
    // Abandoning the photo path — drop the uploaded photo + any pose/face
    // landmarks so the size-result view doesn't render the "scanning the
    // uploaded photo" UI (MobileScanningView reads `previewUrl` + landmarks
    // to decide between product-image and photo-mode).
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setSelectedFile(null);
    selectedFileRef.current = null;
    setPreviewUrl(null);
    setBodyLandmarks(null);
    setFaceLandmarks(null);
    setSizingMethod("quick");
    setSizingLoading(true);
    setView("size-result");
    submitSizing("quick");
  }, [apiUrl, dynamicFields, sizeGuide, submitSizing, sizingUnit, weightUnit, previewUrl, resetFreshSizingRunState]);

  // ── Snap path: parallel try-on + sizing in one shot ──
  const handleSnapSubmit = useCallback(async (data: SnapSubmitData) => {
    console.log("[ps-sdk] handleSnapSubmit fired", {
      hasApiRef: !!apiRef.current,
      hasSseRef: !!sseRef.current,
      apiUrl,
    });
    if (!apiRef.current) {
      // Surface the error to the UI instead of dead-ending on the photo step.
      // Previously this silently returned, which read as "Analyze My Size
      // button does nothing" on mobile — no error, no indication of why.
      const msg = t("SDK not configured. Please refresh and try again.");
      console.warn("[ps-sdk] handleSnapSubmit BAILED — apiRef is null. Check api init.");
      setErrorMessage(msg);
      setView("error");
      onError?.({ message: msg, code: "SDK_NOT_CONFIGURED" });
      return;
    }
    const baseUrl = getApiUrl(apiUrl);
    const key = getApiKey();
    console.log("[ps-sdk] handleSnapSubmit calling backend", { baseUrl });
    const nextStyleMatchSelections = (data.styleMatchSelections || [])
      .filter((item) => item && item.image && item.title)
      .map((item) => ({
        slot: item.slot,
        productId: item.productId,
        title: item.title,
        image: item.displayImage || item.image,
        displayImage: item.displayImage || item.image,
        url: item.url,
        color: item.color,
        garmentType: item.garmentType,
        recommendedSize: item.recommendedSize,
      }));
    styleMatchSelectionsRef.current = nextStyleMatchSelections;
    setResultStyleMatchSelections(nextStyleMatchSelections);
    if (nextStyleMatchSelections.length) {
      console.log("[ps-sdk:style-match] selected garments for try-on", nextStyleMatchSelections.map((item) => ({
        slot: item.slot,
        title: item.title,
        imageSet: Boolean(item.image),
      })));
    }

    // Store file for try-on (null when called from a stored profile base64)
    setSelectedFile(data.photoFile);
    selectedFileRef.current = data.photoFile;
    const normalizedPhotoSource = normalizeProfilePhotoSource(data.photoBase64);
    const objUrl = data.photoFile
      ? URL.createObjectURL(data.photoFile)
      : normalizedPhotoSource;
    if (!objUrl) {
      const msg = t("Profile photo is missing. Please add a photo and try again.");
      setErrorMessage(msg);
      setView("error");
      onError?.({ message: msg, code: "PROFILE_PHOTO_MISSING" });
      return;
    }
    setPreviewUrl(objUrl);

    // Mirror the snap-form values into formRef so handleTryOnSubmit can
    // surface height/weight in the silhouette context. The manual-quick
    // path already writes these (line ~1224); snap was missing them and
    // the prompt's "User's height/weight" line came out blank.
    formRef.current.height = String(data.height);
    formRef.current.weight = String(data.weight);
    formRef.current.heightUnit = data.heightUnit;
    formRef.current.weightUnit = data.weightUnit;
    formRef.current.gender = data.gender;
    if (data.age != null) formRef.current.age = String(data.age);
    formRef.current.bodyImage = data.photoBase64;
    formRef.current.photoBase64 = data.photoBase64;
    const braFields = snapBraFields(data);
    if (braFields.bandSize) formRef.current.bandSize = braFields.bandSize;
    if (braFields.cupSize) formRef.current.cupSize = braFields.cupSize;
    if (braFields.braSizeRegion) formRef.current.braSizeRegion = braFields.braSizeRegion;
    if (data.extraMeasurements) {
      for (const [key, value] of Object.entries(data.extraMeasurements)) {
        formRef.current[key] = String(value);
      }
    }

    resetFreshSizingRunState();
    setSizingLoading(true);
    setEstimationDone(false);
    setView("size-result");

    if (measurementType === "body-basic") {
      const minVisible = new Promise<void>((resolve) => setTimeout(resolve, 6000));
      const guideOnlyResult = buildAccessoryGuideOnlySizingResult(sizeGuideRef.current || sizeGuide);
      noFitFoundRef.current = false;
      setSizingResult(guideOnlyResult);
      setUsedAiPhotoSizing(true);
      await minVisible;
      setEstimationDone(true);
      setSizingLoading(false);
      return;
    }

    // ── Face / head path: FaceLandmarker + optional body landmarks → /sizing/face-recommend ──
    // Close-up photos use the face mesh. Full-body photos use pose landmarks
    // for the visible scan overlay and for the local head crop used in sizing.
    if (measurementType === "face" || measurementType === "head") {
      setFaceLandmarks(null);
      setBodyLandmarks(null);
      // Enforce a minimum visible duration for the "Detecting face/head"
      // stages — detection + backend usually resolve in well under 2s,
      // which feels like a flash. Hats use the full apparel cadence so
      // uniform scrub caps stay in the scan state for the expected 6s.
      const minVisible = new Promise<void>((r) => setTimeout(r, getFaceHeadScanMinVisibleMs(measurementType)));
      let landmarksVisibleMin: Promise<void> | null = null;
      const bodyLandmarksPromise: Promise<BodyLandmarks | null> = detectBodyLandmarks(objUrl)
        .then((landmarks) => {
          if (landmarks) setCachedMediaPipe(landmarks);
          return landmarks;
        })
        .catch(() => null);
      const waitForBodyLandmarks = (timeoutMs: number): Promise<BodyLandmarks | null> =>
        Promise.race([
          bodyLandmarksPromise,
          new Promise<null>((resolve) => setTimeout(() => resolve(null), timeoutMs)),
        ]);
		      try {
			        let sizingBodyImage = data.photoBase64;
			        let usedHeadCropForSizing = false;
          const faceResultPromise = detectFaceMeasurements(objUrl);
          let bodyLandmarks = await waitForBodyLandmarks(3500);
          let likelyFullBodyPhoto = isLikelyFullBodyLandmarks(bodyLandmarks);
			        let faceResult = await faceResultPromise;
			        if (!faceResult || faceResult.irisConfidence < 0.75 || likelyFullBodyPhoto) {
            if (!bodyLandmarks) {
              bodyLandmarks = await waitForBodyLandmarks(8000);
              likelyFullBodyPhoto = isLikelyFullBodyLandmarks(bodyLandmarks);
            }
			          const croppedHead = await cropHeadForSizing(objUrl, bodyLandmarks);
			          if (croppedHead) {
			            const croppedFaceResult = await detectFaceMeasurements(croppedHead);
			            if (croppedFaceResult) {
			              faceResult = croppedFaceResult;
			              sizingBodyImage = croppedHead;
		              usedHeadCropForSizing = true;
		            }
		          }
		        }
	        const fallbackFaceMeasurementsMm = {
	          irisDiameter: 11.7,
	          pd: data.gender === "female" ? 61 : 64,
	          bridgeWidth: data.gender === "female" ? 17 : 19,
	          faceWidth: data.gender === "female" ? 135 : 145,
	          templeLengthLeft: data.gender === "female" ? 140 : 145,
	          templeLengthRight: data.gender === "female" ? 140 : 145,
	          templeLength: data.gender === "female" ? 140 : 145,
	          headWidth: data.gender === "female" ? 150 : 158,
	          headDepth: data.gender === "female" ? 185 : 195,
	          headCircumference: data.gender === "female" ? 560 : 580,
	        };
	        // Full-body photos can still fail MediaPipe face detection even
	        // after local head crop. Do not block try-on for hats/eyewear:
	        // use conservative fallback measurements for sizing only and keep
	        // the original full-body photo as the try-on model image.
	        if (!faceResult) {
	          console.warn("[ps-sdk] face/head detection failed after crop — continuing with fallback sizing measurements");
	        }
          const useBodyLandmarksForScan = !!bodyLandmarks && (likelyFullBodyPhoto || usedHeadCropForSizing);
          if (useBodyLandmarksForScan) {
            setBodyLandmarks(bodyLandmarks);
            setFaceLandmarks(null);
            landmarksVisibleMin = new Promise<void>((r) => setTimeout(r, 1200));
			        } else if (faceResult && !usedHeadCropForSizing) {
            setBodyLandmarks(null);
			          setFaceLandmarks(faceResult.landmarks);
			          landmarksVisibleMin = new Promise<void>((r) => setTimeout(r, 1200));
			        } else if (faceResult) {
            setBodyLandmarks(null);
			          setFaceLandmarks(null);
			          landmarksVisibleMin = new Promise<void>((r) => setTimeout(r, 1200));
			        }
	        const facePayload: Record<string, unknown> = {
	          product: productContext,
	          sizeGuide: sizeGuide ?? { found: false },
	          sizingUnit: measurementType === "head" ? "cm" : "mm",
	          category: measurementType,
	          bodyImage: sizingBodyImage,
	        };
        facePayload.bodyContext = {
          height: data.height > 0 ? data.height : undefined,
          weight: data.weight > 0 ? data.weight : undefined,
          heightUnit: data.heightUnit as "cm" | "in" | "ft",
          weightUnit: data.weightUnit as "kg" | "lbs",
          gender: data.gender,
          age: data.age,
        };
	        facePayload.faceMeasurementsMm = faceResult?.measurementsMm ?? fallbackFaceMeasurementsMm;
		        facePayload.irisConfidence = faceResult?.irisConfidence ?? 0.35;
		        facePayload.usedFallbackFaceMeasurements = !faceResult;
		        facePayload.usedHeadCropForSizing = usedHeadCropForSizing;
          facePayload.usedBodyLandmarksForSizing = useBodyLandmarksForScan;
          if (bodyLandmarks) {
            facePayload.bodyLandmarks = bodyLandmarks;
          }
		        if (faceResult) {
		          facePayload.faceLandmarks = faceResult.landmarks;
		        }
        const recRes = await fetch(`${baseUrl}/api/v1/sizing/face-recommend`, {
          method: "POST",
          headers: jsonHeaders(key),
          body: JSON.stringify(facePayload),
        });
        if (recRes.ok) {
	          const recData = await recRes.json();
	          if (recData?.found === false) {
	            await Promise.all([minVisible, landmarksVisibleMin || Promise.resolve()]);
	            setNoSizeReason(recData?.reasoning === "NO_SIZE_CHART" ? "no-chart" : "no-match");
            setView("no-chart");
            setEstimationDone(true);
            setSizingLoading(false);
            return;
          }
          // Publish the result as soon as the backend responds so the
          // auto-try-on effect can start immediately. The minVisible timer
          // below only keeps the scan UI mounted long enough to read.
          setSizingResult(recData);
          onComplete?.(recData);
          persistResultToProfile(
            {
              gender: data.gender as "male" | "female",
              height: data.height,
              weight: data.weight,
              heightUnit: data.heightUnit as "cm" | "in" | "ft",
              weightUnit: data.weightUnit as "kg" | "lbs",
              age: data.age,
              bodyImage: data.photoBase64,
              ...braFields,
            },
            recData,
            { skipBodyEstimate: true },
          );
	        } else {
		          await Promise.all([minVisible, landmarksVisibleMin || Promise.resolve()]);
		          const errBody = await recRes.text().catch(() => "");
	          console.error("[ps-sdk] face-recommend failed:", recRes.status, errBody);
	          setErrorMessage(t("Unable to get a size suggestion. Please try manual measurements."));
	          setEstimationDone(true);
	          setSizingLoading(false);
	          setView("error");
	          return;
	        }
	      } catch (err) {
	        console.error("[ps-sdk] face-recommend failed:", err);
		        await Promise.all([minVisible, landmarksVisibleMin || Promise.resolve()]);
		        setErrorMessage(t("Unable to get a size suggestion. Please try manual measurements."));
	        setEstimationDone(true);
	        setSizingLoading(false);
	        setView("error");
	        return;
	      }
		      await Promise.all([minVisible, landmarksVisibleMin || Promise.resolve()]);
		      setSizingLoading(false);
		      return;
    }

    // Pre-compute pose landmarks IN PARALLEL. Landmarks refine the backend's
    // measurement estimates but aren't required — Gemini can estimate body
    // proportions visually when they're missing. On mobile MediaPipe can take
    // 5-15s (slow CPUs, WASM delegate fallback), so race with a short timeout
    // and proceed without landmarks if they're late. Keeps time-to-result
    // bounded at ~2s of pose-detect overhead instead of the full inference.
    modelPoseRef.current = null;
    setBodyLandmarks(null);
    const linesPromise = detectMeasurementLines(objUrl)
      .then((lines) => { modelPoseRef.current = lines; return lines; })
      .catch(() => null);
    const landmarksPromise = detectBodyLandmarks(objUrl)
      .then((lm) => { setBodyLandmarks(lm); return lm; })
      .catch(() => null);

    // Raised from 2500ms after enabling segmentation mask + worldLandmarks
    // (mask rendering on a 3024×4032 photo takes 3-4s end-to-end). Without
    // this, the request shipped to backend WITHOUT landmarks → all anchors
    // fell back to "no anchors" mode and Gemini reverted to BMI-table guesses.
    const LANDMARK_TIMEOUT_MS = 8000;
    const landmarks = await Promise.race([
      landmarksPromise,
      new Promise<null>((resolve) => setTimeout(() => resolve(null), LANDMARK_TIMEOUT_MS)),
    ]);
    if (!landmarks) console.log("[ps-sdk] landmarks not ready in", LANDMARK_TIMEOUT_MS, "ms — proceeding without them");
    void linesPromise; // measurement lines are consumed via modelPoseRef

    // Cache this MediaPipe result so the follow-up /sizing/estimate call
    // (fired from persistResultToProfile) reuses these landmarks instead of
    // re-running MediaPipe on a smaller preview image. Without this,
    // /recommend and /estimate could end up using different anchor scales.
    setCachedMediaPipe(landmarks);

    // ── Single /recommend call with photo — backend handles estimation + comparison ──
    // The photo form is the final authority for body-result units. Reading
    // only the parent sizingUnit state here can race a recent Metric/Imperial
    // selection and leave the request/result stuck on the default cm value.
    // Keep the dedicated shoe/accessory unit contracts unchanged.
    const requestedSizingUnit = data.extraMeasurements?.footLengthCm
      ? "cm"
      : measurementType === "body"
        ? (data.sizingUnit === "in" || data.sizingUnit === "cm"
          ? data.sizingUnit
          : data.heightUnit === "in" || data.heightUnit === "ft" ? "in" : "cm")
        : sizingUnit;
    if (measurementType === "body" && requestedSizingUnit !== sizingUnit) {
      setSizingUnit(requestedSizingUnit);
    }

    const payload: Record<string, unknown> = {
      sessionId: getOrCreateSessionId(),
      method: "photo",
      locale: sizingCountry,
      sizingUnit: requestedSizingUnit,
      product: productContext,
      bodyImage: data.photoBase64,
      // BlazePose landmarks (normalized 0–1 coordinates). The backend uses
      // these to compute exact pixel distances and feed them to Gemini as
      // anchors instead of asking it to estimate visually.
      bodyLandmarks: landmarks || undefined,
      measurements: {
        gender: data.gender,
        height: data.height,
        weight: data.weight,
        heightUnit: data.heightUnit,
        weightUnit: data.weightUnit,
        sizingUnit: requestedSizingUnit,
        ...(data.extraMeasurements ?? {}),
        ...(data.age && { age: data.age }),
        // Body shape profiles (item #11) — already collected in
        // CreateProfileWizard (chest/midsection/hip pickers). Forward
        // them so the backend can include in Gemini prompt.
        ...((data as any).chestProfile ? { chestProfile: (data as any).chestProfile } : {}),
        ...((data as any).midsectionProfile ? { midsectionProfile: (data as any).midsectionProfile } : {}),
        ...((data as any).hipProfile ? { hipProfile: (data as any).hipProfile } : {}),
      },
      // Bra size (item #13) — converted to bust cm in backend. Only
      // sent for women's profiles. BodyProfileView now ships a typed
      // braSize object directly; older code paths still ship raw
      // bandSize/cupSize, so handle both.
      ...((data as any).braSize
        ? { braSize: (data as any).braSize }
        : (data.gender === "female" && (data as any).bandSize && (data as any).cupSize)
          ? {
              braSize: {
                band: parseFloat(String((data as any).bandSize)),
                cup: String((data as any).cupSize),
                region: ((data as any).braRegion ?? (data as any).braSizeRegion ?? "US"),
              },
            }
          : {}),
      // Manual measurement override (item #12) — when the profile
      // already has known body measurements, ship them as a hard
      // override the backend will return verbatim.
      ...((data as any).knownMeasurements
        ? { knownMeasurements: (data as any).knownMeasurements }
        : {}),
    };
    if (sizeGuide?.found) payload.sizeGuide = sizeGuide;

    {
      const lmObj = (payload as any).bodyLandmarks;
      const jointCount = lmObj
        ? Object.keys(lmObj).filter((k) => k !== "imageWidth" && k !== "imageHeight" && lmObj[k]).length
        : 0;
      console.log(`[ps-sdk:debug] payload → bodyLandmarks=${!!lmObj}(${jointCount} joints)`);
    }

    // Hold the scanning UI for 6 s on desktop so the cycling stage text
    // (DETECTING → SCANNING → ANALYZING → MATCHING → FINALIZING) has time
    // to read through. setSizingResult is applied the moment the response
    // lands so the auto-fire useEffect kicks try-on off immediately —
    // only the UI transition (sizingLoading → false) is delayed. By the
    // time the result card appears, try-on is already ~5.8 s into its
    // Gemini render. On mobile MobileScanningView's stage timer is now
    // independent of this window, so a long minVisible doesn't dictate
    // how fast the cycling text moves there.
    const minVisible = new Promise<void>((r) => setTimeout(r, 6000));
    try {
      const tReq = Date.now();
      const payloadBytes = JSON.stringify(payload).length;
      console.log(`[ps-sdk:T] ▶ POST /sizing/recommend payload=${Math.round(payloadBytes / 1024)}KB bodyImage=${!!(payload as any).bodyImage} landmarks=${!!(payload as any).bodyLandmarks}`);
      const recRes = await fetch(`${baseUrl}/api/v1/sizing/recommend`, {
        method: "POST",
        headers: jsonHeaders(key),
        body: JSON.stringify(payload),
      });
      console.log(`[ps-sdk:T] ◀ /sizing/recommend status=${recRes.status} roundTrip=${Date.now() - tReq}ms`);
      if (recRes.ok) {
        const recData = await recRes.json();
        // Backend says no size chart for this product → empty state, skip
        // sizing UI entirely and let the user jump to try-on.
        if (recData?.found === false && recData?.reasoning === "NO_SIZE_CHART") {
          setNoSizeReason("no-chart");
          setView("no-chart");
          setEstimationDone(true);
          setSizingLoading(false);
          return;
        }
        // ── No-match short-circuit ──
        // If the backend explicitly reports found:false, do not treat it as
        // an error and do not burn Gemini on try-on. Still render the normal
        // size result screen so the user can continue, with Try-On disabled.
        const matchScore = computeMatchScore(recData);
        // Important: only block explicit no-match. Do not block lower match
        // percentages because borderline-but-valid fits should still try on.
        const isGuideOnly = !!(recData?.guideOnly || recData?.oneSize);
        const isLowFit = recData?.found === false && !isGuideOnly; void matchScore;
        if (isLowFit) {
          console.log(`[ps-sdk:gate] LOW FIT (match=${matchScore ?? "?"}%, found=${recData?.found}) — suppressing try-on`);
          noFitFoundRef.current = true;
          setTryOnProcessing(false); setTryOnStartedAt(null);
          setNoSizeReason("no-match");
          setSizingResult({ ...recData, found: false });
          onComplete?.({ ...recData, found: false });
          setView("no-chart");
          setEstimationDone(true);
          setSizingLoading(false);
          return;
        } else {
          setSizingResult(recData);
          onComplete?.(recData);
          // Save measurements (estimated in parallel) + history to the profile
          persistResultToProfile(
            {
              gender: data.gender as "male" | "female",
              height: data.height,
              weight: data.weight,
              heightUnit: data.heightUnit as "cm" | "in" | "ft",
              weightUnit: data.weightUnit as "kg" | "lbs",
              age: data.age,
              bodyImage: data.photoBase64,
              ...braFields,
            },
            recData,
          );
        }
	      } else {
	        const errBody = await recRes.text().catch(() => "");
	        console.error("[ps-sdk] Sizing recommend failed:", recRes.status, errBody);
	        setErrorMessage(t("Unable to get a size suggestion. Please try manual measurements."));
	        setEstimationDone(true);
	        setSizingLoading(false);
	        setView("error");
	        return;
	      }
	    } catch (err) {
	      console.error("[ps-sdk] Sizing recommend network error:", err);
	      setErrorMessage(t("Unable to connect to sizing service. Please try manual measurements."));
	      setEstimationDone(true);
	      setSizingLoading(false);
	      setView("error");
	      return;
	    }
	    await minVisible;
	    setSizingLoading(false);
  }, [apiUrl, productImage, productContext, measurementType, sizingUnit, weightUnit, sizingCountry, sizeGuide, dynamicFields, persistResultToProfile, resetFreshSizingRunState]);
  // Keep the forward-ref in sync so handleUseActiveProfile can call us
  snapSubmitRef.current = handleSnapSubmit;

  // ── Try-on submit (photo only, no sizing — sizing is done separately) ──
  //
  // `overrideFile` lets callers (e.g. the photo guide overlay's START TRY-ON
  // button) pass the just-selected file directly, instead of depending on
  // state propagation through `setSelectedFile`. Without this, calling
  // `handleFileSelect(file)` immediately followed by `handleTryOnSubmit()` in
  // the same React tick races against batched state updates and the closure
  // can read a stale `selectedFile` (null in normal mode or the wrong file
  // after a Retake) — surfacing as the "Please upload a photo first" error
  // even though the file is sitting right there.
  const handleTryOnSubmit = useCallback(async (overrideFile?: File) => {
		const file = overrideFile || selectedFile || selectedFileRef.current;
		if (!file || !apiRef.current) {
			const msg = !apiRef.current ? t("SDK not configured. Please provide an API key.") : t("Please upload a photo first.");
			setErrorMessage(msg);
			setView("error");
      reportSdkClientError({
        message: msg,
        code: !apiRef.current ? "SDK_NOT_CONFIGURED" : "PHOTO_MISSING",
        component: "TryOn",
        view,
        severity: !apiRef.current ? "high" : "low",
      });
      onError?.({ message: msg, code: "SDK_NOT_CONFIGURED" });
      return;
    }
    if (storeTryOnLimitReachedRef.current) {
      setTryOnProcessing(false);
      setTryOnStartedAt(null);
      setErrorMessage(null);
      if (sizingResultRef.current?.recommendedSize) {
        setView("size-result");
      } else {
        setErrorMessage(t("Your monthly try-on limit has been reached. AI sizing is still available."));
      }
      return;
    }
    // Make sure parent state is in sync with the file we're about to try on.
    // If the caller passed an override, update state so subsequent renders
    // (and the result view) see the right file/preview.
    if (overrideFile && overrideFile !== selectedFile) {
      setSelectedFile(overrideFile);
      selectedFileRef.current = overrideFile;
    }
    completedRef.current = false;
    currentTryOnJobIdRef.current = null;
    transientTryOnResultUrlRef.current = null;
    setErrorMessage(null);
    setTryOnProcessing(true);
    setTryOnStartedAt(Date.now());

    const vtoCategory = measurementTypeToVtoCategory(measurementType, resolvedProductFitType);
    const isApparel = vtoCategory === "apparel";

    // Pose detection is body-frame specific. Close-up face/head photos used
    // for eyewear/headwear try-on don't produce reliable BlazePose output,
    // and the fit-info it feeds into is irrelevant for accessories anyway.
    //
    // When the snap path already ran MediaPipe (sizingResult is in hand and
    // modelPoseRef / bodyLandmarks are populated), DO NOT re-run it. Clearing
    // and re-detecting causes a visible flicker — the skeleton overlay drops
    // off the scan card for ~1 s while BlazePose chews on the image again
    // for an identical result. Reuse the existing landmarks instead.
    const previewObjUrl = (overrideFile ? null : previewUrl) || URL.createObjectURL(file);
    if (overrideFile || !previewUrl) setPreviewUrl(previewObjUrl);
    if (isApparel && (!modelPoseRef.current || !bodyLandmarks)) {
      modelPoseRef.current = null;
      setBodyLandmarks(null);
      detectMeasurementLines(previewObjUrl)
        .then((lines) => { modelPoseRef.current = lines; })
        .catch(() => {});
      detectBodyLandmarks(previewObjUrl)
        .then((lm) => { setBodyLandmarks(lm); })
        .catch(() => {});
    }

    try {
      // Apparel try-on benefits from a sharper input — Gemini's image
      // model is constrained by source detail. 1024 / 0.85 doubles the
      // pixel count vs the legacy 512 / 0.65 default. Accessory paths
      // stay on whatever their dedicated handlers compress to.
      const modelImage = await compressImage(file, isApparel ? { maxDimension: 1024, quality: 0.85 } : {});
      formRef.current.photoBase64 = modelImage;
      formRef.current.bodyImage = modelImage;

      // Apply the user's pending size override (clicked "Use this for Try
      // On" in the detail view). The detail view already rebuilt
      // matchDetails using the right chart row (main for size, length for
      // inseam/height), so we use them verbatim. `displayLabel` goes into
      // silhouetteContext so Gemini's prompt's `recommendedSize` line
      // reads the user's pick ("34 / 32" for pants, "40R" for jacket).
      // Merge every section's override. For a 3-piece suit, user can pick
      // Jacket 42 AND Pants 33 AND Vest L — all three need to flow into the
      // silhouette prompt so Gemini renders the correct visual at each body
      // region. Dedupe by measurement name: if two sections disagree on the
      // same measurement (e.g. Height appears in both Jacket and Pants), the
      // first one wins.
      const overridesMap = pendingCustomSizesRef.current;
      const overrideSections = Object.keys(overridesMap);
      const hasAnyOverride = overrideSections.length > 0;
      const mergedOverrideMd: Array<{ measurement: string; userValue: string; chartRange: string; fit: string }> | null = hasAnyOverride ? (() => {
        const seen = new Set<string>();
        const out: Array<{ measurement: string; userValue: string; chartRange: string; fit: string }> = [];
        for (const section of overrideSections) {
          const md = overridesMap[section]?.matchDetails || [];
          for (const m of md) {
            const k = m.measurement.toLowerCase().trim();
            if (seen.has(k)) continue;
            seen.add(k);
            out.push(m);
          }
        }
        return out;
      })() : null;
      // Composite label like "Jacket 42 / Short, Pants 33 / 30" — drives the
      // "Recommended size (from brand chart)" line in Gemini's silhouette
      // prompt so the model sees every section the user customized.
      const compositeDisplayLabel = hasAnyOverride
        ? overrideSections
            .map(s => {
              const cleanName = s.replace(/\s*[—–-]\s*.*/g, "").trim();
              return `${cleanName} ${overridesMap[s].displayLabel}`;
            })
            .join(", ")
        : undefined;
      // Back-compat alias — downstream code below still reads `override`.
      const override = hasAnyOverride
        ? {
            selectedSize: compositeDisplayLabel ?? "",
            displayLabel: compositeDisplayLabel ?? "",
            matchDetails: mergedOverrideMd ?? undefined,
          }
        : null;
      console.log("[ps-sdk:tryon] reading pendingCustomSizesRef at submit", {
        sectionCount: overrideSections.length,
        sections: overrideSections,
        compositeDisplayLabel,
        mergedOverrideMdCount: mergedOverrideMd?.length ?? 0,
        mergedOverrideMdPreview: mergedOverrideMd?.map(m => ({ area: m.measurement, chartRange: m.chartRange })),
      });

      // Flatten matchDetails across root + every section, deduped by
      // measurement name. For multi-section products (tuxedo) the root
      // `matchDetails` often only carries Height — the real per-area fit
      // info (Chest/Waist/Inseam) lives in `sections.*.matchDetails`.
      // Without this flatten, Gemini's FIT DIRECTIVES block gets only one
      // row → the try-on renders without any fit variation no matter what
      // size the user picked.
      const flattenAllMatchDetails = (): Array<{ measurement: string; userValue: string; chartRange: string; fit: string; section?: string }> => {
        const seen = new Set<string>();
        const out: Array<{ measurement: string; userValue: string; chartRange: string; fit: string; section?: string }> = [];
        console.log("[ps-sdk:flatten] sizingResult keys:", Object.keys(sizingResult || {}));
        console.log("[ps-sdk:flatten] root matchDetails:", (sizingResult?.matchDetails || []).map(m => m.measurement));
        console.log("[ps-sdk:flatten] sections:", sizingResult?.sections ? Object.keys(sizingResult.sections) : "none");
        const push = (md: Array<{ measurement: string; userValue: string; chartRange: string; fit: string }> | undefined, src: string, section: string | undefined) => {
          if (!md) return;
          console.log(`[ps-sdk:flatten]   ${src} →`, md.map(m => m.measurement));
          for (const m of md) {
            const k = m.measurement.toLowerCase().replace(/\s*\(.*?\)\s*/g, "").trim();
            if (seen.has(k)) continue;
            seen.add(k);
            out.push({ ...m, section });
          }
        };
        push(sizingResult?.matchDetails, "root", undefined);
        if (sizingResult?.sections) {
          for (const [secName, sec] of Object.entries(sizingResult.sections)) {
            push((sec as { matchDetails?: Array<{ measurement: string; userValue: string; chartRange: string; fit: string }> })?.matchDetails, `section:${secName}`, secName);
          }
        }
        console.log("[ps-sdk:flatten] final unique:", out.map(m => m.measurement));
        return out;
      };

      const effectiveMatchDetails = (override?.matchDetails && override.matchDetails.length)
        ? override.matchDetails
        : flattenAllMatchDetails();

      // Build fit info from sizing result + pre-computed pose coordinates.
      // Accessories have no body-fit semantics, so skip even if matchDetails
      // is populated (accessory sizing fills in different keys).
      let fitInfo: FitAreaInfo[] | undefined;
      if (isApparel && effectiveMatchDetails.length) {
        const unitRaw = (sizingResult?.unit || sizingUnit || "in").toString().toLowerCase();
        const unit: "in" | "cm" | "mm" = unitRaw === "cm" ? "cm" : unitRaw === "mm" ? "mm" : "in";
        fitInfo = buildFitInfo(effectiveMatchDetails, modelPoseRef.current, unit);
      }
      console.log("[ps-sdk:tryon] fitInfo built", { count: fitInfo?.length || 0, areas: fitInfo?.map(f => `${f.area}(${f.fit})`) });

      // ── Pick the garment-reference image sent to Gemini ──
      // The UI keeps showing `productImage` (gallery slot 0). For
      // Gemini's image 2 we want the "model wearing the garment" shot —
      // not the flat-lay or close-up detail. Priority:
      //   1. Manual override
      //   2. Pre-picked from Gemini Flash on modal open
      //   3. In-browser MediaPipe pose-detection fallback
      //   4. productImage (gallery slot 0)
	      let garmentImage = productImage;
	      let detailImage = garmentDetailImage || bestGarmentDetailImageRef.current || null;
      const selectedStyleItems = styleMatchSelectionsRef.current.filter((item) => item.image);
      if (selectedStyleItems.length) {
        console.log("[ps-sdk:style-match] sending selected outfit items with PDP anchor garment", {
          selectedCount: selectedStyleItems.length,
        });
	      }
	      if (garmentReferenceImage) {
	        garmentImage = garmentReferenceImage;
	      } else if (bestGarmentImageRef.current) {
	        garmentImage = bestGarmentImageRef.current;
	        console.log(`[ps-sdk:tryon] using pre-picked garment reference: ${garmentImage}`);
      } else if (effectiveProductImages && effectiveProductImages.length > 1) {
	        const backendPick = await pickBestGarmentImageViaBackend(effectiveProductImages);
	        if (backendPick?.worn) {
	          garmentImage = backendPick.worn;
	          detailImage = detailImage || backendPick.detail;
	        } else {
	          const best = await pickBestGarmentImage(effectiveProductImages);
	          if (best && best !== productImage) {
	            console.log(`[ps-sdk:tryon] auto-picked garment reference (mediapipe): ${best}`);
	            garmentImage = best;
	          }
	        }
	      }
	      if (!detailImage && effectiveProductImages?.length) {
	        detailImage = effectiveProductImages.find((img) => img && img !== garmentImage) || null;
	      }
      const activeTryOnProfile = activeProfileId ? profiles.find((p) => p.id === activeProfileId) : null;

	      const response = await apiRef.current.submitTryOn(
        modelImage,
        garmentImage,
        fitInfo,
        vtoCategory,
        {
          productId: effectiveProductId,
          productTitle,
          productCategory,
          productSubcategory,
          productFitType: resolvedProductFitType,
          productType,
          productTags: productTagsList,
	          productDescription,
	          productMaterial,
	          ...(detailImage && detailImage !== garmentImage ? { garmentDetailImage: detailImage } : {}),
          ...(selectedStyleItems.length ? {
            styleMatchSelections: selectedStyleItems.map((item) => ({
              slot: item.slot,
              productId: item.productId,
              title: item.title,
              image: item.displayImage || item.image,
              url: item.url,
              color: item.color,
              garmentType: item.garmentType,
              recommendedSize: item.recommendedSize,
            })),
          } : {}),
	          ...(sizingResult ? { sizingResult } : {}),
          sdkOpenId: sdkOpenIdRef.current ?? undefined,
	          silhouetteContext: buildSilhouetteContext(
            sizingResult,
            sizeGuide,
            override?.displayLabel,
            (() => {
              // The form already collected this 200ms before /sizing/recommend
              // returned — pull straight from formRef instead of round-tripping
              // through profiles. ft → 5'6" formatting; cm/in pass through.
              const h = parseFloat(formRef.current.height || "0");
              if (!h || h <= 0) return undefined;
              const u = formRef.current.heightUnit || heightUnit || "cm";
              if (u === "ft") {
                const ft = Math.floor(h);
                const inches = Math.round((h - ft) * 12);
                return `${ft}'${inches}"`;
              }
              return u === "in" ? `${h}"` : `${h} cm`;
            })(),
            (() => {
              const w = parseFloat(formRef.current.weight || "0");
              if (!w || w <= 0) return undefined;
              const u = formRef.current.weightUnit || weightUnit || "kg";
              return `${w} ${u}`;
            })(),
          ),
          modelImageId: modelImageIdRef.current ?? undefined,
          profileLoggedIn: !!profileSessionRef.current,
          profileId: activeTryOnProfile?.id,
          profileName: activeTryOnProfile?.name,
        }
      );
      currentTryOnJobIdRef.current = response.jobId;
      tagSdkClaritySession({
        sdkOpenId: sdkOpenIdRef.current,
        productId: effectiveProductId,
        productTitle: restoredProductTitle || productTitle,
        productUrl: restoredProductUrl || effectiveProductUrl,
        view,
        jobId: response.jobId,
        source: shouldUseShopifyCartAttribution(apiUrl) ? "shopify-sdk" : "sdk",
      });
      // Capture backend-cached photo id so the next regen reuses the same bytes.
			if (response.modelImageId) modelImageIdRef.current = response.modelImageId;
			onProcessing?.(response.jobId);
			const usePollingOnly = shouldUseShopifyCartAttribution(apiUrl);
			if (!usePollingOnly) {
				sseRef.current?.disconnect();
				sseRef.current = new SseClient(response.streamUrl || apiRef.current.getStreamUrl(response.jobId));
			}
			unsubRef.current?.();
			unsubRef.current = usePollingOnly ? null : (sseRef.current?.onJob(response.jobId, handleVtoUpdate) ?? null);
      let attempts = 0;
      const maxPollingAttempts = 160; // 8 minutes at 3s intervals.
      pollingRef.current = setInterval(async () => {
        if (completedRef.current) { if (pollingRef.current) clearInterval(pollingRef.current); pollingRef.current = null; return; }
        attempts++;
        if (attempts > maxPollingAttempts) {
          if (isTransientResultImageUrl(resultImageUrlRef.current)) {
            completedRef.current = true;
            cleanupJob();
            return;
          }
          const transientFallback = transientTryOnResultUrlRef.current;
          if (transientFallback) {
            setResultImageUrl(transientFallback);
            completedRef.current = true;
            cleanupJob();
            setTryOnProcessing(false);
            setTryOnStartedAt(null);
            return;
          }
          handleVtoUpdate({
            galleryId: response.jobId,
            status: "failed",
            imageUrl: null,
            error: t("Try-on is taking longer than expected. Please try again."),
            timestamp: Date.now(),
          });
          return;
        }
        try {
          const status = await apiRef.current!.getStatus(response.jobId);
          const completedImageUrl = status.imageUrl || status.resultImageUrl || status.outputUrl || status.remoteOutputUrl || null;
          if (status.status === "completed" && completedImageUrl) {
            handleVtoUpdate({ galleryId: response.jobId, status: status.status, imageUrl: completedImageUrl, error: null, timestamp: Date.now() });
          } else if (status.status === "failed") {
            handleVtoUpdate({ galleryId: response.jobId, status: status.status, imageUrl: null, error: status.message || t("Try-on generation failed"), timestamp: Date.now() });
          }
        } catch { /* continue polling */ }
      }, 3000);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t("Failed to start try-on");
      const code = err instanceof PrimeStyleError ? err.code : undefined;
      const savedResult = savedTryOnResultFromLimitError(err);
      cleanupJob();
      setTryOnProcessing(false);
      setTryOnStartedAt(null);
      if (code !== "TRYON_IP_LIMIT_REACHED") {
        reportSdkClientError({
          message,
          code: code || "TRYON_SUBMIT_FAILED",
          stack: err instanceof Error ? err.stack : undefined,
          component: "TryOn",
          view,
          severity: "medium",
        });
      }
      if (savedResult) {
        const entry = savedTryOnResultToHistoryEntry(savedResult, {
          productImage,
          productImages: effectiveProductImages,
          productCarouselItems: effectiveProductCarouselItems,
          productTitle,
          productUrl: effectiveProductUrl,
        });
        setBackendSavedFitEntry(entry);
        setPendingBackendRestoreEntry(entry);
        onError?.({ message, code });
        return;
      }
      if (isStoreMonthlyTryOnLimitError(err)) {
        setStoreTryOnLimitReached(true);
        setErrorMessage(null);
        if (sizingResultRef.current?.recommendedSize) {
          setView("size-result");
        }
        return;
      }
      setErrorMessage(message);
      if (code === "TRYON_IP_LIMIT_REACHED" && sizingResultRef.current?.recommendedSize) {
        setView("size-result");
      } else if (!sizingResultRef.current?.recommendedSize) {
        setView("error");
      }
      onError?.({ message, code });
    }
		}, [selectedFile, productImage, effectiveProductImages, effectiveProductCarouselItems, garmentReferenceImage, garmentDetailImage, effectiveProductId, effectiveProductUrl, productTitle, restoredProductTitle, restoredProductUrl, productCategory, productSubcategory, resolvedProductFitType, productType, productTagsList, productDescription, productMaterial, measurementType, sizingResult, sizeGuide, activeProfileId, profiles, apiUrl, onProcessing, onError, handleVtoUpdate, cleanupJob, reportSdkClientError, pickBestGarmentImageViaBackend, t, view]);

  // Wait for sizingResult before firing try-on. The snap path returns
  // sizing in ~200 ms (consistent path, no Gemini call), so the wall-clock
  // cost vs the old parallel-fire is negligible. In return, the try-on
  // request carries full fitInfo + silhouetteContext (FIT DIRECTIVES,
  // user measurements, suggested size + chart-row measurements), and
  // buildFitAwarePrompt emits the whole prompt instead of falling back
  // to the bare prime directive.
  useEffect(() => {
    if (view !== "size-result") {
      autoTryOnFiredRef.current = false;
      return;
    }
    if (noFitFoundRef.current) return;
    if (storeTryOnLimitReached) return;
    if (autoTryOnFiredRef.current) return;
    if (tryOnProcessing || resultImageUrl) return;
    if (!sizingResult) return;
    const file = selectedFile || selectedFileRef.current;
    if (!file) return;
    autoTryOnFiredRef.current = true;
    handleTryOnSubmit();
  }, [view, selectedFile, sizingResult, storeTryOnLimitReached, tryOnProcessing, resultImageUrl, handleTryOnSubmit]);

  // (Size-pick regen flow removed — try-on is single-shot per submission.)

  const handleDownload = useCallback(() => {
    if (!resultImageUrl) return;
    if (resultImageUrl.startsWith("data:")) {
      const link = document.createElement("a");
      link.href = resultImageUrl;
      link.download = `primestyle-tryon-${Date.now()}.png`;
      link.click();
    } else {
      fetch(resultImageUrl).then(r => r.blob()).then(blob => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `primestyle-tryon-${Date.now()}.png`;
        link.click();
        setTimeout(() => URL.revokeObjectURL(url), 100);
      }).catch(() => window.open(resultImageUrl, "_blank"));
    }
  }, [resultImageUrl]);

  const openProductPageUrl = useCallback((url: string) => {
    if (url && typeof window !== "undefined") {
      try {
        window.open(new URL(url, window.location.origin).href, "_blank", "noopener,noreferrer");
      } catch {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    }
  }, []);

  const handleContinueShopping = useCallback(() => {
    handleClose();
  }, [handleClose]);

  const handleBackToProductPage = useCallback(() => {
    const targetUrl = restoredProductUrl || effectiveProductUrl || "";
    if (targetUrl) {
      openProductPageUrl(targetUrl);
      return;
    }
    handleClose();
  }, [effectiveProductUrl, handleClose, openProductPageUrl, restoredProductUrl]);

  const buildAddToBagSelectedSizes = useCallback((): PrimeStyleAddToBagPayload["selectedSizes"] => {
    const overrides = Object.values(pendingCustomSizesRef.current);
    if (overrides.length) {
      return overrides.map((override) => ({
        sectionName: override.sectionName,
        selectedSize: override.selectedSize,
        selectedLength: override.selectedLength,
        displayLabel: override.displayLabel,
        isOverride: true,
      }));
    }

	    const sections = (sizingResult as any)?.sections;
	    if (sections && typeof sections === "object") {
	      const selected = Object.entries(sections).map(([sectionName, sectionResult]) => {
	        const result = sectionResult as { recommendedSize?: string; size?: string; length?: string; guideOnly?: boolean; availableSizes?: string[] };
	        const selectedSize = result.size || result.recommendedSize || "";
	        if (!selectedSize) return null;
        if (result.guideOnly && (result.availableSizes?.length || 0) > 1 && /^see available sizes$/i.test(selectedSize.trim())) return null;
	        const displayLabel = [selectedSize, result.length].filter(Boolean).join(" / ");
	        return {
          sectionName,
          selectedSize,
          selectedLength: result.length,
          displayLabel,
          isOverride: false,
        };
      }).filter(Boolean) as NonNullable<PrimeStyleAddToBagPayload["selectedSizes"]>;
      return selected.length ? selected : undefined;
    }

	    const recommendedSize = sizingResult?.recommendedSize || "";
	    if (!recommendedSize) return undefined;
    if ((sizingResult as any)?.guideOnly && ((sizingResult as any)?.availableSizes?.length || 0) > 1 && /^see available sizes$/i.test(recommendedSize.trim())) {
      return undefined;
    }
	    return [{
      sectionName: "Your Fit",
      selectedSize: recommendedSize,
      displayLabel: recommendedSize,
      isOverride: false,
    }];
  }, [sizingResult]);

  const handleAddToBag = useCallback(async () => {
    if (!onAddToBag || !sizingResult) return;
    const payload = {
      productId,
      productTitle: restoredProductTitle || productTitle,
      productUrl: restoredProductUrl || effectiveProductUrl,
      recommendedSize: sizingResult.recommendedSize,
      sizingResult,
      resultImageUrl: resultImageUrlRef.current || resultImageUrl,
      historyEntryId: currentHistoryEntryIdRef.current ?? undefined,
      selectedSizes: buildAddToBagSelectedSizes(),
    };
    try {
      await onAddToBag(payload);
      reportSdkEvent("SIZE_RECOMMENDATION_ACCEPTED", {
        recommendedSize: sizingResult.recommendedSize,
        metadata: { selectedSizes: payload.selectedSizes },
      });
      reportSdkEvent("ADD_TO_CART_FROM_TRYON", {
        recommendedSize: sizingResult.recommendedSize,
        metadata: {
          selectedSizes: payload.selectedSizes,
          historyEntryId: payload.historyEntryId,
          hasResult: Boolean(payload.resultImageUrl),
        },
      });
    } catch (error) {
      reportSdkClientError({
        message: error instanceof Error ? error.message : "Add to bag failed",
        code: "ADD_TO_BAG_FAILED",
        stack: error instanceof Error ? error.stack : undefined,
        component: "ResultActions",
        view,
        severity: "medium",
        metadata: { selectedSizes: payload.selectedSizes },
      });
      throw error;
    }
  }, [
    buildAddToBagSelectedSizes,
    effectiveProductUrl,
    onAddToBag,
    productId,
    productTitle,
    restoredProductTitle,
    restoredProductUrl,
    resultImageUrl,
    reportSdkClientError,
    reportSdkEvent,
    sizingResult,
    view,
  ]);

  const handleTryOnFeedbackSubmit = useCallback(async ({ rating, note }: { rating?: number; note?: string }) => {
    const profileLoggedIn = Boolean(profileSession);
    const activeProfile = profileLoggedIn
      ? profiles.find((profile) => profile.id === activeProfileId)
      : null;
    const jobId = currentTryOnJobIdRef.current ?? undefined;
    const historyEntryId = currentHistoryEntryIdRef.current ?? undefined;
    const feedbackKey = buildTryOnFeedbackKey({
      jobId,
      historyEntryId,
      resultImageUrl,
      productId: effectiveProductId,
      productTitle: restoredProductTitle || productTitle,
      recommendedSize: sizingResult?.recommendedSize,
    });
    if (submittedFeedbackKeysRef.current.has(feedbackKey)) return;
    submittedFeedbackKeysRef.current.add(feedbackKey);
    lsSet(TRYON_FEEDBACK_SUBMITTED_KEY, Array.from(submittedFeedbackKeysRef.current).slice(-80));
    const payload = {
      jobId,
      historyEntryId,
      rating,
      note: note?.trim() || undefined,
      productId: effectiveProductId,
      productTitle: restoredProductTitle || productTitle,
      productUrl: restoredProductUrl || effectiveProductUrl,
      recommendedSize: sizingResult?.recommendedSize,
      profileLoggedIn,
      profileId: activeProfile?.id,
      profileName: activeProfile?.name,
      profileAccessToken: profileSession?.accessToken,
    };
    try {
      if (shouldUseShopifyCartAttribution(apiUrl)) {
        await logTryOnFeedback(payload);
        return;
      }
      await apiRef.current?.submitTryOnFeedback(payload);
    } catch (error) {
      submittedFeedbackKeysRef.current.delete(feedbackKey);
      lsSet(TRYON_FEEDBACK_SUBMITTED_KEY, Array.from(submittedFeedbackKeysRef.current).slice(-80));
      throw error;
    }
  }, [activeProfileId, apiUrl, effectiveProductId, effectiveProductUrl, productTitle, profileSession, profiles, restoredProductTitle, restoredProductUrl, resultImageUrl, sizingResult?.recommendedSize]);

  const handleRetry = useCallback(() => {
    completedRef.current = false;
    cleanupJob();
    setSelectedFile(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setResultImageUrl(null);
    historySavedRef.current = false;
    historyTryonSavedRef.current = false;
    currentHistoryEntryIdRef.current = null;
    currentTryOnJobIdRef.current = null;
    setRestoredProductImage(null);
    setRestoredProductImages(null);
    setRestoredProductCarouselItems(null);
    setRestoredProductTitle(null);
    setRestoredProductUrl(null);
    setRestoredHistoryEntryId(null);
    setRestoredHistoryMatchesCurrentProduct(false);
    setErrorMessage(null);
    setSizingMethod(null);
    setSizingResult(null);
    setSizingLoading(false);
    setProfileSaved(false);
    setBodyProfileInitialStep("basics");
    setFormKey((k) => k + 1);
    setView("body-profile");
  }, [previewUrl, cleanupJob]);

  // ── Profile helpers ──
  const applyProfile = useCallback((id: string) => {
    const p = profiles.find(pr => pr.id === id);
    if (!p) return;
    setActiveProfileId(id);
    // Update lastUsedAt timestamp
    setProfiles(prev => prev.map(pr => pr.id === id ? { ...pr, lastUsedAt: Date.now() } : pr));
    const fd: Record<string, string> = { gender: p.gender || "male" };
    if (p.measurements) {
      for (const [key, value] of Object.entries(p.measurements)) {
        if (typeof value === "number" && Number.isFinite(value)) {
          fd[key] = String(value);
        }
      }
    }
    if (p.height ?? p.heightCm) fd.height = String(p.height ?? p.heightCm);
    if (p.weight ?? p.weightKg) fd.weight = String(p.weight ?? p.weightKg);
    if (p.chest) fd.chest = String(p.chest);
    if (p.bust) fd.bust = String(p.bust);
    if (p.waist) fd.waist = String(p.waist);
    if (p.hips) fd.hips = String(p.hips);
    if (p.shoulderWidth) fd.shoulderWidth = String(p.shoulderWidth);
    if (p.sleeveLength) fd.sleeveLength = String(p.sleeveLength);
    if (p.inseam) fd.inseam = String(p.inseam);
    if (p.neckCircumference) fd.neckCircumference = String(p.neckCircumference);
    if (p.footLengthCm) fd.footLengthCm = String(p.footLengthCm);
    if (p.shoeEU) fd.shoeEU = p.shoeEU;
    if (p.shoeUS) fd.shoeUS = p.shoeUS;
    if (p.shoeUK) fd.shoeUK = p.shoeUK;
    if (p.fitPreference) fd.fitPreference = p.fitPreference;
    if (p.customMeasurements) {
      for (const [key, value] of Object.entries(p.customMeasurements)) {
        fd[key] = String(value);
      }
    }
    formRef.current = fd;
    setFormGender(fd.gender || "male");
    if (p.country) setSizingCountry(p.country);
    if (p.measurementsUnit) setSizingUnit(p.measurementsUnit);
    else if (p.sizingUnit) setSizingUnit(p.sizingUnit);
    if (p.heightUnit) setHeightUnit(p.heightUnit);
    if (p.weightUnit) setWeightUnit(p.weightUnit);
    setFormKey(k => k + 1);
    reportProfileEvent("SDK_PROFILE_SELECTED", p, {
      measurementKeys: p.measurements ? Object.keys(p.measurements) : Object.keys(fd),
      source: "applyProfile",
    });
  }, [profiles, reportProfileEvent]);
  applyProfileRef.current = applyProfile;

  const saveProfile = useCallback((name: string, asNew?: boolean) => {
    const id = (asNew ? null : activeProfileId) || `p_${Date.now()}`;
    const isExistingProfile = !asNew && profiles.some((x) => x.id === id);
    const existingProfile = isExistingProfile ? profiles.find((x) => x.id === id) : null;
    const formPhotoBase64 =
      typeof formRef.current.photoBase64 === "string" && formRef.current.photoBase64.trim()
        ? formRef.current.photoBase64.trim()
        : typeof formRef.current.bodyImage === "string" && formRef.current.bodyImage.trim()
          ? formRef.current.bodyImage.trim()
          : null;
    const p: Profile = {
      id, name,
      gender: (formRef.current.gender as "male" | "female") || "male",
      height: formRef.current.height ? parseFloat(formRef.current.height) : undefined,
      weight: formRef.current.weight ? parseFloat(formRef.current.weight) : undefined,
      chest: formRef.current.chest ? parseFloat(formRef.current.chest) : undefined,
      bust: formRef.current.bust ? parseFloat(formRef.current.bust) : undefined,
      waist: formRef.current.waist ? parseFloat(formRef.current.waist) : undefined,
      hips: formRef.current.hips ? parseFloat(formRef.current.hips) : undefined,
      shoulderWidth: formRef.current.shoulderWidth ? parseFloat(formRef.current.shoulderWidth) : undefined,
      sleeveLength: formRef.current.sleeveLength ? parseFloat(formRef.current.sleeveLength) : undefined,
      inseam: formRef.current.inseam ? parseFloat(formRef.current.inseam) : undefined,
      neckCircumference: formRef.current.neckCircumference ? parseFloat(formRef.current.neckCircumference) : undefined,
      footLengthCm: formRef.current.footLengthCm ? parseFloat(formRef.current.footLengthCm) : undefined,
      shoeEU: formRef.current.shoeEU, shoeUS: formRef.current.shoeUS, shoeUK: formRef.current.shoeUK,
      fitPreference: formRef.current.fitPreference,
      country: sizingCountry,
      sizingUnit,
      heightUnit,
      weightUnit,
      createdAt: existingProfile?.createdAt ?? Date.now(),
      lastUsedAt: Date.now(),
    };
    if (formPhotoBase64) p.photoBase64 = formPhotoBase64;
    else if (existingProfile?.photoUrl) p.photoUrl = existingProfile.photoUrl;
    else if (existingProfile?.photoBase64) p.photoBase64 = existingProfile.photoBase64;
    // Save any dynamic fields not in the typed Profile
    const KNOWN_PROFILE_KEYS = new Set([
      "chest", "bust", "waist", "hips", "shoulderWidth", "sleeveLength",
      "inseam", "neckCircumference", "footLengthCm", "shoeEU", "shoeUS", "shoeUK",
      "fitPreference", "gender", "height", "weight", "heightFeet", "heightInches",
    ]);
    const custom: Record<string, number> = {};
    for (const [key, val] of Object.entries(formRef.current)) {
      if (!KNOWN_PROFILE_KEYS.has(key) && val && !isNaN(Number(val))) {
        custom[key] = parseFloat(val);
      }
    }
    if (Object.keys(custom).length > 0) p.customMeasurements = custom;
    setProfiles(prev => {
      const idx = prev.findIndex(x => x.id === id);
      if (idx >= 0) { const next = [...prev]; next[idx] = p; return next; }
      return [...prev, p].slice(-50);
    });
    setActiveProfileId(id);
    setProfileSaved(true);
    reportProfileEvent(isExistingProfile ? "SDK_PROFILE_UPDATED" : "SDK_PROFILE_CREATED", p, {
      source: "manual-save",
      customMeasurementKeys: Object.keys(custom),
    });
  }, [activeProfileId, profiles, sizingCountry, sizingUnit, heightUnit, weightUnit, reportProfileEvent]);

  const saveHistoryEntry = useCallback(async (): Promise<string> => {
    const id = `h_${Date.now()}`;
    currentHistoryEntryIdRef.current = id;

    // Snapshot the user's photo into IndexedDB at ORIGINAL quality.
    // Previously we re-encoded to 768 px @ 0.7 — that was visibly soft
    // when re-running try-on from a history entry. The browser stores
    // Blobs in IndexedDB; size is bounded by browser quota (~50% of disk
    // on most setups), so the original JPEG at typical phone resolutions
    // is fine.
    let hasPhoto = false;
    const file = selectedFile || selectedFileRef.current;
    if (file) {
      try {
        hasPhoto = await savePhotoIDB(id, file);
      } catch {
        /* non-fatal — entry still saved without photo */
      }
    }

    // Snapshot what the user actually picked when they ran the try-on, plus
    // whether each section's pick matched the AI recommendation or overrode it.
    const overrides = pendingCustomSizesRef.current;
    const recommended = sizingResult?.recommendedSize ?? "";
    const selectedSizes = Object.values(overrides).map(o => ({
      sectionName: o.sectionName,
      selectedSize: o.selectedSize,
      selectedLength: o.selectedLength,
      displayLabel: o.displayLabel,
      isOverride: !!recommended && o.selectedSize !== recommended,
    }));

    const latestResultImageUrl = resultImageUrlRef.current;
    const durableResultUrl = durableHistoryImageUrl(latestResultImageUrl);
    const profileDraftSnapshot = buildProfileCompletionDraft();

    const entry: HistoryEntry = {
      id,
      productId: effectiveProductId,
      productTitle,
      productUrl: effectiveProductUrl,
      jobId: currentTryOnJobIdRef.current ?? undefined,
      productImage,
      productImages: effectiveProductImages?.slice(0, 12),
      productCarouselItems: effectiveProductCarouselItems?.slice(0, 12),
      resultImageUrl: durableResultUrl,
      recommendedSize: sizingResult?.recommendedSize,
      confidence: sizingResult?.confidence,
      reasoning: sizingResult?.reasoning,
      internationalSizes: sizingResult?.internationalSizes,
      matchDetails: sizingResult?.matchDetails,
      sizingResult: sizingResult || undefined,
      sizeGuide: sizeGuide ? {
        found: sizeGuide.found,
        title: sizeGuide.title,
        headers: sizeGuide.headers,
        rows: sizeGuide.rows,
        sections: sizeGuide.sections,
        sectionImages: sizeGuide.sectionImages,
      } : undefined,
      profileCompletionDraft: profileDraftSnapshot,
      date: Date.now(),
      hasPhoto: hasPhoto || undefined,
      hasResult: latestResultImageUrl ? true : undefined,
      selectedSizes: selectedSizes.length ? selectedSizes : undefined,
    };
    if (activeProfileId) {
      const p = profiles.find(x => x.id === activeProfileId);
      if (p) entry.profileName = p.name;
    }
    setHistory(prev => {
      const next = [entry, ...prev].slice(0, 50);
      // Garbage-collect IndexedDB photos that no longer correspond to a
      // surviving history entry (i.e. the ones trimmed off the tail).
      const keep = new Set(next.map(e => e.id));
      void prunePhotosIDB(keep);
      return next;
    });
    return id;
  }, [effectiveProductId, productTitle, effectiveProductUrl, productImage, effectiveProductImages, effectiveProductCarouselItems, resultImageUrl, sizingResult, sizeGuide, activeProfileId, profiles, selectedFile, buildProfileCompletionDraft]);

  const persistHistoryResult = useCallback((entryId: string, imageUrl: string, cleanupWhenIdle: boolean) => {
    void (async () => {
      const durableResultUrl = durableHistoryImageUrl(imageUrl);
      let storedResult = false;
      try {
        const res = await fetch(imageUrl);
        if (res.ok) {
          const blob = await res.blob();
          storedResult = await saveResultIDB(entryId, blob);
        }
      } catch { /* non-fatal — URL fallback still works */ }
      if (storedResult || durableResultUrl) {
        setHistory(prev => prev.map(entry => {
          if (entry.id !== entryId) return entry;
          return {
            ...entry,
            ...(currentTryOnJobIdRef.current ? { jobId: currentTryOnJobIdRef.current } : {}),
            ...(durableResultUrl ? { resultImageUrl: durableResultUrl } : {}),
            ...(storedResult ? { hasResult: true } : {}),
          };
        }));
      }
      // If we kept SSE/polling alive past close so this background result
      // could land, tear them down now that history is safely patched.
      if (cleanupWhenIdle) {
        unsubRef.current?.(); unsubRef.current = null;
        if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
      }
    })();
  }, []);

  // Save history when sizing or try-on result is shown
  useEffect(() => {
    if (view === "size-result" && sizingResult && !historySavedRef.current) {
      historySavedRef.current = true;
      historyTryonSavedRef.current = !!resultImageUrl;
      saveHistoryEntry()
        .then((id) => {
          const latestResult = resultImageUrlRef.current;
          if (latestResult) persistHistoryResult(id, latestResult, false);
        })
        .catch(() => { /* non-fatal */ });
    } else if ((view === "size-result" || view === "idle" || view === "photo-guide" || view === "result") && resultImageUrl && !historyTryonSavedRef.current) {
      // Try-on result arrived after initial save: patch the most-recent
      // entry with the result image AND the picks the user just confirmed.
      // Also fires when view==="idle" (user closed the modal mid-try-on) or
      // view==="photo-guide" (no-chart/direct try-on) so completed results
      // still land in history even without a sizing result.
      historyTryonSavedRef.current = true;
      if (!historySavedRef.current) {
        historySavedRef.current = true;
        saveHistoryEntry()
          .then((id) => persistHistoryResult(id, resultImageUrl, view === "idle"))
          .catch(() => { /* non-fatal */ });
        return;
      }
      const overrides = pendingCustomSizesRef.current;
      const recommended = sizingResult?.recommendedSize ?? "";
      const selectedSizes = Object.values(overrides).map(o => ({
        sectionName: o.sectionName,
        selectedSize: o.selectedSize,
        selectedLength: o.selectedLength,
        displayLabel: o.displayLabel,
        isOverride: !!recommended && o.selectedSize !== recommended,
      }));
      // Persist the try-on result image as a Blob in IndexedDB so the
      // history can restore it even when the saved URL has expired
      // (Cloudinary delete, data: URL too big for localStorage, etc.).
      const entryId = currentHistoryEntryIdRef.current;
      if (!entryId) {
        saveHistoryEntry()
          .then((id) => persistHistoryResult(id, resultImageUrl, view === "idle"))
          .catch(() => { /* non-fatal */ });
        return;
      }
      const durableResultUrl = durableHistoryImageUrl(resultImageUrl);
      setHistory(prev => {
        const idx = prev.findIndex(entry => entry.id === entryId);
        if (idx < 0) return prev;
        const updated = {
          ...prev[idx]!,
          ...(currentTryOnJobIdRef.current ? { jobId: currentTryOnJobIdRef.current } : {}),
          ...(durableResultUrl ? { resultImageUrl: durableResultUrl } : {}),
          hasResult: true,
          ...(selectedSizes.length ? { selectedSizes } : {}),
        };
        const next = [...prev];
        next[idx] = updated;
        return next;
      });
      persistHistoryResult(entryId, resultImageUrl, view === "idle");
    } else if (view === "result" && resultImageUrl && !historySavedRef.current) {
      historySavedRef.current = true;
      saveHistoryEntry()
        .then((id) => persistHistoryResult(id, resultImageUrl, false))
        .catch(() => { /* non-fatal */ });
    } else if (view === "welcome" || view === "body-profile") {
      historySavedRef.current = false;
      historyTryonSavedRef.current = false;
      currentHistoryEntryIdRef.current = null;
      currentTryOnJobIdRef.current = null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, sizingResult, resultImageUrl]);

  // ── Restore a history entry into the result view ──
  const restoreHistory = useCallback((entry: HistoryEntry) => {
    // History restore is a review action. Stop any live try-on stream first
    // so a late SSE/polling result cannot replace the restored image.
    cleanupJob();
    completedRef.current = true;
    setTryOnProcessing(false);
    setTryOnStartedAt(null);
    // Mark as already saved so it won't re-save this history entry
    historySavedRef.current = true;
    historyTryonSavedRef.current = !!entry.resultImageUrl || !!entry.hasResult;
    currentHistoryEntryIdRef.current = entry.id;
    currentTryOnJobIdRef.current = entry.jobId || null;
    // Block the auto-fire effect — it watches sizingResult+selectedFile and
    // would otherwise kick off a fresh try-on the moment the IndexedDB photo
    // restoration sets selectedFile. Restoring history must NEVER spend a
    // try-on credit; the user is reviewing what they already paid for.
    autoTryOnFiredRef.current = true;
    // Override the displayed garment image/title with the entry's snapshot
    // so the left-side product card matches the size info on the right when
    // the user opens history from a different product page.
	    const isCurrentProductEntry = historyEntryMatchesProduct(entry, productId, productTitle);
    setRestoredProductImage(entry.productImage || null);
    setRestoredProductImages(
      entry.productImages?.length
        ? entry.productImages
        : isCurrentProductEntry && effectiveProductImages?.length
          ? effectiveProductImages
          : entry.productImage
            ? [entry.productImage]
            : null,
    );
    setRestoredProductCarouselItems(
      entry.productCarouselItems?.length
        ? entry.productCarouselItems
        : isCurrentProductEntry && effectiveProductCarouselItems?.length
          ? effectiveProductCarouselItems
          : null,
    );
	    setRestoredProductTitle(entry.productTitle || null);
	    setRestoredProductUrl(entry.productUrl || null);
	    setRestoredHistoryEntryId(entry.id);
	    setRestoredHistoryMatchesCurrentProduct(isCurrentProductEntry);
    if (entry.profileCompletionDraft) {
      const draft = entry.profileCompletionDraft;
      const restoredForm: Record<string, string> = { ...formRef.current };
      if (draft.gender) restoredForm.gender = draft.gender;
      if (draft.height != null) restoredForm.height = String(draft.height);
      if (draft.weight != null) restoredForm.weight = String(draft.weight);
      if (draft.heightUnit) restoredForm.heightUnit = String(draft.heightUnit);
      if (draft.weightUnit) restoredForm.weightUnit = String(draft.weightUnit);
      if (draft.age != null) restoredForm.age = String(draft.age);
      if (draft.chestProfile) restoredForm.chestProfile = draft.chestProfile;
      if (draft.midsectionProfile) restoredForm.midsectionProfile = draft.midsectionProfile;
      if (draft.hipProfile) restoredForm.hipProfile = draft.hipProfile;
      if (draft.bandSize) restoredForm.bandSize = draft.bandSize;
      if (draft.cupSize) restoredForm.cupSize = draft.cupSize;
      if (draft.braSizeRegion) restoredForm.braSizeRegion = draft.braSizeRegion;
      if (draft.customMeasurements) {
        for (const [key, value] of Object.entries(draft.customMeasurements)) {
          restoredForm[key] = String(value);
        }
      }
      formRef.current = restoredForm;
      setFormGender(restoredForm.gender || "male");
      if (draft.heightUnit) setHeightUnit(String(draft.heightUnit));
      if (draft.weightUnit) setWeightUnit(String(draft.weightUnit));
      if (draft.sizingUnit) setSizingUnit(String(draft.sizingUnit));
    }
    // Restore sizing result
    if (entry.sizingResult) {
      setSizingResult(entry.sizingResult);
    } else if (entry.recommendedSize) {
      // Fallback for old entries without full sizingResult
      setSizingResult({
        recommendedSize: entry.recommendedSize,
        confidence: entry.confidence || "high",
        reasoning: entry.reasoning || "",
        internationalSizes: entry.internationalSizes,
        matchDetails: entry.matchDetails,
      });
    }
    // Restore size guide
    if (entry.sizeGuide) {
      setSizeGuide(entry.sizeGuide as SizeGuide);
      setSizeGuideFetching(false);
    }
    // Restore try-on result image. Prefer the IndexedDB blob (always
    // present when hasResult is set) since the saved URL may have
    // expired or been a too-large data: URL that lost detail. Fall back
    // to the URL when no blob is stored. The URL is set immediately so
    // the UI doesn't flash empty while the IDB read resolves.
    setResultImageUrl(entry.resultImageUrl || null);
    if ((entry as any).hasResult || entry.resultImageUrl) {
      void getResultIDB(entry.id).then(blob => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        setResultImageUrl(url);
      });
    }
    // Restore the user's photo into selectedFile from IndexedDB so a
    // subsequent "Try On" click does NOT re-prompt for upload. Async — the
    // view transition runs immediately; the file is set as soon as the
    // IndexedDB read returns.
    if (entry.hasPhoto) {
      void getPhotoIDB(entry.id).then(blob => {
        if (!blob) return;
        const file = new File([blob], `history-photo-${entry.id}.jpg`, { type: blob.type || "image/jpeg" });
        setSelectedFile(file);
        selectedFileRef.current = file;
      });
    }
    // Restore the user's per-section picks so the SizeResultView shows the
    // same selected pills + isOverride indicator.
    if (entry.selectedSizes && entry.selectedSizes.length) {
      const map: Record<string, CustomOverride> = {};
      for (const s of entry.selectedSizes) {
        map[s.sectionName] = {
          sectionName: s.sectionName,
          selectedSize: s.selectedSize,
          selectedLength: s.selectedLength,
          displayLabel: s.displayLabel,
        };
      }
      setPendingCustomSizes(map);
      pendingCustomSizesRef.current = map;
    }
    setSizingLoading(false);
    // Single-section products: skip the section-cards intermediate
    // screen and go straight to the detail view (the size table). The
    // user already chose this product; one card to click into is just
    // friction. Multi-section products (tuxedo: jacket / pants / vest)
    // keep the cards because they still need a per-section picker.
    const sectionNames = entry.sizeGuide?.sections ? Object.keys(entry.sizeGuide.sections) : [];
    if (sectionNames.length === 1) {
      setActiveSection(sectionNames[0]!);
    } else {
      setActiveSection(null);
    }
    setDrawer(null);
    setView("size-result");
  }, [cleanupJob, productId, productTitle, effectiveProductImages, effectiveProductCarouselItems]);

  useEffect(() => {
    if (!pendingBackendRestoreEntry) return;
    onOpen?.();
    restoreHistory(pendingBackendRestoreEntry);
    setPendingBackendRestoreEntry(null);
  }, [onOpen, pendingBackendRestoreEntry, restoreHistory]);

  const savedStorefrontFitEntry = useMemo(() => {
    if (!shouldUseStorefrontSavedFit) return null;
    if (
      backendSavedFitEntry?.recommendedSize &&
      historyEntryMatchesProduct(backendSavedFitEntry, effectiveProductId, productTitle)
    ) {
      return backendSavedFitEntry;
    }
    if (
      shouldUseCurrentRunSavedFit &&
      currentRunSavedFitEntry?.recommendedSize &&
      hasCompletedTryOnResult(currentRunSavedFitEntry) &&
      historyEntryMatchesProduct(currentRunSavedFitEntry, effectiveProductId, productTitle)
    ) {
      return currentRunSavedFitEntry;
    }
    return null;
  }, [
    backendSavedFitEntry,
    currentRunSavedFitEntry,
    effectiveProductId,
    productTitle,
    shouldUseCurrentRunSavedFit,
    shouldUseStorefrontSavedFit,
  ]);

	  const storefrontButtonLoading = shouldUseStorefrontSavedFit && backendSavedFitLookup;
	  const storefrontButtonText = storefrontButtonLoading
	    ? t("Checking your fit")
	    : savedStorefrontFitEntry?.recommendedSize
	    ? `${t("Your size is")} ${savedStorefrontFitEntry.recommendedSize}`
	    : resolvedButtonText;

	  const handleStorefrontButtonClick = useCallback(() => {
	    if (storefrontButtonLoading) return;
	    if (savedStorefrontFitEntry) {
	      onOpen?.();
	      restoreHistory(savedStorefrontFitEntry);
	      return;
	    }
	    handleOpen();
	  }, [handleOpen, onOpen, restoreHistory, savedStorefrontFitEntry, storefrontButtonLoading]);

  // ── Form field updater (ref-based — no re-render) ──
  const updateField = useCallback((key: string, val: string) => {
    formRef.current[key] = val;
  }, []);

  // ── Shoe field based on country ──
  const shoeField = useMemo(() => {
    const map: Record<string, { key: string; labelKey: string; ph: string }> = {
      US: { key: "shoeUS", labelKey: "Shoe size (US)", ph: "e.g. 10" },
      UK: { key: "shoeUK", labelKey: "Shoe size (UK)", ph: "e.g. 9" },
      AU: { key: "shoeUK", labelKey: "Shoe size (UK)", ph: "e.g. 9" },
    };
    const raw = map[sizingCountry] || { key: "shoeEU", labelKey: "Shoe size (EU)", ph: "e.g. 43" };
    return { key: raw.key, label: t(raw.labelKey), ph: raw.ph };
  }, [sizingCountry, t]);

  // ── CSS variable mapping ──
  const rootVars: Record<string, string | undefined> = {
    "--ps-btn-bg": btnS.backgroundColor, "--ps-btn-color": btnS.textColor,
    "--ps-btn-radius": btnS.borderRadius, "--ps-btn-font-size": btnS.fontSize,
    "--ps-btn-font": btnS.fontFamily, "--ps-btn-font-weight": btnS.fontWeight,
    "--ps-btn-text-decoration": btnS.textDecoration,
    "--ps-btn-padding": btnS.padding, "--ps-btn-border": btnS.border,
    "--ps-btn-width": btnS.width, "--ps-btn-min-width": btnS.minWidth,
    "--ps-btn-max-width": btnS.maxWidth, "--ps-btn-height": btnS.height,
    "--ps-btn-hover-bg": btnS.hoverBackgroundColor, "--ps-btn-hover-color": btnS.hoverTextColor,
    "--ps-btn-icon-size": btnS.iconSize, "--ps-btn-icon-color": btnS.iconColor,
    "--ps-btn-shadow": btnS.boxShadow, "--ps-modal-overlay": mdlS.overlayColor,
    "--ps-modal-bg": mdlS.backgroundColor, "--ps-modal-color": mdlS.textColor,
    "--ps-modal-radius": mdlS.borderRadius, "--ps-modal-width": mdlS.width,
    "--ps-modal-max-width": mdlS.maxWidth, "--ps-modal-font": mdlS.fontFamily,
    "--ps-modal-header-bg": mdlS.headerBackgroundColor, "--ps-modal-header-color": mdlS.headerTextColor,
    "--ps-modal-close-color": mdlS.closeButtonColor, "--ps-upload-border": mdlS.uploadBorderColor,
    "--ps-upload-bg": mdlS.uploadBackgroundColor, "--ps-upload-color": mdlS.uploadTextColor,
    "--ps-upload-icon-color": mdlS.uploadIconColor, "--ps-modal-primary-bg": mdlS.primaryButtonBackgroundColor,
    "--ps-modal-primary-color": mdlS.primaryButtonTextColor, "--ps-modal-primary-radius": mdlS.primaryButtonBorderRadius,
    "--ps-loader": mdlS.loaderColor, "--ps-result-radius": mdlS.resultBorderRadius,
    // New theming variables.
    // accentColor falls back to the trigger button background so the user's
    // brand color cascades to the modal automatically without having to pass
    // both buttonStyles AND modalStyles. Most consumers only set buttonStyles.
    "--ps-accent": mdlS.accentColor ?? btnS.backgroundColor,
    "--ps-accent-hover": mdlS.accentHoverColor ?? btnS.hoverBackgroundColor,
    "--ps-accent-light": mdlS.accentLightColor,
    "--ps-text-primary": mdlS.textPrimaryColor, "--ps-text-secondary": mdlS.textSecondaryColor,
    "--ps-text-muted": mdlS.textMutedColor, "--ps-border-color": mdlS.borderColor,
    "--ps-bg-secondary": mdlS.secondaryBackgroundColor,
    "--ps-error-color": mdlS.errorColor, "--ps-success-color": mdlS.successColor,
    "--ps-logo-height": mdlS.logoHeight,
  };
  const cssVars = Object.fromEntries(Object.entries(rootVars).filter(([, v]) => v !== undefined)) as CSSProperties;
  const isTextTrigger = btnS.displayMode === "text";

  // ── Render body (key forces animation only on view change) ──
  function renderBody() {
    switch (view) {
      case "welcome": return <div key="v-welcome" className="ps-tryon-view-enter"><WelcomeView productImage={productImage} setView={setView} t={t} /></div>;
      case "body-profile": {
        // Wrist still uses a dedicated measurement view. Hats and sunglasses
        // intentionally use the newer body/profile UI below: hats should feel
        // like necklace/earring accessories, and sunglasses should use the
        // same photo/details screen as the men's flow while keeping the
        // head/face sizing + VTO category internally.
        const activeProfileForAccessory = profiles.find((x) => x.id === activeProfileId) || null;
        const accCommon = {
          productImage,
          productTitle,
          formRef,
          activeProfile: activeProfileForAccessory,
          sizingUnit,
          setSizingUnit,
          setSizingMethod,
          setSizingLoading,
          setView,
          submitSizing,
          onSnapSubmit: handleSnapSubmit,
          onBack: handleClose,
          onStartFresh: () => {
            setActiveProfileId(null);
            formRef.current = {};
            setFormKey((k) => k + 1);
          },
          apiUrl: getApiUrl(apiUrl),
          apiKey: getApiKey(),
          sizeGuide,
          t,
        };
        if (measurementType === "wrist") return <div key={`v-wrist-${activeProfileId || "anon"}-${formKey}`} className="ps-tryon-view-enter"><WristSizeView {...accCommon} /></div>;

        // Default: full body sizing wizard
        const isSunglassesUi = measurementType === "face";
        const isHatUi = measurementType === "head";
        const isWomenProduct = isSunglassesUi ? false : effectiveProductGender === "female";
        const simplePhotoOnly = measurementType === "foot" || measurementType === "body-basic" || isHatUi;
        const activeProfileForBody = profiles.find((x) => x.id === activeProfileId) || null;
        const waitingForActiveProfile =
          !!profileSession &&
          profileStoreHydrating &&
          profiles.length === 0 &&
          !activeProfileForBody;
        if (waitingForActiveProfile) {
          return (
            <div key="v-bodyprofile-loading" className="ps-tryon-view-enter">
              <div className="ps-bp-profile-hydrating">
                <div className="ps-tryon-size-loading-spinner" aria-hidden="true" />
                <span>{t("Loading your fit profile")}</span>
              </div>
            </div>
          );
        }
        return <div key="v-bodyprofile" className="ps-tryon-view-enter"><BodyProfileView
        // Remount when the active profile changes so the basics step seeds
        // the new profile's height/weight/age into its useState initializers.
        key={`bp-${activeProfileId || "anon"}-${formKey}-${bodyProfileInitialStep || "default"}`}
        productImage={productImage}
        productImages={effectiveProductImages}
        productCarouselItems={effectiveProductCarouselItems}
        productTitle={productTitle}
        isWomen={isWomenProduct}
        basicOnly={false}
	        initialStep={bodyProfileInitialStep === "basics" ? "basics" : undefined}
	        simplePhotoOnly={simplePhotoOnly}
	        simpleAccessoryLayout={measurementType === "body-basic" || isHatUi}
	        simpleQuestionMode={measurementType === "foot" ? "shoeReference" : "heightWeight"}
        formRef={formRef}
        sizingCountry={sizingCountry}
        heightUnit={heightUnit} setHeightUnit={setHeightUnit}
        weightUnit={weightUnit} setWeightUnit={setWeightUnit}
        sizingUnit={sizingUnit} setSizingUnit={setSizingUnit}
        apiUrl={getApiUrl(apiUrl)}
        apiKey={getApiKey()}
        styleMatchProduct={styleMatchProduct}
        outfitBuilderSource={outfitBuilderSource}
        instantOutfitLooks={instantOutfitLooks}
        hasActiveProfileWithMeasurements={(() => {
          // Mirrors the gate inside `handleUseActiveProfile`. We used to also
          // require `p.measurements` to be non-empty, but `recommendForProduct`
          // doesn't need it — the backend estimates missing body parts. Gating
          // on basic identity (height + weight) is enough to enable the
          // "Find My Best Fit" fast path on freshly-created profiles. Supports
          // BOTH the canonical and legacy field names.
          const p = activeProfileForBody;
          if (!p) return false;
          const ph = (p.height ?? (p as any).heightCm ?? 0) as number;
          const pw = (p.weight ?? (p as any).weightKg ?? 0) as number;
          return ph > 0 && pw > 0;
        })()}
        onUseActiveProfile={handleUseActiveProfile}
        activeProfileName={(() => {
          const p = activeProfileForBody;
          return p?.name || null;
        })()}
        activeProfile={activeProfileForBody}
        currentRecommendedSize={sizingResult?.recommendedSize || savedStorefrontFitEntry?.recommendedSize || currentRunSavedFitEntry?.recommendedSize || null}
        onStartFresh={() => {
          setActiveProfileId(null);
          setBodyProfileInitialStep(null);
          formRef.current = {};
        }}
        onComplete={(data) => {
          formRef.current.gender = data.gender;
          if (data.bandSize) formRef.current.bandSize = data.bandSize;
          if (data.cupSize) formRef.current.cupSize = data.cupSize;
          if ((data as any).braSizeRegion) formRef.current.braSizeRegion = String((data as any).braSizeRegion);
          // Manual entry path — no photo collected here. Mark Try-On as
          // unavailable so the result screen drops the button entirely.
          // bodyImage is set when the user filled the manual form AFTER
          // taking a photo somewhere upstream; we treat that as photo-mode.
          setUsedAiPhotoSizing(!!data.bodyImage);
          handleQuickEstimate(
            data.height, data.weight, data.heightUnit, data.weightUnit, data.gender,
            data.age, undefined,
            data.chestProfile, data.midsectionProfile, data.hipProfile,
            data.bodyImage,
          );
        }}
        onSnapSubmit={(data) => {
          // AI photo flow — user uploaded a body photo; Try-On stays on.
          setUsedAiPhotoSizing(true);
          handleSnapSubmit(data);
        }}
        onBack={handleClose}
        t={t}
      /></div>;
      }
      case "estimation-review": return <div key="v-est-review" className="ps-tryon-view-enter"><EstimationReviewView
        productImage={productImage} productTitle={productTitle}
        estimatedValues={estimatedValues} estimationLoading={estimationLoading}
        dynamicFields={dynamicFields}
        formRef={formRef} formGender={formGender} formKey={formKey} setFormKey={setFormKey}
        sizingUnit={sizingUnit} setSizingUnit={setSizingUnit}
        updateField={updateField}
        setSizingMethod={setSizingMethod} setSizingLoading={setSizingLoading}
        setView={setView} submitSizing={submitSizing} t={t}
      /></div>;
      case "size-result": return <div key="v-sizeresult" className="ps-tryon-view-enter"><SizeResultView
        estimationDone={estimationDone}
        sizingLoading={sizingLoading} sizingResult={sizingResult} sizeGuide={sizeGuide}
        resultImageUrl={resultImageUrl} productImage={restoredProductImage || productImage} productImages={restoredProductImages || (restoredProductImage ? [restoredProductImage] : effectiveProductImages)} productCarouselItems={restoredProductCarouselItems || (restoredProductImage ? undefined : effectiveProductCarouselItems)} productTitle={restoredProductTitle || productTitle}
        styleMatchSelections={restoredHistoryEntryId ? undefined : resultStyleMatchSelections}
        productUrl={restoredProductUrl || effectiveProductUrl}
        productMaterial={productMaterial} productDescription={productDescription}
        sizingUnit={sizingUnit} setSizingUnit={setSizingUnit} setView={setView} handleDownload={handleDownload}
        onContinueShopping={handleContinueShopping}
	        isHistoryResult={!!restoredHistoryEntryId && !restoredHistoryMatchesCurrentProduct}
        onBackToProductPage={handleBackToProductPage}
        onAddToBag={onAddToBag ? handleAddToBag : undefined}
        addToBagLabel={addToBagLabel}
        continueShoppingLabel={continueShoppingLabel}
        backToProductPageLabel={backToProductPageLabel}
        onTryOnFeedbackSubmit={handleTryOnFeedbackSubmit}
        selectedFile={selectedFile} previewUrl={previewUrl}
        handleFileSelect={handleFileSelect} handleRemovePreview={handleRemovePreview}
        handleTryOnSubmit={handleTryOnSubmit}
        tryOnProcessing={tryOnProcessing} tryOnStartedAt={tryOnStartedAt}
        tryOnErrorMessage={errorMessage}
        tryOnAvailable={!!sizingResult && !storeTryOnLimitReached}
        showProfileCompletionCta={measurementType === "body" && !!sizingResult && sizingResult.found !== false && (!profileSession || profiles.length === 0)}
        onProfileCompletionCta={handleCompleteProfileFromResult}
        bodyLandmarks={bodyLandmarks}
        faceLandmarks={faceLandmarks}
        measurementType={measurementType === "body-basic" ? "body" : measurementType}
        isAccessoryProduct={measurementType === "body-basic" || measurementType === "face" || measurementType === "head" || measurementType === "wrist"}
        activeSection={activeSection} setActiveSection={setActiveSection}
        pendingCustomSizes={pendingCustomSizes}
        onPendingCustomSizeChange={setPendingCustomSizeBySection}
        onResetTryOn={() => {
          setSelectedFile(null);
          if (previewUrl) URL.revokeObjectURL(previewUrl);
          setPreviewUrl(null);
          setResultImageUrl(null);
        }}
	        onClose={handleClose}
	        portalContainer={resolvedPortalContainer ?? undefined}
	        userHeightCm={(() => {
          const p = profiles.find(x => x.id === activeProfileId);
          if (!p) return undefined;
          const h = p.height ?? p.heightCm;
          if (!h || h <= 0) return undefined;
          const profileUnit = p.heightUnit;
          const resultUnit = sizingResult?.unit || sizingUnit;
          // Convert profile height to match the result unit
          if ((profileUnit === "in" || profileUnit === "ft") && resultUnit !== "in") {
            return Math.round(h * 2.54); // inches → cm
          }
          if ((!profileUnit || profileUnit === "cm") && resultUnit === "in") {
            return Math.round(h / 2.54 * 10) / 10; // cm → in
          }
          return h;
        })()}
        t={t}
      /></div>;
      case "upload": return <div key="v-upload" className="ps-tryon-view-enter"><UploadView
        selectedFile={selectedFile} previewUrl={previewUrl} dragOver={dragOver} setDragOver={setDragOver}
        fileInputRef={fileInputRef} handleFileSelect={handleFileSelect} handleRemovePreview={handleRemovePreview}
        handleTryOnSubmit={handleTryOnSubmit} cn={cn} t={t}
      /></div>;
      case "processing": return <div key="v-proc" className="ps-tryon-view-enter"><ProcessingView
        previewUrl={previewUrl} progressRef={progressRef} progressBarRef={progressBarRef}
        progressTextRef={progressTextRef} progressStatusRef={progressStatusRef}
        progressEtaRef={progressEtaRef} progressRingRef={progressRingRef}
        productTitle={productTitle}
        productMaterial={productMaterial}
        productDescription={productDescription}
        onCancel={() => { setView("size-result"); setTryOnProcessing(false); }}
        cn={cn} t={t}
      /></div>;
      case "result": return <div key="v-result" className="ps-tryon-view-enter"><ResultView setView={setView} /></div>;
      case "profiles": return <div key="v-profiles" className="ps-tryon-view-enter">{profileSession ? <MySizingProfilesView
        profiles={profiles}
        activeProfileId={activeProfileId}
        estimatingProfileIds={estimatingProfileIds}
        apiUrl={getApiUrl(apiUrl)}
        apiKey={getApiKey()}
        initialCreateDraft={profileCompletionDraft}
        onProfileDraftConsumed={clearProfileCompletionDraft}
        onSelectProfile={(id) => {
          applyProfile(id);
          setBodyProfileInitialStep(null);
          setView("body-profile");
        }}
        onSaveProfileMeasurements={(id, measurements, unit) => {
          // User edited measurements in the modal — persist with the chosen unit
          const nextUnit = unit ?? profiles.find((x) => x.id === id)?.measurementsUnit ?? "cm";
          updateProfilesForCurrentSession((list) => list.map((p) => p.id === id ? {
            ...p,
            measurements,
            measurementsUnit: nextUnit,
            lastEditedAt: Date.now(),
          } : p));
          reportProfileEvent("SDK_PROFILE_UPDATED", profiles.find((x) => x.id === id) ?? { id, name: "Profile" }, {
            source: "profile-measurements-modal",
            measurementKeys: Object.keys(measurements),
            unit: nextUnit,
          });
        }}
        onSaveBraSize={(id, bandSize, cupSize) => {
          updateProfilesForCurrentSession((list) => list.map((p) => p.id === id ? {
            ...p,
            bandSize,
            cupSize,
            lastEditedAt: Date.now(),
          } : p));
        }}
        onEditProfile={(p) => { setProfileDetail(p); }}
        onPhotoPreview={(base64) => {
          // When user selects a photo in the wizard, surface the preview in the
          // left panel by setting previewUrl — this shows the user their photo
          // in context while they fill in height/weight/age.
          if (base64) {
            const url = normalizeProfilePhotoSource(base64);
            setPreviewUrl(url);
          } else {
            setPreviewUrl(null);
          }
        }}
        onEstimateFromPhoto={async (args) => {
          // Called by CreateProfileWizard's calculating step — runs MediaPipe
          // + Gemini vision to extract body measurements, returns them to the
          // wizard to persist on the new profile.
          try {
            const [landmarks, headCircumferenceMm] = await Promise.all([
              args.bodyLandmarks
                ? Promise.resolve(args.bodyLandmarks)
                : detectBodyLandmarks(args.photoBase64, { maxLongEdge: 1600 }).catch(() => null),
              estimateHeadCircumferenceMmFromPhoto(args.photoBase64).catch(() => null),
            ]);
            const est = await estimateFullMeasurements({
              apiUrl,
              height: args.height > 0 ? args.height : undefined,
              weight: args.weight > 0 ? args.weight : undefined,
              heightUnit: args.heightUnit,
              weightUnit: args.weightUnit,
              gender: args.gender,
              age: args.age,
              bandSize: args.bandSize,
              cupSize: args.cupSize,
              braSizeRegion: args.braSizeRegion,
              bodyImage: args.photoBase64,
              bodyLandmarks: (landmarks as unknown as Record<string, { x: number; y: number }>) ?? undefined,
            });
            if (!est) return null;
            const estimates = { ...(est.estimates as Record<string, number>) };
            if (typeof headCircumferenceMm === "number" && headCircumferenceMm > 0) {
              estimates.headCircumference = headCircumferenceMmToUnit(headCircumferenceMm, est.unit);
            }
            return { estimates, unit: est.unit };
          } catch {
            return null;
          }
        }}
        onSaveNewProfile={async (data) => {
          const newProfile: Profile = {
            ...(data as Profile),
            id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
            createdAt: Date.now(),
            lastUsedAt: Date.now(),
          };
          const photoBase64 = (newProfile as any).photoBase64 as string | undefined;
          const persistedProfile = sanitizeProfileForPersistence(newProfile);
          const nextProfiles = sanitizeProfilesForPersistence([persistedProfile, ...profiles]);
          setProfiles(nextProfiles);
          setActiveProfileId(newProfile.id);
          reportProfileEvent("SDK_PROFILE_CREATED", persistedProfile, {
            source: "create-profile-wizard",
            usedPhotoForEstimation: !!photoBase64,
            measurementKeys: newProfile.measurements ? Object.keys(newProfile.measurements) : [],
          });
          const session = profileSessionRef.current;
          if (session) {
            if (profileSyncTimerRef.current) {
              clearTimeout(profileSyncTimerRef.current);
              profileSyncTimerRef.current = null;
            }
            try {
              const savedStore = await saveRemoteProfiles(apiUrl, session.accessToken, nextProfiles, newProfile.id);
              applyProfileStore(savedStore.profiles, savedStore.activeProfileId);
            } catch (error) {
              if (isProfileAuthError(error)) {
                clearInvalidProfileSession();
                setProfileAuthError(t("Please sign in again to save your profiles."));
                return newProfile.id;
              }
              setProfileAuthError(error instanceof Error ? error.message : t("Profile sync failed. Your local changes are still saved here."));
            }
          }

          // 1b. Photo-path profile creation no longer redirects to size-result.
          //     The CreateProfileWizard's "calculating" step estimates body
          //     measurements via onEstimateFromPhoto, and they're included
          //     in the saved profile data. The user stays in the profile view.

          // 2. Background: compute full body measurements via /sizing/estimate
          //    so the profile has chest/hips/sleeve/inseam/etc. for any future
          //    product. The recommendForProduct() headless API picks them up
          //    automatically on the next product page visit.
          //
          //    Trigger conditions: a photo (vision flow can estimate everything
          //    from the image alone) OR explicit height + weight (text flow).
          //    The previous gate of `heightVal > 0 && weightVal > 0` blocked
          //    photo-only profiles from ever running estimation, leaving
          //    HEIGHT/WEIGHT/AGE as "—" forever.
          const heightVal = (newProfile as any).heightCm || newProfile.height || 0;
          const weightVal = (newProfile as any).weightKg || newProfile.weight || 0;
          const heightUnitVal = (newProfile.sizingUnit === "in" ? "in" : "cm") as "cm" | "in";
          const weightUnitVal = (newProfile.sizingUnit === "in" ? "lbs" : "kg") as "kg" | "lbs";
          // Skip background estimation if the wizard already populated
          // measurements. The image-flow runs full vision estimation inline
          // (so estimateResults already came back from the same backend),
          // and re-running would replace the wizard's values with a slightly
          // different estimate, making the post-save Active Profile screen
          // disagree with what the user just saw in the review step. Test by
          // both: (a) measurements object exists with keys, AND (b) neck
          // came back > 0 (the most user-visible field that previously
          // diverged across runs).
          const measurementsObj = (newProfile as any).measurements as Record<string, number | undefined> | undefined;
          const hasMeasurements = !!measurementsObj
            && Object.keys(measurementsObj).length > 0
            && typeof measurementsObj.neckCircumference === "number"
            && measurementsObj.neckCircumference > 0;
          console.log("[ps-sdk:saveProfile] hasMeasurements=" + hasMeasurements,
            measurementsObj ? { neck: measurementsObj.neckCircumference, chest: measurementsObj.chest, photoBase64Set: !!photoBase64 } : null);
          if (!hasMeasurements && (photoBase64 || (heightVal > 0 && weightVal > 0))) {
            // Mark this profile as estimating so the detail view shows a
            // spinner instead of "—" while we wait for the backend.
            setEstimatingProfileIds((prev) => new Set(prev).add(newProfile.id));
            // Run landmark detection in parallel — non-blocking, falls back to null
            const landmarksPromise = photoBase64
              ? detectBodyLandmarks(photoBase64, { maxLongEdge: 1600 }).catch(() => null)
              : Promise.resolve(null);

            landmarksPromise
              .then((landmarks) => estimateFullMeasurements({
                apiUrl,
	                height: heightVal > 0 ? heightVal : undefined,
	                weight: weightVal > 0 ? weightVal : undefined,
	                heightUnit: heightUnitVal,
	                weightUnit: weightUnitVal,
	                gender: newProfile.gender === "female" ? "female" : "male",
	                age: (newProfile as any).age,
                chestProfile: (newProfile as any).chestProfile,
                midsectionProfile: (newProfile as any).midsectionProfile,
                hipProfile: (newProfile as any).hipProfile,
                bandSize: (newProfile as any).bandSize,
                cupSize: (newProfile as any).cupSize,
                braSizeRegion: (newProfile as any).braSizeRegion,
                bodyImage: photoBase64,
                bodyLandmarks: (landmarks as unknown as Record<string, { x: number; y: number }>) ?? undefined,
              }))
              .then((est) => {
                if (est) {
                  updateProfilesForCurrentSession((list) => list.map((p) => p.id === newProfile.id ? {
                    ...p,
                    measurements: est.estimates,
                    measurementsUnit: est.unit,
                    lastEditedAt: Date.now(),
                  } : p));

                  // Patch userEstimates (height/weight/age) onto the profile if
                  // they came back from vision and weren't already set.
                  if (est.userEstimates) {
                    const userEstimates = est.userEstimates;
                    updateProfilesForCurrentSession((list) => {
                      const idx = list.findIndex((p) => p.id === newProfile.id);
                      if (idx < 0) return list;
                      const all = [...list];
                      const target = all[idx]!;
                      const u = userEstimates;
                      const patched: Profile = { ...target };
                      if (u.height && !(target.height || target.heightCm)) {
                        patched.height = u.height;
                        patched.heightCm = u.height;
                        patched.heightUnit = "cm";
                      }
                      if (u.weight && !(target.weight || target.weightKg)) {
                        patched.weight = u.weight;
                        patched.weightKg = u.weight;
                        patched.weightUnit = "kg";
                      }
                      if (u.age && !target.age) {
                        patched.age = u.age;
                      }
                      all[idx] = patched;
                      return all;
                    });
                  }

                }
              })
              .catch(() => { /* non-fatal */ })
              .finally(() => {
                // Clear the estimating flag regardless of success/failure so
                // the spinner goes away even when estimation fails.
                setEstimatingProfileIds((prev) => {
                  const next = new Set(prev);
                  next.delete(newProfile.id);
                  return next;
                });
              });
          }
          return newProfile.id;
        }}
        onDeleteProfile={(id) => {
          void deleteProfileForCurrentSession(id).catch(() => {
            /* error message is surfaced via profileAuthError */
          });
        }}
        onRequestDelete={(id) => setDeleteConfirmId(id)}
        onLogout={handleProfileLogout}
        onClose={() => setView("body-profile")}
        onRegisterBackInterceptor={handleRegisterProfilesBack}
        t={t}
      /> : <SocialProfileAuthView
        loadingProvider={profileAuthLoadingProvider}
        error={profileAuthError}
        onLogin={handleSocialProfileLogin}
        t={t}
      />}</div>;
      case "error": return <div key="v-error" className="ps-tryon-view-enter"><ErrorView
        productImage={productImage}
        productTitle={productTitle}
        title={errorMessage ? t("Try-on failed") : undefined}
        message={errorMessage || undefined}
        onManualMeasurements={handleRetry}
        cn={cn}
        t={t}
      /></div>;
      case "no-chart": return <div key="v-nochart" className="ps-tryon-view-enter"><NoChartView
        productImage={productImage}
        productTitle={productTitle}
        reason={noSizeReason}
        onTryOn={() => setView("photo-guide")}
        onClose={onClose ?? (() => {})}
        t={t}
      /></div>;
      case "photo-guide": return <div key="v-photoguide" className="ps-tryon-view-enter"><PhotoGuideView
        measurementType={measurementType === "body-basic" ? "body" : measurementType}
        apiUrl={getApiUrl(apiUrl)}
        apiKey={getApiKey()}
        onBack={() => setView("no-chart")}
        onSubmit={(file) => {
          handleFileSelect(file);
          handleTryOnSubmit(file);
        }}
        t={t}
      /></div>;
      default: return null;
    }
  }

  return (
    <div className={cx("ps-tryon-root", cn.root || className)} style={{ visibility: cssReady ? "visible" : "hidden", ...cssVars, ...style }} suppressHydrationWarning data-ps-tryon data-clarity-unmask="true">
      <button
        onClick={handleStorefrontButtonClick}
	        className={cx([
	          "ps-tryon-btn",
	          isTextTrigger ? "ps-tryon-btn--text" : "",
	          storefrontButtonLoading ? "ps-tryon-btn--loading" : "",
	        ].filter(Boolean).join(" "), cn.button)}
	        type="button"
	        disabled={storefrontButtonLoading}
	        aria-busy={storefrontButtonLoading || undefined}
	      >
	        {storefrontButtonLoading ? (
	          <span className="ps-tryon-btn-spinner" aria-hidden="true" />
	        ) : (
	          !isTextTrigger && showIcon !== false && (buttonIcon || <CameraIcon />)
	        )}
	        <span>{storefrontButtonText}</span>
	      </button>

	      {view !== "idle" && resolvedPortalContainer && createPortal(
        <div className={cx("ps-tryon-overlay", cn.overlay)} style={cssVars} data-ps-tryon-portal data-clarity-unmask="true">
          {/* Modal class composition:
              - `ps-tryon-modal-wide` for content-heavy views (size result,
                body profile, profiles drawer, etc.)
              - We DELIBERATELY no longer add `ps-tryon-modal-tall` for the
                profiles view — that used to animate the modal taller when
                entering profile creation, which we don't want. The wizard
                must fit inside the standard modal height. */}
          <div className={cx(`ps-tryon-modal${(view === "result" && resultImageUrl && sizingResult) || view === "size-result" || view === "estimation-review" || view === "body-profile" || view === "profiles" || view === "no-chart" || view === "photo-guide" || view === "error" ? " ps-tryon-modal-wide" : ""}`, cn.modal)} onClick={e => e.stopPropagation()} data-clarity-unmask="true">
            {/* Header — minimal: powered-by + lang + profile + history + close */}
            <div className={cx("ps-tryon-header ps-tryon-header-minimal", cn.header)}>
              <a
                className="ps-tryon-powered-by"
                href="https://primestyleai.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("Powered by")} <strong>PrimeStyleAI</strong>
              </a>
	              <LangSwitcher
	                activeLocale={activeLocale}
	                onSelect={setActiveLocale}
	                portalContainer={resolvedPortalContainer ?? undefined}
	              />
              <button type="button" className="ps-tryon-header-icon ps-tryon-header-icon-labeled" title={t("Profiles")} onClick={() => {
                // Close history drawer first if open, then toggle profiles view
                if (drawer) setDrawer(null);
                if (view === "profiles") {
                  setView(prevViewRef.current || "body-profile");
                  prevViewRef.current = null;
                } else {
                  prevViewRef.current = view;
                  setView("profiles");
                }
              }}>
                <UserIcon />
                <span className="ps-tryon-header-icon-label">{t("Profile")}</span>
              </button>
              <button type="button" className="ps-tryon-header-icon ps-tryon-header-icon-labeled" title={t("History")} onClick={() => {
                // If viewing profiles, go back to previous view first
                if (view === "profiles") {
                  setView(prevViewRef.current || "body-profile");
                  prevViewRef.current = null;
                }
                if (drawer === "history") { setDrawer(null); } else { prevViewRef.current = prevViewRef.current || view; setDrawer("history"); }
              }}>
                <ClockIcon />
                <span className="ps-tryon-header-icon-label">{t("History")}</span>
              </button>
              {/* Mobile-only hamburger — collapses Profile/History/Lang
                  into a small popover so a 360 px viewport doesn't have
                  to fit four separate controls. Hidden on desktop via
                  the .ps-tryon-header-mobile-menu CSS rules. */}
              <button
                type="button"
                ref={mobileMenuBtnRef}
                className="ps-tryon-header-mobile-menu-btn"
                aria-label={t("Menu")}
                aria-expanded={mobileMenuOpen}
                onClick={() => setMobileMenuOpen(v => !v)}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
              </button>
              <button type="button" onClick={handleClose} className={cx("ps-tryon-close", cn.closeButton)}>
                <XIcon />
              </button>
            </div>
            {/* Portal the popover to <body> so the modal's overflow:hidden
                doesn't clip it. Without the portal, top:100% would push
                the menu outside the modal's clipping rect — invisible no
                matter the z-index. */}
	            {mobileMenuOpen && resolvedPortalContainer && createPortal(
              <>
                <div
                  className="ps-tryon-header-mobile-backdrop"
                  onClick={() => setMobileMenuOpen(false)}
                />
                <div
                  className="ps-tryon-header-mobile-menu"
                  role="menu"
                  style={(() => {
                    const r = mobileMenuBtnRef.current?.getBoundingClientRect();
                    if (!r) return undefined;
                    return {
                      top: `${Math.round(r.bottom + 6)}px`,
                      right: `${Math.round(window.innerWidth - r.right)}px`,
                    } as React.CSSProperties;
                  })()}
                >
                  <button
                    type="button"
                    className="ps-tryon-header-mobile-menu-item"
                    role="menuitem"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      if (drawer) setDrawer(null);
                      if (view === "profiles") {
                        setView(prevViewRef.current || "body-profile");
                        prevViewRef.current = null;
                      } else {
                        prevViewRef.current = view;
                        setView("profiles");
                      }
                    }}
                  >
                    <UserIcon />
                    <span>{t("Profile")}</span>
                  </button>
                  <button
                    type="button"
                    className="ps-tryon-header-mobile-menu-item"
                    role="menuitem"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      if (view === "profiles") {
                        setView(prevViewRef.current || "body-profile");
                        prevViewRef.current = null;
                      }
                      if (drawer === "history") {
                        setDrawer(null);
                      } else {
                        prevViewRef.current = prevViewRef.current || view;
                        setDrawer("history");
                      }
                    }}
                  >
                    <ClockIcon />
                    <span>{t("History")}</span>
                  </button>
                  <div className="ps-tryon-header-mobile-menu-lang">
	                    <LangSwitcher
	                      activeLocale={activeLocale}
	                      onSelect={(l) => { setActiveLocale(l); setMobileMenuOpen(false); }}
	                      portalContainer={resolvedPortalContainer ?? undefined}
	                    />
                  </div>
                </div>
              </>,
	              resolvedPortalContainer,
	            )}

            {/* Stepper — hidden on first screen, processing, and multi-section size result */}
            {view !== "body-profile" && view !== "processing" && !(view === "size-result" && sizeGuide?.sections && Object.keys(sizeGuide.sections).length > 1) && (
              <Stepper view={view} stepIndex={stepIndex} />
            )}

            {/* Body */}
            <div ref={bodyRef} className={cx("ps-tryon-body", cn.body)} style={{ position: "relative", overflow: drawer ? "hidden" : undefined }} data-clarity-unmask="true">
              {showBackButton && (
                <button className="ps-tryon-back-btn" onClick={handleBack}>
                  <svg width="18" height="12" viewBox="0 0 18 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="17" y1="6" x2="1" y2="6" /><polyline points="5 1 1 6 5 11" />
                  </svg>
                  {t("Back")}
                </button>
              )}
              {renderBody()}
              <DrawerPanel
                drawer={drawer} setDrawer={setDrawer}
                profiles={profiles} setProfiles={setProfiles} history={history} setHistory={setHistory}
                setProfileDetail={setProfileDetail} restoreHistory={restoreHistory} t={t}
              />
            </div>

            {/* Delete confirmation — rendered at modal level so blur covers header + body + nav */}
            {deleteConfirmId && (
              <DeleteConfirmModal
                onConfirm={() => {
                  const id = deleteConfirmId;
                  void deleteProfileForCurrentSession(id)
                    .then(() => setDeleteConfirmId(null))
                    .catch(() => {
                      /* keep modal open so the user can retry or cancel */
                    });
                }}
                onCancel={() => setDeleteConfirmId(null)}
                t={t}
              />
            )}

            {/* Fast-path confirmation — verify unit + values before running
                the recommend against the user's stored profile. */}
            {confirmProfile && (
              <ConfirmMeasurementsModal
                profile={confirmProfile}
                onProceed={proceedFromConfirmProfile}
                onEdit={cancelFromConfirmProfile}
                t={t}
              />
            )}

          </div>
        </div>,
	        resolvedPortalContainer
	      )}

      {/* Profile detail modal */}
      <ProfileDetailModal
	        profileDetail={profileDetail} setProfileDetail={setProfileDetail}
	        setProfiles={setProfiles} activeProfileId={activeProfileId}
	        setActiveProfileId={setActiveProfileId} t={t}
	        portalContainer={resolvedPortalContainer ?? undefined}
	      />
    </div>
  );
}
